import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {
    Box, Divider, IconButton, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow,
    Paper,
    Popover, Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import '@mui/icons-material/SwapHoriz';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {MoneyField, UpDownIconDisplay, useSplitTabsTableContainerRef} from '@/components/common';
import type {AuditBuildCompanyFSTreeItems} from '@/services/auditService/auditCompanyFSCategoryService';
import type {CompanyAnnualSelectItem} from '@/services/accountService/companyAnnualService';
import {
    parseAmountDisplay,
    daysBetween,
    formatDayMonYYYY,
    parseDateFlexible,
    computeYearOfAssessment,
} from '@/utils/dataUtils';
import {resolvePriorPeriodSlice} from '@/utils/wpPriorPeriodBfRollForward';
import {
    resolveAdjustmentColumnHeaderYoa,
} from '@/utils/auditPeriodSplit';
import {FS_CODE, isCode, findNodeByCodes} from '@/utils/fsCodes';
import {
    filterHiddenCostOfSalesFlatRows,
    resolveBsInventoriesFromBsTree,
    shouldHidePlCostOfSalesRow,
    applyPlCotCosPurOverrides,
    resolvePlCotPrimaryCostAmount,
    resolvePlCotCosAmountForParentRollup,
    resolveRawCosForPlCotParentAuditedRollup,
    shouldHidePlCotCosRowWhenRawZero,
    resolvePlCotCostOfSalesAdjustmentSum,
    shouldUsePlCotFormulaCosAudited,
    sumPlCotComponentAuditedValues,
    resolvePlLastYearLeafAmount,
    inferPlCotInputMode,
    resolvePlCotInputMode,
    applyPlCotPurSelfAuditedIfAllAdjustmentsZero,
    type PlCotInputMode,
} from '@/utils/plCostOfSalesVisibility';
import {
    plComputeComputedAmounts,
    plResolveAdjustmentValue,
    plNameFor,
    plIsProtectedParentHeader,
    resolvePlLeafAuditedValue,
    recomputePlDerivedFormulaNodes,
    isPlCustomFsNode,
} from '@/pages/components/fs-report/FsFormula';
import {hasChildren, toNestedTree} from '@/utils/fsDerived';
import {buildFsSummaryVisibleByFsId} from '@/utils/fsHideZeroRows';
import {useFsCategoryStore} from '@/store/audit/fsCategoryStore';
import {useFsAdjustStore} from '@/store/audit/fsAdjustStore';
import {
    sumPlAfterTaxBridgeAcrossSlices,
    sumRoundedAuditedAcrossSlices,
    usePlMultiPeriodChainStore,
} from '@/store/audit/plMultiPeriodChainStore';
import type {AuditedMode} from '@/pages/components/fs-report/BalanceSheet';
import {computePlAfterTaxAdjustmentsByRefNo} from '@/pages/components/fs-report/plAfterTaxAdjustBridge';
import {isMustFillScheduleCode, ScheduleChip} from '@/pages/components/fs-report/ScheduleChip';
import {FsMaterialityHeader} from '@/pages/components/fs-report/FsMaterialityHeader';
import {
    FsStatementTableSkeletonRows,
    buildAdjustmentSkeletonColumns,
} from '@/pages/components/fs-report/FsStatementTableSkeleton';
import {InlineEditableName, canEditItemAlias, isItemAliasLocked} from '@/pages/components/fs-report/InlineEditableName';
import {adjustColumnToggleHeaderSx, lastYearColumnSeparatorSx} from '@/pages/components/fs-report/fsColumnSx';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import {
    computePlFsAmountsFromDisplays,
    computePlLastYearAmountsFromDisplays,
    sumAmountDisplayMaps,
    buildPlFsInitSnapshot,
    buildPlFsWarmupSignature,
    countFsTreeNodes,
    consumePlFsWarmup,
    peekPlFsWarmup,
    resolvePlCotFsIds,
    zeroPlCotFormulaSideInDataMap,
    applyPlCotInputModeSwitch,
    clampPlCotPurToFormulaMin,
    resolvePlCotPurFormulaAmount,
    type PlFsInitSnapshot,
} from '@/pages/components/fs-report/plFsMultiCompute';
import {PlFsMultiVirtualTable} from '@/pages/components/fs-report/PlFsMultiVirtualTable';
import {
    createCustomNode,
    deleteCustomNode,
    updateCustomNode,
} from '@/services/auditService/auditCompanyFSCustomNodeService';
import {isPlCustomFsItem, type PlFsFlatRow} from '@/pages/components/fs-report/PlFsTableRow';
import {buildTotalPlClientTree} from '@/pages/components/fs-report/plMultiTotalView';
import type {PlMultiViewMode} from '@/pages/components/fs-report/plMultiTotalView';
import {
    buildPerYoaPlClientByFsId,
    listPlClientYoaColumns,
} from '@/pages/components/fs-report/plClientByYoaCompute';
import {
    autoAdjItemsFromCatalog,
    collectMultiAdjColumnCatalog,
    manualRefNosFromCatalog,
    matchesAutoAdjColumn,
    plMergeColumnFilter,
    plSingleColumnFilter,
} from '@/utils/fsAdjustmentMultiColumns';
import {useFillRemainingViewport} from '@/hooks/useFillRemainingViewport';
import {appendFsChineseName, glExcelLineName, resolveFsChineseName} from '@/utils/glToFsMapper';

// 转换函数
function convertAuditFsItemVOToFsSimpleData(vo: any): any {
    const level = vo.level || 0;

    // Recursively convert children if they exist
    const children = vo.children && Array.isArray(vo.children)
        ? vo.children.map(convertAuditFsItemVOToFsSimpleData)
        : [];

    return {
        fsId: vo.fsId,
        level: level,
        name: vo.name || '',
        nameEn: vo.nameEn ?? null,
        nameZh: vo.nameZh ?? null,
        aliasEn: vo.aliasEn ?? null,
        aliasZh: vo.aliasZh ?? null,
        aliasLocked: vo.aliasLocked ?? null,
        statement: vo.statement ?? null,
        code: vo.code || '',
        entry: (vo.entry || '') as 'Dr' | 'Cr',
        scheduleName: vo.scheduleName || '',
        scheduleCode: vo.scheduleCode || '',
        openingAmount: vo.openingAmount ?? vo.client ?? vo.amount ?? 0,
        amount: vo.openingAmount ?? vo.client ?? vo.amount ?? 0,
        amountsByYearOfAssessment: vo.amountsByYearOfAssessment || null,
        adjustment: (Array.isArray(vo.adjustment) ? vo.adjustment : vo.adjustByPapers)?.map((paper: any) => ({
            refNo: paper.refNo || '',
            adjustment: paper.adjustment || 0,
            headerId: paper.headerId,
            adjustmentMode: paper.adjustmentMode,
            isAutoAdj: paper.isAutoAdj ?? (paper.adjustmentMode === 4),
            autoAdjType: paper.autoAdjType,
            yearOfAssessment: paper.yearOfAssessment ?? null,
        })) || [],
        finalAmount: vo.audited || 0,
        lastYearAudited: vo.lastYearChangeAudited ?? vo.lastYearAudited ?? 0,
        lastYearChangeAudited: vo.lastYearChangeAudited ?? vo.lastYearAudited ?? null,
        adjustByPaper: vo.adjustByPaper || false,
        wpStatus: vo.wpStatus ?? null,
        wpIssueRemark: vo.wpIssueRemark ?? null,
        outstanding: vo.outstanding ?? null,
        outstandingRemark: vo.outstandingRemark ?? null,
        notes: vo.notes ?? null,
        isCustom: vo.isCustom ?? false,
        customNodeId: vo.customNodeId ?? null,
        templateCodeRef: vo.templateCodeRef ?? null,
        children: children
    };
}

/** Virtual fsId for inline custom-create draft (not persisted; compute-only overlay). */
const CUSTOM_INLINE_DRAFT_FS_ID = -2_000_000_001;

type CustomInlineDraftAmounts = {
    parentFsId: number;
    currentAmount: number;
    lastYearAmount: number;
    amountsByYoa: Record<string, number>;
};

/** Insert a custom draft/leaf after PL-COT-COS; otherwise before parent Level-2 subtotal. */
function insertCustomRowAfterCostOfSales<T extends {
    item?: any;
    isSubtotal?: boolean;
    isCustomDraft?: boolean;
    parentItem?: {fsId?: number};
}>(
    rows: T[],
    parentFsId: number,
    parentCode: string | undefined,
    draftRow: T,
): T[] {
    const isCot = isCode(parentCode, ...FS_CODE.PL.COST_OF_TURNOVER);
    if (isCot) {
        const cosIdx = rows.findIndex(
            (r) => !r.isSubtotal
                && !r.isCustomDraft
                && isCode(r.item, ...FS_CODE.PL.COST_OF_SALES),
        );
        if (cosIdx >= 0) {
            return [...rows.slice(0, cosIdx + 1), draftRow, ...rows.slice(cosIdx + 1)];
        }
    }
    const idx = rows.findIndex(
        (r) => r.isSubtotal && Number(r.parentItem?.fsId) === parentFsId,
    );
    if (idx >= 0) {
        return [...rows.slice(0, idx), draftRow, ...rows.slice(idx)];
    }
    const parentIdx = rows.findIndex(
        (r) => !r.isSubtotal && !r.isCustomDraft && Number(r.item?.fsId) === parentFsId,
    );
    if (parentIdx >= 0) {
        return [...rows.slice(0, parentIdx + 1), draftRow, ...rows.slice(parentIdx + 1)];
    }
    return rows;
}

/** Inject a temporary custom leaf under parent so PL-COT/OI/AE rollup + afterTax include draft amounts before save. */
function injectCustomInlineDraftLeaf(nodes: any[] | null | undefined, parentFsId: number): any[] {
    if (!nodes?.length) return nodes || [];
    return nodes.map((n) => {
        const fsId = Number(n?.fsId);
        if (fsId === parentFsId) {
            const kids = Array.isArray(n.children) ? [...n.children] : [];
            const draftLeaf = {
                fsId: CUSTOM_INLINE_DRAFT_FS_ID,
                parentId: parentFsId,
                level: 3,
                code: '',
                name: '',
                isCustom: true,
                children: [],
            };
            const cosIdx = kids.findIndex((c) => isCode(c, ...FS_CODE.PL.COST_OF_SALES));
            if (cosIdx >= 0) {
                kids.splice(cosIdx + 1, 0, draftLeaf);
            } else {
                kids.push(draftLeaf);
            }
            return {...n, children: kids};
        }
        if (Array.isArray(n?.children) && n.children.length) {
            return {...n, children: injectCustomInlineDraftLeaf(n.children, parentFsId)};
        }
        return n;
    });
}

function overlayCustomInlineDraftAmounts(
    amountDisplays: Record<number, string>,
    draft: CustomInlineDraftAmounts | null | undefined,
): Record<number, string> {
    if (!draft) return amountDisplays;
    return {...amountDisplays, [CUSTOM_INLINE_DRAFT_FS_ID]: String(draft.currentAmount || 0)};
}

function overlayCustomInlineDraftLastYear(
    lastYearAmounts: Record<number, string>,
    draft: CustomInlineDraftAmounts | null | undefined,
): Record<number, string> {
    if (!draft) return lastYearAmounts;
    return {...lastYearAmounts, [CUSTOM_INLINE_DRAFT_FS_ID]: String(draft.lastYearAmount || 0)};
}

function overlayCustomInlineDraftByYoa(
    byYoa: Record<string, Record<number, string>>,
    draft: CustomInlineDraftAmounts | null | undefined,
): Record<string, Record<number, string>> {
    if (!draft) return byYoa;
    const next: Record<string, Record<number, string>> = {...byYoa};
    for (const [yoa, num] of Object.entries(draft.amountsByYoa || {})) {
        next[yoa] = {...(next[yoa] || {}), [CUSTOM_INLINE_DRAFT_FS_ID]: String(num || 0)};
    }
    return next;
}

/**
 * Phase 4 MULTI PL Tab: map one FS-column YOA onto openingAmount (like FS multi-column render).
 * Does not change SINGLE / no-yoa path (#20).
 */
function convertAuditFsItemVOForYoa(vo: any, yoa: string | null | undefined): any {
    const yoaKey = String(yoa || '').trim();
    const level = vo.level || 0;
    const children = vo.children && Array.isArray(vo.children)
        ? vo.children.map((c: any) => convertAuditFsItemVOForYoa(c, yoaKey))
        : [];

    const byYoa = vo.amountsByYearOfAssessment as Record<string, number | string> | null | undefined;
    let opening = Number(vo.openingAmount ?? vo.client ?? vo.amount ?? 0) || 0;
    if (yoaKey && byYoa && typeof byYoa === 'object' && Object.keys(byYoa).length > 0) {
        const raw = byYoa[yoaKey];
        if (raw !== undefined && raw !== null && raw !== '') {
            opening = Number(raw) || 0;
        } else {
            // GL Submit / legacy SINGLE: amount under "" — do not wipe Client on MULTI tabs
            const legacyRaw = byYoa[''];
            if (legacyRaw !== undefined && legacyRaw !== null && legacyRaw !== '') {
                opening = Number(legacyRaw) || 0;
            } else {
                opening = 0;
            }
        }
    }

    // Parent level-2: re-sum children (mirrors backend applyChildAmounts)
    if (level === 2 && children.length > 0) {
        opening = children.reduce((sum: number, c: any) => sum + (Number(c.openingAmount) || 0), 0);
    }

    return {
        fsId: vo.fsId,
        level: level,
        name: vo.name || '',
        code: vo.code || '',
        entry: (vo.entry || '') as 'Dr' | 'Cr',
        scheduleName: vo.scheduleName || '',
        scheduleCode: vo.scheduleCode || '',
        openingAmount: opening,
        amount: opening,
        amountsByYearOfAssessment: byYoa || null,
        adjustment: (Array.isArray(vo.adjustment) ? vo.adjustment : vo.adjustByPapers)?.map((paper: any) => ({
            refNo: paper.refNo || '',
            adjustment: paper.adjustment || 0,
            headerId: paper.headerId,
            adjustmentMode: paper.adjustmentMode,
            isAutoAdj: paper.isAutoAdj ?? (paper.adjustmentMode === 4),
            autoAdjType: paper.autoAdjType,
            yearOfAssessment: paper.yearOfAssessment ?? null,
        })) || [],
        finalAmount: vo.audited || 0,
        lastYearAudited: vo.lastYearChangeAudited ?? vo.lastYearAudited ?? 0,
        lastYearChangeAudited: vo.lastYearChangeAudited ?? vo.lastYearAudited ?? null,
        adjustByPaper: vo.adjustByPaper || false,
        wpStatus: vo.wpStatus ?? null,
        wpIssueRemark: vo.wpIssueRemark ?? null,
        outstanding: vo.outstanding ?? null,
        outstandingRemark: vo.outstandingRemark ?? null,
        isCustom: vo.isCustom ?? false,
        customNodeId: vo.customNodeId ?? null,
        templateCodeRef: vo.templateCodeRef ?? null,
        children: children
    };
}

/** Phase 6-B: bootstrap Audited map from period plLine when sibling Tab not yet published. */
function bootstrapAuditedMapFromPlVos(nodes: any[] | null | undefined): Record<number, number> {
    const out: Record<number, number> = {};
    const sumAdj = (node: any): number => {
        const list = Array.isArray(node?.adjustment) ? node.adjustment
            : (Array.isArray(node?.adjustByPapers) ? node.adjustByPapers : []);
        return list.reduce((s: number, a: any) => s + (Number(a?.adjustment) || 0), 0);
    };
    const visit = (list: any[]) => {
        for (const n of list || []) {
            const children = Array.isArray(n?.children) ? n.children : [];
            if (children.length > 0) {
                visit(children);
                continue;
            }
            const fsId = Number(n?.fsId);
            if (!Number.isFinite(fsId)) continue;
            const client = Number(n?.openingAmount ?? n?.client ?? n?.amount ?? 0) || 0;
            out[fsId] = client + sumAdj(n);
        }
    };
    visit(nodes || []);
    return out;
}

import {useAdjustmentStore} from '@/store/audit/adjustmentStore';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import {fetchAnnualMeta} from '@/store/audit/annualMeta';
import {isExclusiveCode, EXCLUSIVE_CODES} from '@/adjustment/adjust/dialogs/utils/adjustment-helpers';
import {useQueryClient} from '@tanstack/react-query';
import {useQueryRoundingAdjust} from '@/services/auditService/adjustmentQueries';
import {roundingAdjustService} from '@/services/auditService/roundingAdjustService';
import {
    buildRoundingAdjustmentMaps,
    emptyRoundingAdjustGroup,
    hasRoundingTargets,
    requestAuditedRoundingGate,
    subscribeRoundingDifference,
} from '@/pages/components/fs-report/roundingAdjustHelpers';

/** Stable empty refs — avoid `[]` / `{}` literals in hook deps (re-render → setPLComputed loop). */
const EMPTY_FS_YOA_LIST: string[] = [];
const EMPTY_NESTED_BS_TREE: any[] = [];
const EMPTY_COMPUTED_AMOUNTS: Record<number, number> = {};
const EMPTY_COMPUTED_BY_YOA: Record<string, Record<number, number>> = {};
const EMPTY_PL_COT_FS_IDS: ReturnType<typeof resolvePlCotFsIds> = {};
const EMPTY_ROUNDED_AUDITED_BY_SLICE: Record<string, Record<number, number>> = {};
const EMPTY_AFTER_TAX_BY_SLICE: Record<string, {
    client: number;
    audited: number;
    lastYear: number;
    roundedAudited?: number | null;
}> = {};

function normalizeLineCode(input: any): string {
    const raw = String(input || '').trim();
    if (!raw) return '';
    const upper = raw.toUpperCase();
    if (upper.startsWith('BS-') || upper.startsWith('PL-') || upper.startsWith('FS-')) return raw;
    const mapped = useFsAdjustStore.getState().getLineByCode(raw)?.code;
    const mappedUpper = String(mapped || '').trim().toUpperCase();
    if (mappedUpper.startsWith('BS-') || mappedUpper.startsWith('PL-') || mappedUpper.startsWith('FS-')) return mapped as string;
    return raw;
}

function getScheduleName(map: Map<string, string>, item: any): string {
    const normalized = normalizeLineCode(item?.code || item?.scheduleCode);
    const legacy = String(item?.scheduleCode || '').trim();
    return map.get(normalized) || (legacy ? map.get(legacy) : undefined) || (item?.scheduleName || '');
}

// Helper component to display change percentage with icon

const PL_AE_SCHEDULE_CONTROLLED_CODES = new Set([
    'PL-AE-ACF','PL-AE-ADM','PL-AE-ADP','PL-AE-AR','PL-AE-AOP','PL-AE-BC','PL-AE-BMF',
    'PL-AE-BRF','PL-AE-CER','PL-AE-CLE','PL-AE-CMP','PL-AE-CSE','PL-AE-CONS','PL-AE-CUR',
    'PL-AE-CRR','PL-AE-CUD','PL-AE-DSN','PL-AE-EW','PL-AE-ENT','PL-AE-EXL','PL-AE-EXH',
    'PL-AE-FRA','PL-AE-FRG','PL-AE-GAS','PL-AE-HOT','PL-AE-INS','PL-AE-LIC','PL-AE-LTR',
    'PL-AE-MED','PL-AE-MEM','PL-AE-MVE','PL-AE-MES','PL-AE-OFF','PL-AE-OTR','PL-AE-PEN',
    'PL-AE-PE','PL-AE-PS','PL-AE-PAF','PL-AE-RAM','PL-AE-SAM','PL-AE-SCF','PL-AE-SWF',
    'PL-AE-STO','PL-AE-SUB','PL-AE-SUE','PL-AE-SUN','PL-AE-TEL','PL-AE-TRN','PL-AE-TRV',
    'PL-AE-TP','PL-AE-UTL','PL-AE-VO','PL-AE-WAG','PL-AE-WSE','PL-AE-WHT','PL-AE-IML',
]);

export type ProfitLossMode = 'fs' | 'adjustment';

export type {PlMultiViewMode};

export interface ProfitLossHandle {
    getLines: () => Array<{ fsId: number; amount: number }>
    /**
     * Flush open custom-node draft + PL amount maps into fsCategoryStore
     * so FsDetail collectLines / changeFs includes custom fsId amounts.
     */
    prepareForPersist: () => Promise<void>
}

export interface ProfitLossProps {
    mode: ProfitLossMode;
    annualId: number;
    /** FS mode: preferred company id (FsDetail prop); falls back to store */
    companyId?: number | null;
    /**
     * Phase 4: MULTI PL Tab 子期间。adjustment 模式下表头与 plLine 优先用该片；
     * 缺省 = 整段 annual（SINGLE / 现网，#20）。
     */
    periodScope?: {
        periodFrom: string;
        periodTo: string;
        yearOfAssessment?: string | null;
        sliceKey: string;
    } | null;
    /**
     * MULTI TotalSingle：`'total'` 时 Client = Σ periods[].plLine（见 plMultiTotalView）。
     * 缺省 / `'multiple'` = 现网分片或整段行为。
     */
    plMultiViewMode?: PlMultiViewMode;
    // Adjustment-only
    onScheduleClick?: (item: any) => void;
    // FS Opening Edit Mode
    fsOpeningEditMode?: boolean;
    onFsOpeningDataChange?: (rowData: Array<{
        fsId: number;
        code: string;
        scheduleCode?: string;
        scheduleName: string;
        statement: 'BS' | 'PL';
        entry?: string;
        adjustmentAmount: number;
        openingAmount: number;
        closingAmount: number;
        adjustHisAmount: number;
    }>) => void;
    // Hide Last Year and Change columns
    hideLastYearColumns?: boolean;
    /** 只显示 Last Year，隐藏 Current 与 Change（Create GL 上年录入） */
    lastYearOnly?: boolean;
    /** Last Year AI 合并说明：fsCode → "Sales of goods (706,840); Sales Refund (-25,861)" */
    lastYearRemarkByFsCode?: Record<string, string> | null;
    /**
     * Phase 3: FS MULTI — YOA column headers (e.g. ["2022/23","2023/24"]).
     * When length >= 2, replaces single Current column. SINGLE / omit = unchanged (#20).
     */
    fsYoaColumns?: string[] | null;
    // Callback when clicking on adjustment cell (headerId, refNo, item)
    onAdjustmentCellClick?: (headerId: string, refNo: string, item: any) => void;
    // Shared Adjust Total mode state (controlled from parent for synchronization)
    adjustTotalMode?: boolean;
    onAdjustTotalModeChange?: (mode: boolean) => void;
    /** Report adjust column count (manual + Auto) so parent can pick initial Adjust Total vs detail */
    onAdjustColumnCountChange?: (count: number) => void;
    // 只读模式：所有节点不可编辑，仅做渲染显示
    readOnly?: boolean;
    /** GL FS Summary：点击科目名跳转 Tiny Mode（传 fsCode） */
    onFsLineClick?: (fsCode: string) => void;
    /** 可跳转的科目码；未传则凡有 code 的叶子均可点 */
    navigableFsCodes?: Set<string>;
    /** GL FS Summary：fsCode → Excel 导入列名，与 Tiny Mode 一致 */
    glExcelNameByFsCode?: Record<string, string> | null;
    /** GL FS Summary：fsCode → Excel 导入中文列名 */
    glExcelNameZhByFsCode?: Record<string, string> | null;
    /** GL FS Summary：开=客户别名，关=标准科目名（与 Tiny Mode 同源） */
    showClientAlias?: boolean;
    // Shared Audited Rounded mode state (controlled from parent for synchronization)
    auditedRounded?: AuditedMode;
    onAuditedRoundedChange?: (mode: AuditedMode) => void;
    /** Adjustment-only: allow double-click rename of unlocked leaf item names */
    itemAliasEditEnabled?: boolean;
    onItemAliasChange?: (payload: {
        fsId: number;
        code: string;
        statement: 'BS' | 'PL';
        displayName: string;
    }) => Promise<void>;
    /** Notify parent when internal table init loading flips (FsDetail progressive shell). */
    onLoadingChange?: (loading: boolean) => void;
}

export const ProfitLoss = forwardRef<ProfitLossHandle, ProfitLossProps>(({
                                                                             mode,
                                                                             annualId,
                                                                             companyId: companyIdProp = null,
                                                                             periodScope = null,
                                                                             plMultiViewMode = 'multiple',
                                                                             onScheduleClick,
                                                                             fsOpeningEditMode,
                                                                             onFsOpeningDataChange,
                                                                             hideLastYearColumns = false,
                                                                             lastYearOnly = false,
                                                                             lastYearRemarkByFsCode = null,
                                                                             fsYoaColumns = null,
                                                                             onAdjustmentCellClick,
                                                                             adjustTotalMode: controlledAdjustTotalMode,
                                                                             onAdjustTotalModeChange,
                                                                             onAdjustColumnCountChange,
                                                                             readOnly = false,
                                                                             onFsLineClick,
                                                                             navigableFsCodes,
                                                                             glExcelNameByFsCode = null,
                                                                             glExcelNameZhByFsCode = null,
                                                                             showClientAlias = false,
                                                                             auditedRounded: controlledAuditedRounded,
                                                                             onAuditedRoundedChange,
                                                                             itemAliasEditEnabled = false,
                                                                             onItemAliasChange,
                                                                             onLoadingChange,
                                                                         }, ref) => {
    // 根据 mode 选择数据源
    // adjustment 模式: 使用 fsAdjustStore (展示层 - queryAdjustmentList 数据)
    // fs 模式: 使用 fsCategoryStore (编辑层 - 用户编辑数据)

    // 正确订阅 store 的数据变化（selector，避免整 store 订阅触发重渲染风暴）
    const plTreeFromStore = useFsAdjustStore(state => state.adjustRecord?.plLine);
    const periodsFromStore = useFsAdjustStore(state => state.adjustRecord?.periods);
    const bsTreeFromStore = useFsAdjustStore(state => state.adjustRecord?.bsLine);
    const adjustSpanType = useFsAdjustStore(state => state.adjustRecord?.spanType);
    const adjustEngagementYoa = useFsAdjustStore(state => state.adjustRecord?.yearOfAssessment);
    const plAuditedBySlice = usePlMultiPeriodChainStore((s) => s.auditedBySlice);
    // Total/combine only: subscribe to per-tab rounded maps / AFTER_TAX bridge for Σ(round-each-tab).
    // Period tabs use stable empty refs so publishing maps does not re-render every sibling tab.
    const roundedAuditedBySlice = usePlMultiPeriodChainStore((s) => (
        plMultiViewMode === 'total' ? s.roundedAuditedBySlice : EMPTY_ROUNDED_AUDITED_BY_SLICE
    ));
    const afterTaxBySlice = usePlMultiPeriodChainStore((s) => (
        plMultiViewMode === 'total' ? s.afterTaxBySlice : EMPTY_AFTER_TAX_BY_SLICE
    ));
    const setPlSliceAudited = usePlMultiPeriodChainStore((s) => s.setSliceAudited);
    const setPlSliceAfterTax = usePlMultiPeriodChainStore((s) => s.setSliceAfterTax);
    const setPlSliceRoundedAudited = usePlMultiPeriodChainStore((s) => s.setSliceRoundedAudited);
    const clearPlPeriodChain = usePlMultiPeriodChainStore((s) => s.clear);
    const adjustRecordCompanyId = useFsAdjustStore(s => Number(s.adjustRecord?.companyId || 0) || 0);
    const plCotInputMode = useFsCategoryStore(s => s.plCotInputMode ?? 'COS');
    const setPlCotInputMode = useFsCategoryStore(s => s.setPlCotInputMode);

    const currentMode = useAdjustmentStore(s => s.currentMode);
    const mode2CapturedValue = useAdjustmentStore(s => s.mode2CapturedValue);
    const mode2CapturedFsId = useAdjustmentStore(s => s.mode2CapturedFsId);
    const mode2CapturedEntry = useAdjustmentStore(s => s.mode2CapturedEntry);
    const mode2AssistUsed = useAdjustmentStore(s => s.mode2AssistUsed);
    const setMode2CapturedValue = useAdjustmentStore(s => s.setMode2CapturedValue);
    const setMode2AssistUsed = useAdjustmentStore(s => s.setMode2AssistUsed);
    const fsOpeningResetTrigger = useAdjustmentStore(s => s.fsOpeningResetTrigger);

    const sCompanyId = useFsCategoryStore(s => s.companyId);
    const sCompanyName = useFsCategoryStore(s => s.companyName);
    const sPLTreeFromCategory = useFsCategoryStore(s => s.plTree);
    const sPLValues = useFsCategoryStore(s => s.plValues);
    const sPLLastYearValues = useFsCategoryStore(s => s.plLastYearValues);
    const sBsLastYearValues = useFsCategoryStore(s => s.bsLastYearValues);
    const sBsTree = useFsCategoryStore(s => s.bsTree);
    const sBsValues = useFsCategoryStore(s => s.bsValues);
    const setPlValues = useFsCategoryStore(s => s.setPlValues);
    const setPlLastYearValues = useFsCategoryStore(s => s.setPlLastYearValues);
    const setPLComputed = useFsCategoryStore(s => s.setPLComputed);
    const applyPlFsMountSync = useFsCategoryStore(s => s.applyPlFsMountSync);
    const sAdjustments = useFsCategoryStore(s => s.adjustments);
    const sAllowedScheduleCodes = useFsCategoryStore(s => s.allowedScheduleCodes);
    const sScheduleNameMap = useFsCategoryStore(s => s.scheduleNameMap);

    // adjustment 模式从 fsAdjustStore 获取树，fs 模式从 fsCategoryStore 获取
    // MULTI + periodScope：每 Tab 对应该 YOA 列（与 FS 多列一致），禁止空 plLine / Σ 合并到 tab0
    const sPLTreeFromAdjust = useMemo(() => {
        if (mode !== 'adjustment') return null;
        if (!plTreeFromStore) return null;

        // TotalSingle: Client = Σ each period slice openingAmount/client
        if (plMultiViewMode === 'total'
            && Array.isArray(periodsFromStore)
            && periodsFromStore.length >= 2) {
            const totalTree = buildTotalPlClientTree(
                periodsFromStore,
                plTreeFromStore,
                convertAuditFsItemVOForYoa,
            );
            if (totalTree && totalTree.length > 0) {
                return totalTree;
            }
        }

        const yoa = periodScope?.yearOfAssessment ? String(periodScope.yearOfAssessment).trim() : '';
        if (periodScope && yoa) {
            let source: any[] | null | undefined = null;
            if (Array.isArray(periodsFromStore) && periodsFromStore.length > 0) {
                const match = periodsFromStore.find(
                    (p) =>
                        (periodScope.sliceKey && p.key === periodScope.sliceKey) ||
                        (p.yearOfAssessment && String(p.yearOfAssessment).trim() === yoa)
                );
                // Only use period plLine when it has nodes (empty [] was the bug: blank tabs)
                if (match?.plLine && Array.isArray(match.plLine) && match.plLine.length > 0) {
                    source = match.plLine;
                }
            }
            // Fallback: top-level tree + YOA opening overlay (FS column amounts)
            if (!source) {
                source = plTreeFromStore;
            }
            return source.map((vo) => convertAuditFsItemVOForYoa(vo, yoa));
        }

        return plTreeFromStore.map(convertAuditFsItemVOToFsSimpleData);
    }, [mode, plTreeFromStore, periodsFromStore, periodScope?.sliceKey, periodScope?.yearOfAssessment, plMultiViewMode]);
    const sPLTree = mode === 'adjustment' ? sPLTreeFromAdjust : sPLTreeFromCategory;

    const sBSTreeFromAdjust = useMemo(() => {
        if (mode !== 'adjustment') return null;
        if (!bsTreeFromStore) return null;
        const tree = bsTreeFromStore.map(convertAuditFsItemVOToFsSimpleData);
        return tree;
    }, [mode, bsTreeFromStore]);

    const sBSTreeNested = useMemo(() => {
        // 必须返回稳定空引用：`[] || sBsTree` 里空数组为真，会误用新建 [] 并每轮触发 COT effect
        if (mode !== 'adjustment') return EMPTY_NESTED_BS_TREE;
        return toNestedTree((sBSTreeFromAdjust || []) as any[]);
    }, [mode, sBSTreeFromAdjust]);

    /** adjustment 用嵌套 BS；fs / lastYearOnly 用 store 扁平 BS（勿用 [] 短路） */
    const bsTreeForCot = useMemo(() => {
        if (mode === 'adjustment' && sBSTreeNested.length > 0) return sBSTreeNested;
        return sBsTree || EMPTY_NESTED_BS_TREE;
    }, [mode, sBSTreeNested, sBsTree]);

    const resolvedCompanyId = (
        Number(companyIdProp) || Number(sCompanyId) || adjustRecordCompanyId || undefined
    ) || undefined;
    const resolvedCompanyName = sCompanyName ?? null;
    const resolvedInitialTree = useMemo(() => sPLTree ?? [], [sPLTree]);
    const resolvedExternalValues = useMemo(() => sPLValues, [sPLValues]);
    const resolvedOnExternalChange = setPlValues;

    const resolvedAdjustments = sAdjustments ?? null;
    const resolvedAllowedScheduleCodes = sAllowedScheduleCodes;
    const resolvedScheduleNameMapFromParent = sScheduleNameMap;

    /** Seed from idle warmup / init snapshot — avoids recomputing formulas on first paint. */
    const fsComputeSeedRef = useRef<PlFsInitSnapshot | null>(null);
    const fsComputeSeedSigRef = useRef<string | null>(null);
    const lastFsInitSigRef = useRef<string | null>(null);

    const [fsTree, setFsTree] = useState<AuditBuildCompanyFSTreeItems[]>(() => {
        // Hydrate from FsDetail idle warmup on first mount (SINGLE + MULTI).
        // peek (not consume) so FsDetail's PL-open effect does not cold-rebuild the same snapshot.
        if (mode !== 'fs') return [];
        const flat = resolvedInitialTree;
        if (!flat || !Array.isArray(flat) || flat.length === 0) return [];
        const storeState = useFsCategoryStore.getState();
        const signature = buildPlFsWarmupSignature({
            annualId: Number(annualId),
            fsYoaColumns,
            plTreeLen: countFsTreeNodes(flat),
            plValues: resolvedExternalValues,
            plValuesByYoa: storeState.plValuesByYoa,
            plLastYearValues: storeState.plLastYearValues,
            bsValues: storeState.bsValues,
            bsLastYearValues: storeState.bsLastYearValues,
            cotInputMode: storeState.plCotInputMode ?? 'COS',
        });
        const snap = peekPlFsWarmup(signature);
        if (!snap) return [];
        fsComputeSeedRef.current = snap;
        fsComputeSeedSigRef.current = snap.signature;
        lastFsInitSigRef.current = snap.signature;
        return snap.fsTree as any;
    });
    const [periodLineText, setPeriodLineText] = useState<string | null>(null);
    const [presentationCurrency, setPresentationCurrency] = useState<string | null>(null);
    const [yearOfAssessment, setYearOfAssessment] = useState<string | null>(null);
    const [lastYearOfAssessment, setLastYearOfAssessment] = useState<string | null>(null);
    // Warm hit → skip loading skeleton; cold → effect builds snapshot
    const [loading, setLoading] = useState<boolean>(() => (mode === 'fs' ? !fsComputeSeedRef.current : true));

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);
    /** FS mode: inline edit custom leaf name (display ↔ InputField; no dialog) */
    const [customInlineEdit, setCustomInlineEdit] = useState<{
        editId: number;
        fsId: number;
        nameEn: string;
        saving: boolean;
    } | null>(null);
    /** Inline create draft above parent Level-2 subtotal (name + Current/Last Year) */
    const [customInlineDraft, setCustomInlineDraft] = useState<{
        parentFsId: number;
        parentCode?: string;
        parentEntry?: string;
        nameEn: string;
        currentAmount: number;
        lastYearAmount: number;
        amountsByYoa: Record<string, number>;
        saving: boolean;
    } | null>(null);
    /** After create+reload: apply draft amounts onto the new synthetic fsId */
    const pendingCustomDraftAmountsRef = useRef<{
        fsId: number;
        currentAmount: number;
        lastYearAmount: number;
        amountsByYoa: Record<string, number>;
    } | null>(null);
    /** Delete confirm Popover for custom node (always required before physical delete) */
    const [customDeleteAnchor, setCustomDeleteAnchor] = useState<{
        el: HTMLElement;
        item: any;
    } | null>(null);

    // FS mode state
    const [amountDisplays, setAmountDisplays] = useState<Record<number, string>>(
        () => fsComputeSeedRef.current?.amountDisplays ?? {},
    );
    /** Phase 3 MULTI: per-YOA leaf displays; only used when fsYoaColumns.length >= 2 */
    const [amountDisplaysByYoa, setAmountDisplaysByYoa] = useState<Record<string, Record<number, string>>>(
        () => fsComputeSeedRef.current?.amountDisplaysByYoa ?? {},
    );
    const isFsMulti = mode === 'fs' && Array.isArray(fsYoaColumns) && fsYoaColumns.length >= 2;
    // Stable empty list when not MULTI — inline `[]` made afterTax effect deps change every render
    const fsYoaList = isFsMulti ? (fsYoaColumns as string[]) : EMPTY_FS_YOA_LIST;
    const setPlValuesByYoa = useFsCategoryStore(s => s.setPlValuesByYoa);
    const [lastYearAmounts, setLastYearAmounts] = useState<Record<number, string>>(
        () => fsComputeSeedRef.current?.lastYearAmounts ?? {},
    );
    // Imperative persist flush reads latest maps (avoid stale useImperativeHandle closure)
    const amountDisplaysRef = useRef(amountDisplays);
    amountDisplaysRef.current = amountDisplays;
    const lastYearAmountsRef = useRef(lastYearAmounts);
    lastYearAmountsRef.current = lastYearAmounts;
    const amountDisplaysByYoaRef = useRef(amountDisplaysByYoa);
    amountDisplaysByYoaRef.current = amountDisplaysByYoa;
    const customInlineDraftRef = useRef(customInlineDraft);
    customInlineDraftRef.current = customInlineDraft;
    const customInlineEditRef = useRef(customInlineEdit);
    customInlineEditRef.current = customInlineEdit;

    // 共享的 expanded 状态（FS 和 adjustment 模式都使用）
    const [expanded, setExpanded] = useState<Record<number, boolean>>(
        () => fsComputeSeedRef.current?.expanded ?? {},
    );

    // ADJ mode state
    const [fsOpeningAmounts, setFsOpeningAmounts] = useState<Record<number, number>>({});

    // FS mode: COT input mode toggle - 'COS' (default) or 'PUR'
    // 'COS': user inputs Cost of Sales, Purchases is calculated
    // 'PUR': user inputs Purchases, Cost of Sales is calculated
    // Current and Last Year columns have independent toggle states
    const [cotInputModeCurrent, setCotInputModeCurrent] = useState<PlCotInputMode>(() =>
        useFsCategoryStore.getState().plCotInputMode ?? 'COS',
    );
    const [cotInputModeLastYear, setCotInputModeLastYear] = useState<PlCotInputMode>(() =>
        useFsCategoryStore.getState().plCotInputMode ?? 'COS',
    );
    /** PUR 模式下首次引入存货时冻结 COS 基数，避免 sync 覆盖 PUR 引发无限更新 */
    const frozenCosBaseRef = useRef<number | null>(null);
    /** 用户手动切换 COS↔PUR 后不再冻结，避免切换种子被再次抬高 */
    const cotFreezeSuppressedRef = useRef(false);
    /** PUR 可编时公式下限用的 COS 基数（切换前 COS 录入值） */
    const purCosFloorRef = useRef<number | null>(null);
    /** Cap root to remaining viewport; only TableContainer scrolls. */
    const fillRootRef = useRef<HTMLDivElement>(null);
    const fillHeightPx = useFillRemainingViewport(fillRootRef);
    /** Report table H-overflow to SplitTabsPanel for icon vs full-name tabs. */
    const tableContainerRef = useSplitTabsTableContainerRef<HTMLDivElement>();

    // Adjust Total mode: when true, merge all adjustment columns into one total column
    // Use controlled props if provided, otherwise use internal state
    const [internalAdjustTotalMode, setInternalAdjustTotalMode] = useState<boolean>(false);
    const adjustTotalMode = controlledAdjustTotalMode !== undefined ? controlledAdjustTotalMode : internalAdjustTotalMode;
    const setAdjustTotalMode = onAdjustTotalModeChange || setInternalAdjustTotalMode;

    /** Total view: false = Client Σ (merged); true = one Client column per YOA (split, display-only).
     *  Init = true：adjustment · MULTI · Total 视图初始化即 Client 按 YOA 拆分渲染（gating 见 canExpandClientByYoa）。 */
    const [clientByYoaExpanded, setClientByYoaExpanded] = useState(true);
    useEffect(() => {
        if (plMultiViewMode !== 'total') setClientByYoaExpanded(false);
    }, [plMultiViewMode]);

    // ===== Rounding Adjustment（仅 adjustment 模式）=====
    const queryClient = useQueryClient();
    const {data: roundingGroup} = useQueryRoundingAdjust(
        Number(annualId),
        mode === 'adjustment' && !!annualId
    );
    const plRoundingAdjustments = useMemo(() => {
        // Total 汇总：跨片 Σ Client；PL RoundingAdjustment 按 code 跨 YOA 加总后叠到对应节点（与 Audited 列三态同渲染）
        if (plMultiViewMode === 'total') {
            return buildRoundingAdjustmentMaps(roundingGroup).plDisplayMap;
        }
        // SINGLE / 无 periodScope：不传 options，走改造前 map 构建
        const yoa = periodScope?.yearOfAssessment
            ? String(periodScope.yearOfAssessment).trim()
            : '';
        if (!yoa) {
            return buildRoundingAdjustmentMaps(roundingGroup).plDisplayMap;
        }
        return buildRoundingAdjustmentMaps(roundingGroup, {yearOfAssessment: yoa}).plDisplayMap;
    }, [roundingGroup, periodScope?.yearOfAssessment, plMultiViewMode]);
    // 共享 Popover 删除确认状态
    const [deleteAnchor, setDeleteAnchor] = useState<{ el: HTMLElement; nodeCode: string } | null>(null);
    // 与 BalanceSheet 同步的 rounding 差值（用于 PL 表头警告）
    const [roundingDifference, setRoundingDifference] = useState(0);
    useEffect(() => subscribeRoundingDifference(setRoundingDifference), []);
    // 删除回调（乐观清空 React Query 缓存 + 回滚）
    const handleRoundingRemove = useCallback(() => {
        queryClient.setQueryData(['rounding-adjust', Number(annualId)], emptyRoundingAdjustGroup(Number(annualId)));
        roundingAdjustService.remove(Number(annualId)).then(() => {
            // 无感刷新：主调整列表后台 refetch
            queryClient.invalidateQueries({queryKey: ['adjustment', 'list']});
        }).catch(() => {
            queryClient.invalidateQueries({queryKey: ['rounding-adjust', Number(annualId)]});
        });
    }, [annualId, queryClient]);

    // 监听 fsOpeningResetTrigger 变化，清空 fsOpeningAmounts
    useEffect(() => {
        if (fsOpeningResetTrigger > 0) {
            setFsOpeningAmounts({});
        }
    }, [fsOpeningResetTrigger]);

    // Phase 6-B: reset PL lastYear chain when switching annual
    useEffect(() => {
        clearPlPeriodChain();
        frozenCosBaseRef.current = null;
        cotFreezeSuppressedRef.current = false;
        purCosFloorRef.current = null;
        return () => {
            clearPlPeriodChain();
        };
    }, [annualId, clearPlPeriodChain]);

    // 当 fsOpeningEditMode 激活时，展开包含 EXCLUSIVE_CODES 的父节点
    useEffect(() => {
        if (!fsOpeningEditMode || !fsTree || fsTree.length === 0) return;

        const plExclusiveCodes = Array.from(EXCLUSIVE_CODES).filter(code => code.startsWith('PL-'));
        const parentIdsToExpand: Set<number> = new Set();

        // 递归查找包含 EXCLUSIVE_CODES 的节点及其所有父节点
        const findParentsOfExclusiveCodes = (nodes: any[], parentIds: number[] = []): void => {
            for (const node of nodes || []) {
                const nodeCode = String(node?.code || '').trim().toUpperCase();
                const currentParentIds = [...parentIds];

                // 如果当前节点是 EXCLUSIVE_CODE，标记所有父节点需要展开
                if (plExclusiveCodes.includes(nodeCode)) {
                    currentParentIds.forEach(id => parentIdsToExpand.add(id));
                }

                // 如果有子节点，继续递归
                if (Array.isArray(node?.children) && node.children.length > 0) {
                    findParentsOfExclusiveCodes(node.children, [...currentParentIds, Number(node.fsId)]);
                }
            }
        };

        findParentsOfExclusiveCodes(fsTree as any[]);

        // 更新 expanded 状态，展开所有需要展开的父节点
        if (parentIdsToExpand.size > 0) {
            setExpanded(prev => {
                const next = {...prev};
                parentIdsToExpand.forEach(id => {
                    next[id] = true;
                });
                return next;
            });
        }
    }, [fsOpeningEditMode, fsTree]);

    const scheduleNameMap = useMemo(() => {
        if (resolvedScheduleNameMapFromParent) return resolvedScheduleNameMapFromParent;
        const map = new Map<string, string>();
        for (const hdr of (resolvedAdjustments || [])) {
            for (const ln of (hdr?.lines || [])) {
                const legacyScheduleCode = String((ln as any)?.scheduleCode || '').trim();
                const code = normalizeLineCode((ln as any)?.code || (ln as any)?.scheduleCode);
                const name = String((ln as any)?.scheduleName || '').trim();
                if (code && name && !map.has(code)) map.set(code, name);
                if (legacyScheduleCode && name && !map.has(legacyScheduleCode)) map.set(legacyScheduleCode, name);
            }
        }
        return map;
    }, [resolvedAdjustments, resolvedScheduleNameMapFromParent]);

    useEffect(() => {
        // Skip if no data
        if (!resolvedInitialTree || resolvedInitialTree.length === 0) {
            setLoading(false);
            return;
        }

        if (mode === 'fs') {
            // SINGLE + MULTI: consume idle warmup / build snapshot once (avoids cold toNestedTree + expand on click)
            const storeState = useFsCategoryStore.getState();
            // 读 getState() 而非闭包 resolvedExternalValues，避免把 sPLValues 放进 deps
            const signature = buildPlFsWarmupSignature({
                annualId: Number(annualId),
                fsYoaColumns,
                plTreeLen: countFsTreeNodes(resolvedInitialTree),
                plValues: storeState.plValues,
                plValuesByYoa: storeState.plValuesByYoa,
                plLastYearValues: storeState.plLastYearValues,
                bsValues: storeState.bsValues,
                bsLastYearValues: storeState.bsLastYearValues,
                cotInputMode: storeState.plCotInputMode ?? 'COS',
            });

            // Skip rebuild when COT/sync wrote plValues back (same structural signature)
            if (lastFsInitSigRef.current === signature && fsComputeSeedSigRef.current === signature) {
                setLoading(false);
                return;
            }

            const snap = consumePlFsWarmup(signature) || buildPlFsInitSnapshot({
                annualId: Number(annualId),
                plTreeFlat: resolvedInitialTree,
                plValues: storeState.plValues,
                plValuesByYoa: storeState.plValuesByYoa,
                plLastYearValues: storeState.plLastYearValues,
                bsTree: storeState.bsTree,
                bsValues: storeState.bsValues,
                bsLastYearValues: storeState.bsLastYearValues,
                cotInputMode: storeState.plCotInputMode ?? 'COS',
                fsYoaColumns,
            });

            if (snap) {
                fsComputeSeedRef.current = snap;
                fsComputeSeedSigRef.current = snap.signature;
                lastFsInitSigRef.current = snap.signature;
                // Merge post-create draft amounts into snap before setState (survives reload wipe)
                const pending = pendingCustomDraftAmountsRef.current;
                let displays = snap.amountDisplays;
                let displaysByYoa = snap.amountDisplaysByYoa;
                let lyDisplays = snap.lastYearAmounts;
                if (pending && Number.isFinite(pending.fsId) && pending.fsId > 0) {
                    pendingCustomDraftAmountsRef.current = null;
                    displays = {...displays, [pending.fsId]: String(pending.currentAmount || 0)};
                    lyDisplays = {...lyDisplays, [pending.fsId]: String(pending.lastYearAmount || 0)};
                    if (pending.amountsByYoa && Object.keys(pending.amountsByYoa).length) {
                        displaysByYoa = {...displaysByYoa};
                        for (const [yoa, num] of Object.entries(pending.amountsByYoa)) {
                            displaysByYoa[yoa] = {
                                ...(displaysByYoa[yoa] || {}),
                                [pending.fsId]: String(num || 0),
                            };
                        }
                    }
                    // Invalidate seed computed — maps now differ from warmup
                    fsComputeSeedRef.current = null;
                    fsComputeSeedSigRef.current = null;
                }
                // React 18 batches these into one render
                setFsTree(snap.fsTree as any);
                setAmountDisplays(displays);
                setAmountDisplaysByYoa(displaysByYoa);
                setLastYearAmounts(lyDisplays);
                // Keep prior expand state (e.g. parent opened for inline create)
                setExpanded((prev) => ({...snap.expanded, ...prev}));
                // Data/save maps: zero COT formula side for Current / per-YOA / Last Year immediately
                // (display maps keep baked formula; collectLines reads store)
                // 上年模式必须用 snapshot 里按「原始」上年金额推断的结果；
                // 勿对已 pre-bake 的 lastYearAmounts 再 infer（COS/PUR 被算成一致后会误判为 COS，清掉 PUR）。
                const cotMode = (storeState.plCotInputMode ?? 'COS') as PlCotInputMode;
                const cotModeLastYear = (snap.cotInputModeLastYear
                    ?? resolvePlCotInputMode(null, {
                        plTree: snap.fsTree,
                        bsTree: storeState.bsTree,
                        plValuesMap: storeState.plLastYearValues || {},
                        bsValuesMap: storeState.bsLastYearValues || {},
                        bsLastYearMap: storeState.bsLastYearValues || {},
                    })) as PlCotInputMode;
                const cotIds = snap.cotFsIds;
                storeState.setPlValues(
                    zeroPlCotFormulaSideInDataMap(displays, cotMode, cotIds),
                );
                // 落库 map：按原始上年侧清公式；不要用 bake 后的双边金额再 zero
                const lyBase = Object.keys(storeState.plLastYearValues || {}).length > 0
                    ? Object.fromEntries(
                        Object.entries(storeState.plLastYearValues).map(([k, v]) => [Number(k), String(v)]),
                    )
                    : lyDisplays;
                const lyMerged = pending
                    ? {...lyBase, [pending.fsId]: String(pending.lastYearAmount || 0)}
                    : lyBase;
                const lyDataForStore = zeroPlCotFormulaSideInDataMap(
                    lyMerged,
                    cotModeLastYear,
                    cotIds,
                );
                storeState.setPlLastYearValues(lyDataForStore);
                setCotInputModeLastYear(cotModeLastYear);
                if (Array.isArray(fsYoaColumns) && fsYoaColumns.length >= 2) {
                    const nextByYoa: Record<string, Record<number, string>> = {};
                    for (const yoa of fsYoaColumns) {
                        nextByYoa[yoa] = zeroPlCotFormulaSideInDataMap(
                            displaysByYoa[yoa] || {},
                            cotMode,
                            cotIds,
                        );
                    }
                    storeState.setPlValuesByYoa(nextByYoa);
                }
                setLoading(false);
                return;
            }
        }

        // adjustment / non-fs fallback: original init (no warmup snapshot)
        fsComputeSeedRef.current = null;
        fsComputeSeedSigRef.current = null;
        lastFsInitSigRef.current = null;

        const tree = toNestedTree((resolvedInitialTree || []) as any[]);
        setFsTree(tree as any);
        setLoading(false);
        // 勿把 resolvedExternalValues(=sPLValues) 放进 deps：本 effect 会 setPlValues，
        // 旧依赖会在 COT sync / Last Year 灌表时自触发 Maximum update depth。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedInitialTree, mode, annualId, fsYoaColumns]);

    useEffect(() => {
        if (mode !== 'fs') return;
        // Client：用落库 plValues（公式侧为 0）做零值规则，勿用 amountDisplays（双边都有展示值）
        // lastYearOnly：跳过本年 COT 偏好推断（GL 上年步不编辑本年）
        if (!lastYearOnly) {
            if (fsTree.length > 0) {
                const curMap = sPLValues || {};
                const nextCur = resolvePlCotInputMode(plCotInputMode, {
                    plTree: fsTree,
                    bsTree: bsTreeForCot,
                    plValuesMap: curMap,
                    bsValuesMap: sBsValues || {},
                    bsLastYearMap: sBsLastYearValues || {},
                });
                setCotInputModeCurrent((prev) => (prev === nextCur ? prev : nextCur));
                if (nextCur !== plCotInputMode) {
                    setPlCotInputMode(nextCur);
                }
            } else {
                setCotInputModeCurrent(plCotInputMode);
            }
        }
        // Last Year：从落库 map 推断可编侧（手动 toggle 写入后仍以零值规则对齐）
        const lyMap = Object.keys(sPLLastYearValues || {}).length > 0
            ? (sPLLastYearValues || {})
            : lastYearAmounts;
        if (Object.keys(lyMap).length > 0 && fsTree.length > 0) {
            // 用 functional update 读当前 preference，勿把 cotInputModeLastYear 放进 deps，
            // 否则 resolve(COS)↔resolve(PUR) 会自触发无限 setState（Last Year MoneyField 爆栈）。
            setCotInputModeLastYear((prev) => {
                const next = resolvePlCotInputMode(prev, {
                    plTree: fsTree,
                    bsTree: bsTreeForCot,
                    plValuesMap: lyMap,
                    bsValuesMap: sBsLastYearValues || {},
                    bsLastYearMap: sBsLastYearValues || {},
                });
                return next === prev ? prev : next;
            });
        } else if (!lastYearOnly) {
            setCotInputModeLastYear((prev) => (prev === plCotInputMode ? prev : plCotInputMode));
        }
    }, [
        mode, lastYearOnly, plCotInputMode, sPLValues, lastYearAmounts, sPLLastYearValues, fsTree,
        bsTreeForCot, sBsValues, sBsLastYearValues, setPlCotInputMode,
    ]);

    const isProtectedParentHeader = useCallback((item: AuditBuildCompanyFSTreeItems) => plIsProtectedParentHeader(item), []);

    const isEditableLeaf = useCallback((item: any, inputMode?: 'COS' | 'PUR'): boolean => {
        if (readOnly) return false;
        const leaf = !Array.isArray(item?.children) || item.children.length === 0;
        if (mode !== 'fs') return leaf && !isProtectedParentHeader(item);
        const modeToUse = inputMode ?? cotInputModeCurrent;
        const isOI = isCode(item, ...FS_CODE.PL.OPENING_INVENTORIES);
        const isPUR = isCode(item, ...FS_CODE.PL.PURCHASES);
        const isCI = isCode(item, ...FS_CODE.PL.CLOSING_INVENTORIES);
        const isCOS = isCode(item, ...FS_CODE.PL.COST_OF_SALES);
        if (modeToUse === 'COS') {
            const isNonEditable = isOI || isPUR || isCI;
            return leaf && !isProtectedParentHeader(item) && !isNonEditable;
        }
        const isNonEditable = isOI || isCI || isCOS;
        return leaf && !isProtectedParentHeader(item) && !isNonEditable;
    }, [isProtectedParentHeader, mode, readOnly, cotInputModeCurrent]);

    const forEachNode = useCallback((nodes: any[], fn: (n: any, level: number) => void, level = 0) => {
        for (const n of nodes || []) {
            fn(n, level);
            if (hasChildren(n)) forEachNode(n.children, fn, level + 1);
        }
    }, []);

    const sumLeafDescendantsFS = useCallback((node: any): number => {
        if (!hasChildren(node)) {
            if (isProtectedParentHeader(node)) return 0;
            return parseAmountDisplay(amountDisplays[node.fsId] || '');
        }
        let sum = 0;
        for (const ch of node.children || []) sum += sumLeafDescendantsFS(ch);
        return sum;
    }, [amountDisplays, isProtectedParentHeader]);

    // Helper function to find node by fsId
    const findNodeByFsId = useCallback((nodes: any[], targetFsId: number): any => {
        for (const node of nodes || []) {
            if (Number(node.fsId) === targetFsId) return node;
            if (Array.isArray(node.children)) {
                const found = findNodeByFsId(node.children, targetFsId);
                if (found) return found;
            }
        }
        return null;
    }, []);

    // Get BS Inventories values for PL calculations (FS mode only)
    const bsInventoriesCurrentAmount = useMemo(() => {
        if (mode !== 'fs') return 0;
        const inventoriesNode = findNodeByCodes((sBsTree || []) as any[], FS_CODE.BS.INVENTORIES);
        if (!inventoriesNode) return 0;
        return parseAmountDisplay(sBsValues[inventoriesNode.fsId] || '0');
    }, [mode, sBsTree, sBsValues]);

    const bsInventoriesLastYearAmount = useMemo(() => {
        if (mode !== 'fs') return 0;
        const inventoriesNode = findNodeByCodes((sBsTree || []) as any[], FS_CODE.BS.INVENTORIES);
        if (!inventoriesNode) return 0;
        return parseAmountDisplay(sBsLastYearValues[inventoriesNode.fsId] || '0');
    }, [mode, sBsTree, sBsLastYearValues]);

    const filterHiddenCostOfSalesRows = useCallback(<T extends { item: any; isSubtotal?: boolean }>(flatRows: T[]): T[] => {
        return filterHiddenCostOfSalesFlatRows(flatRows, FS_CODE.PL.COST_OF_SALES);
    }, []);

    const plCotFsIds = useMemo(() => {
        if (mode !== 'fs' || !fsTree || fsTree.length === 0) return EMPTY_PL_COT_FS_IDS;
        return resolvePlCotFsIds(fsTree);
    }, [mode, fsTree]);

    // Inline create draft: include typed amounts in parent rollup / PAT / BS balancing before FS save
    const fsTreeForCompute = useMemo(() => {
        if (mode !== 'fs' || !customInlineDraft || !fsTree?.length) return fsTree;
        return injectCustomInlineDraftLeaf(fsTree as any[], customInlineDraft.parentFsId) as typeof fsTree;
    }, [mode, fsTree, customInlineDraft]);

    const amountDisplaysForCompute = useMemo(
        () => overlayCustomInlineDraftAmounts(amountDisplays, customInlineDraft),
        [amountDisplays, customInlineDraft],
    );

    const lastYearAmountsForCompute = useMemo(
        () => overlayCustomInlineDraftLastYear(lastYearAmounts, customInlineDraft),
        [lastYearAmounts, customInlineDraft],
    );

    const amountDisplaysByYoaForCompute = useMemo(
        () => overlayCustomInlineDraftByYoa(amountDisplaysByYoa, customInlineDraft),
        [amountDisplaysByYoa, customInlineDraft],
    );

    /** Phase 3: per-YOA computed maps for MULTI FS columns */
    const computeAllComputedAmountsByYoa = useMemo(() => {
        if (!isFsMulti || !fsTreeForCompute || fsTreeForCompute.length === 0) return EMPTY_COMPUTED_BY_YOA;
        const seed = fsComputeSeedRef.current;
        if (
            !customInlineDraft
            && seed?.computedByYoa
            && seed.fsTree === fsTree
            && seed.amountDisplaysByYoa === amountDisplaysByYoa
        ) {
            return seed.computedByYoa;
        }
        const out: Record<string, Record<number, number>> = {};
        for (const yoa of fsYoaList) {
            out[yoa] = computePlFsAmountsFromDisplays({
                fsTree: fsTreeForCompute,
                amountDisplays: amountDisplaysByYoaForCompute[yoa] || {},
                bsInventoriesCurrentAmount,
                bsInventoriesLastYearAmount,
                cotInputMode: cotInputModeCurrent,
                cotFsIds: plCotFsIds,
                frozenCosBaseRef,
                cotFreezeSuppressedRef,
                purCosFloorRef,
            });
        }
        return out;
    }, [
        isFsMulti,
        fsYoaList,
        fsTree,
        fsTreeForCompute,
        amountDisplaysByYoa,
        amountDisplaysByYoaForCompute,
        customInlineDraft,
        bsInventoriesCurrentAmount,
        bsInventoriesLastYearAmount,
        cotInputModeCurrent,
        plCotFsIds,
    ]);

    const computeAllComputedAmounts = useMemo(() => {
        if (mode !== 'fs' || !fsTreeForCompute || fsTreeForCompute.length === 0) {
            return EMPTY_COMPUTED_AMOUNTS;
        }
        // Warmup seed: skip full recompute on first paint (SINGLE + MULTI); skip when draft overlay active
        const seed = fsComputeSeedRef.current;
        if (
            !customInlineDraft
            && seed?.computed
            && seed.fsTree === fsTree
            && seed.amountDisplays === amountDisplays
        ) {
            return seed.computed;
        }
        return computePlFsAmountsFromDisplays({
            fsTree: fsTreeForCompute,
            amountDisplays: amountDisplaysForCompute,
            bsInventoriesCurrentAmount,
            bsInventoriesLastYearAmount,
            cotInputMode: cotInputModeCurrent,
            cotFsIds: plCotFsIds,
            frozenCosBaseRef,
            cotFreezeSuppressedRef,
            purCosFloorRef,
        });
    }, [
        mode,
        fsTree,
        fsTreeForCompute,
        amountDisplays,
        amountDisplaysForCompute,
        customInlineDraft,
        bsInventoriesCurrentAmount,
        bsInventoriesLastYearAmount,
        cotInputModeCurrent,
        plCotFsIds,
    ]);

    // Computed amounts for Last Year column
    const computeAllLastYearAmounts = useMemo(() => {
        if (mode !== 'fs' || !fsTreeForCompute || fsTreeForCompute.length === 0) {
            return EMPTY_COMPUTED_AMOUNTS;
        }
        const seed = fsComputeSeedRef.current;
        if (
            !customInlineDraft
            && seed?.lastYearComputed
            && seed.fsTree === fsTree
            && seed.lastYearAmounts === lastYearAmounts
        ) {
            return seed.lastYearComputed;
        }
        return computePlLastYearAmountsFromDisplays({
            fsTree: fsTreeForCompute,
            lastYearAmounts: lastYearAmountsForCompute,
            bsInventoriesLastYearAmount,
            cotInputMode: cotInputModeLastYear,
            cotFsIds: plCotFsIds,
        });
    }, [
        mode, fsTree, fsTreeForCompute, lastYearAmounts, lastYearAmountsForCompute,
        customInlineDraft, bsInventoriesLastYearAmount, cotInputModeLastYear, plCotFsIds,
    ]);

    /** COS↔PUR：按上一录入侧重算一致的一对金额；渲染仍走 computed 公式。 */
    // Current / Last Year COT 独立切换：切本年 COS 不得用 applyPlCotInputModeSwitch 清掉上年 PUR
    // （MOON：本年 COS、上年仅 PUR；联切会导致上年成本被写成 0，Cap≠Net Assets）。
    const toggleCotInputModeCurrent = useCallback((next: PlCotInputMode) => {
        fsComputeSeedRef.current = null;
        fsComputeSeedSigRef.current = null;
        // 手动切换后停止「首次存货」冻结，避免下一轮 PUR 再把 COS/PUR 抬高
        cotFreezeSuppressedRef.current = true;
        frozenCosBaseRef.current = null;

        const oiAmt = bsInventoriesLastYearAmount;
        const ciAmt = -bsInventoriesCurrentAmount;
        const store = useFsCategoryStore.getState();
        const cur = applyPlCotInputModeSwitch({
            nextMode: next,
            prevMode: cotInputModeCurrent,
            cotFsIds: plCotFsIds,
            displays: amountDisplays,
            dataMap: store.plValues || {},
            computed: computeAllComputedAmounts,
            oiAmt,
            ciAmt,
        });
        setAmountDisplays(cur.displays);
        setPlValues(cur.dataMap);
        purCosFloorRef.current = next === 'PUR' ? cur.purCosFloor : null;

        if (isFsMulti) {
            const nextDisplaysByYoa: Record<string, Record<number, string>> = {
                ...(amountDisplaysByYoa || {}),
            };
            const nextByYoa: Record<string, Record<number, string>> = {
                ...(store.plValuesByYoa || {}),
            };
            for (const yoa of fsYoaList) {
                const slice = applyPlCotInputModeSwitch({
                    nextMode: next,
                    prevMode: cotInputModeCurrent,
                    cotFsIds: plCotFsIds,
                    displays: amountDisplaysByYoa[yoa] || {},
                    dataMap: store.plValuesByYoa?.[yoa] || amountDisplaysByYoa[yoa] || {},
                    computed: computeAllComputedAmountsByYoa[yoa] || {},
                    oiAmt,
                    ciAmt,
                });
                nextDisplaysByYoa[yoa] = slice.displays;
                nextByYoa[yoa] = slice.dataMap;
            }
            setAmountDisplaysByYoa(nextDisplaysByYoa);
            setPlValuesByYoa(nextByYoa);
        }

        setCotInputModeCurrent(next);
        setPlCotInputMode(next);
    }, [
        plCotFsIds,
        amountDisplays,
        amountDisplaysByYoa,
        computeAllComputedAmounts,
        computeAllComputedAmountsByYoa,
        cotInputModeCurrent,
        bsInventoriesCurrentAmount,
        bsInventoriesLastYearAmount,
        isFsMulti,
        fsYoaList,
        setPlValues,
        setPlValuesByYoa,
        setPlCotInputMode,
    ]);

    /** Last Year COS↔PUR：独立于 Current；不写 plCotInputMode。 */
    const toggleCotInputModeLastYear = useCallback((next: PlCotInputMode) => {
        fsComputeSeedRef.current = null;
        fsComputeSeedSigRef.current = null;

        // WHY: Last Year OI/CI 与 computePlLastYearAmountsFromDisplays 一致（CI = −BS LY INV）
        const oiAmt = bsInventoriesLastYearAmount;
        const ciAmt = -bsInventoriesLastYearAmount;
        const store = useFsCategoryStore.getState();
        const ly = applyPlCotInputModeSwitch({
            nextMode: next,
            prevMode: cotInputModeLastYear,
            cotFsIds: plCotFsIds,
            displays: lastYearAmounts,
            dataMap: store.plLastYearValues || {},
            computed: computeAllLastYearAmounts,
            oiAmt,
            ciAmt,
        });
        setLastYearAmounts(ly.displays);
        setPlLastYearValues(ly.dataMap);
        setCotInputModeLastYear(next);
    }, [
        plCotFsIds,
        lastYearAmounts,
        computeAllLastYearAmounts,
        cotInputModeLastYear,
        bsInventoriesLastYearAmount,
        setPlLastYearValues,
    ]);

    // Batch sync — MULTI only (SINGLE keeps original separate store writes)
    // Mirrors SINGLE COT sync per YOA column: write derived PUR/COS/OI/CI into
    // amountDisplaysByYoa (non-editable) + plValuesByYoa (save), then aggregate Current.
    useEffect(() => {
        if (mode !== 'fs' || lastYearOnly || !fsTree || fsTree.length === 0) return;
        if (!isFsMulti) return;

        const cotFsIds = [
            plCotFsIds.oiFsId,
            plCotFsIds.purFsId,
            plCotFsIds.ciFsId,
            plCotFsIds.cosFsId,
        ].filter((id): id is number => id != null && Number.isFinite(id));

        // —— Per-YOA: same as SINGLE plValues / amountDisplays COT sync ——
        let nextByYoa: Record<string, Record<number, string>> | null = null;
        let displaysByYoaChanged = false;
        const nextDisplaysByYoa: Record<string, Record<number, string>> = {};

        for (const yoa of fsYoaList) {
            const computed = computeAllComputedAmountsByYoa[yoa] || {};
            const cotUpdates: Record<number, string> = {};
            for (const fsId of cotFsIds) {
                if (Object.prototype.hasOwnProperty.call(computed, fsId)) {
                    cotUpdates[fsId] = String(computed[fsId]);
                }
            }
            if (Object.keys(cotUpdates).length === 0) continue;

            const prevCol = amountDisplaysByYoa[yoa] || {};
            const mergedCol = {...prevCol, ...cotUpdates};
            // Save map: formula side (PUR or COS) stays 0; displays keep full computed
            const dataCol = zeroPlCotFormulaSideInDataMap(
                mergedCol,
                cotInputModeCurrent,
                plCotFsIds,
            );
            let plColChanged = false;
            const storeCol = useFsCategoryStore.getState().plValuesByYoa?.[yoa] || {};
            for (const fsId of cotFsIds) {
                if ((dataCol[fsId] ?? '0') !== (storeCol[fsId] ?? '0')) {
                    plColChanged = true;
                    break;
                }
            }
            if (plColChanged) {
                if (!nextByYoa) {
                    nextByYoa = {...(useFsCategoryStore.getState().plValuesByYoa || {})};
                    for (const y of fsYoaList) {
                        if (!nextByYoa[y]) {
                            nextByYoa[y] = zeroPlCotFormulaSideInDataMap(
                                {...(amountDisplaysByYoa[y] || {})},
                                cotInputModeCurrent,
                                plCotFsIds,
                            );
                        }
                    }
                }
                nextByYoa[yoa] = dataCol;
            }

            const displayCol = {...(nextDisplaysByYoa[yoa] || amountDisplaysByYoa[yoa] || {})};
            let colDisplayChanged = false;
            for (const [fsId, val] of Object.entries(cotUpdates)) {
                const id = Number(fsId);
                const node = findNodeByFsId((fsTree || []) as any[], id);
                if (!node || isEditableLeaf(node)) continue;
                if (displayCol[id] !== val) {
                    displayCol[id] = val;
                    colDisplayChanged = true;
                }
            }
            if (colDisplayChanged) {
                displaysByYoaChanged = true;
                nextDisplaysByYoa[yoa] = displayCol;
            }
        }

        if (nextByYoa) {
            setPlValuesByYoa(nextByYoa);
        }
        if (displaysByYoaChanged) {
            setAmountDisplaysByYoa((prev) => {
                let changed = false;
                const next = {...prev};
                for (const [yoa, col] of Object.entries(nextDisplaysByYoa)) {
                    const prevCol = next[yoa] || {};
                    let colChanged = false;
                    const merged = {...prevCol};
                    for (const [fsId, val] of Object.entries(col)) {
                        const id = Number(fsId);
                        if (merged[id] !== val) {
                            merged[id] = val;
                            colChanged = true;
                        }
                    }
                    if (colChanged) {
                        next[yoa] = merged;
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        }

        // —— Aggregate Current (formula on summed displays; OI/CI applied once) ——
        const cotUpdates: Record<number, string> = {};
        for (const fsId of cotFsIds) {
            if (Object.prototype.hasOwnProperty.call(computeAllComputedAmounts, fsId)) {
                cotUpdates[fsId] = String(computeAllComputedAmounts[fsId]);
            }
        }

        let nextPlValues: Record<number, string> | undefined;
        if (Object.keys(cotUpdates).length > 0) {
            const dataUpdates = zeroPlCotFormulaSideInDataMap(
                cotUpdates,
                cotInputModeCurrent,
                plCotFsIds,
            );
            const currentPlValues = useFsCategoryStore.getState().plValues;
            const merged = {...currentPlValues};
            let plChanged = false;
            for (const [fsId, val] of Object.entries(dataUpdates)) {
                const id = Number(fsId);
                if (merged[id] !== val) {
                    merged[id] = val;
                    plChanged = true;
                }
            }
            if (plChanged) nextPlValues = merged;

            // Snapshot pre-bakes COT into amountDisplays — only touch local state if still mismatched
            let displayNeedsUpdate = false;
            for (const [fsId, val] of Object.entries(cotUpdates)) {
                const id = Number(fsId);
                const node = findNodeByFsId((fsTree || []) as any[], id);
                if (!node || isEditableLeaf(node)) continue;
                if (amountDisplays[id] !== val) {
                    displayNeedsUpdate = true;
                    break;
                }
            }
            if (displayNeedsUpdate) {
                setAmountDisplays((prev) => {
                    let changed = false;
                    const next = {...prev};
                    for (const [fsId, val] of Object.entries(cotUpdates)) {
                        const id = Number(fsId);
                        const node = findNodeByFsId((fsTree || []) as any[], id);
                        if (!node || isEditableLeaf(node)) continue;
                        if (next[id] !== val) {
                            next[id] = val;
                            changed = true;
                        }
                    }
                    return changed ? next : prev;
                });
            }
        }

        const afterTaxFsId = plCotFsIds.afterTaxFsId;
        let afterTax: number | undefined;
        let afterTaxLastYear: number | undefined;
        if (afterTaxFsId) {
            // MULTI FS: BS-EQ-LFP Current = Σ(PL-PAT across YOA columns).
            // BS-EQ-AL Current = LY(AL)+LY(LFP)+LY(DIV); LY(LFP) must stay comparative
            // lastYear map only — do not inject prior-YOA PAT (would double-count with Σ LFP).
            afterTax = 0;
            for (const yoa of fsYoaList) {
                afterTax += computeAllComputedAmountsByYoa[yoa]?.[afterTaxFsId] ?? 0;
            }
            afterTaxLastYear = computeAllLastYearAmounts[afterTaxFsId] ?? 0;
        }

        const lyMode = resolvePlCotInputMode(cotInputModeLastYear, {
            plTree: fsTree,
            bsTree: bsTreeForCot,
            plValuesMap: lastYearAmounts,
            bsValuesMap: sBsLastYearValues || {},
            bsLastYearMap: sBsLastYearValues || {},
        });
        applyPlFsMountSync({
            plValues: nextPlValues,
            plLastYearValues: zeroPlCotFormulaSideInDataMap(
                lastYearAmounts,
                lyMode,
                plCotFsIds,
            ),
            afterTax,
            afterTaxLastYear,
        });
    }, [
        mode,
        lastYearOnly,
        fsTree,
        amountDisplays,
        amountDisplaysByYoa,
        computeAllComputedAmounts,
        computeAllComputedAmountsByYoa,
        computeAllLastYearAmounts,
        lastYearAmounts,
        isFsMulti,
        fsYoaList,
        plCotFsIds,
        cotInputModeCurrent,
        cotInputModeLastYear,
        applyPlFsMountSync,
        isEditableLeaf,
        findNodeByFsId,
        setPlValuesByYoa,
        bsTreeForCot,
        sBsLastYearValues,
    ]);

    // SINGLE: original separate store syncs (unchanged behavior)
    // 清公式侧前按上年金额 resolve 模式，避免初始/本年偏好仍是 COS 时把上年 PUR 写成 0。
    // lastYearOnly：仍同步上年 map（供 BS LFP），但勿用「展示双边」金额反复 zero 触发模式翻转。
    useEffect(() => {
        if (mode !== 'fs' || isFsMulti) return;
        const store = useFsCategoryStore.getState();
        if (
            Object.keys(lastYearAmounts).length === 0
            && Object.keys(store.plLastYearValues || {}).length > 0
        ) {
            return;
        }
        const lyMode = resolvePlCotInputMode(cotInputModeLastYear, {
            plTree: fsTree,
            bsTree: bsTreeForCot,
            // 优先用已落库的上年（公式侧已清），避免 display 双边金额导致 COS↔PUR 来回写
            plValuesMap: Object.keys(store.plLastYearValues || {}).length > 0
                ? store.plLastYearValues
                : lastYearAmounts,
            bsValuesMap: sBsLastYearValues || {},
            bsLastYearMap: sBsLastYearValues || {},
        });
        const sourceMap = Object.keys(lastYearAmounts).length > 0
            ? lastYearAmounts
            : (store.plLastYearValues || {});
        store.setPlLastYearValues(
            zeroPlCotFormulaSideInDataMap(sourceMap, lyMode, plCotFsIds),
        );
    }, [
        mode, isFsMulti, lastYearAmounts, cotInputModeLastYear, plCotFsIds,
        fsTree, bsTreeForCot, sBsLastYearValues,
    ]);

    useEffect(() => {
        // GL Last Year 步不编辑本年：跳过本年 COT→plValues/amountDisplays 回写，断更新环
        if (mode !== 'fs' || isFsMulti || lastYearOnly || !fsTree || fsTree.length === 0) return;

        const oiNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.OPENING_INVENTORIES);
        const purNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.PURCHASES);
        const ciNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.CLOSING_INVENTORIES);
        const cosNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.COST_OF_SALES);

        const cotFsIds = [oiNode?.fsId, purNode?.fsId, ciNode?.fsId, cosNode?.fsId]
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id));

        const cotUpdates: Record<number, string> = {};
        for (const fsId of cotFsIds) {
            if (Object.prototype.hasOwnProperty.call(computeAllComputedAmounts, fsId)) {
                cotUpdates[fsId] = String(computeAllComputedAmounts[fsId]);
            }
        }
        if (Object.keys(cotUpdates).length === 0) return;

        const dataUpdates = zeroPlCotFormulaSideInDataMap(
            cotUpdates,
            cotInputModeCurrent,
            plCotFsIds,
        );
        const currentPlValues = useFsCategoryStore.getState().plValues;
        let plChanged = false;
        const nextPlValues = {...currentPlValues};
        for (const [fsId, val] of Object.entries(dataUpdates)) {
            const id = Number(fsId);
            if (nextPlValues[id] !== val) {
                nextPlValues[id] = val;
                plChanged = true;
            }
        }
        if (plChanged) {
            setPlValues(nextPlValues);
        }

        setAmountDisplays((prev) => {
            let changed = false;
            const next = {...prev};
            for (const [fsId, val] of Object.entries(cotUpdates)) {
                const id = Number(fsId);
                const node = findNodeByFsId((fsTree || []) as any[], id);
                if (!node || isEditableLeaf(node)) continue;
                if (next[id] !== val) {
                    next[id] = val;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [
        mode,
        isFsMulti,
        lastYearOnly,
        fsTree,
        computeAllComputedAmounts,
        setPlValues,
        isEditableLeaf,
        findNodeByFsId,
        cotInputModeCurrent,
        plCotFsIds,
    ]);

    // SINGLE Last Year：公式侧写回 lastYearAmounts（可编叶子跳过）；store 仍清公式侧
    useEffect(() => {
        if (mode !== 'fs' || isFsMulti || !fsTree || fsTree.length === 0) return;

        const oiNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.OPENING_INVENTORIES);
        const purNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.PURCHASES);
        const ciNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.CLOSING_INVENTORIES);
        const cosNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.COST_OF_SALES);

        const cotFsIds = [oiNode?.fsId, purNode?.fsId, ciNode?.fsId, cosNode?.fsId]
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id));

        const cotUpdates: Record<number, string> = {};
        for (const fsId of cotFsIds) {
            if (Object.prototype.hasOwnProperty.call(computeAllLastYearAmounts, fsId)) {
                cotUpdates[fsId] = String(computeAllLastYearAmounts[fsId]);
            }
        }
        if (Object.keys(cotUpdates).length === 0) return;

        const dataUpdates = zeroPlCotFormulaSideInDataMap(
            cotUpdates,
            cotInputModeLastYear,
            plCotFsIds,
        );
        const currentLy = useFsCategoryStore.getState().plLastYearValues || {};
        let storeChanged = false;
        const nextStore = {...currentLy};
        for (const [fsId, val] of Object.entries(dataUpdates)) {
            const id = Number(fsId);
            if (nextStore[id] !== val) {
                nextStore[id] = val;
                storeChanged = true;
            }
        }
        if (storeChanged) {
            setPlLastYearValues(nextStore);
        }

        setLastYearAmounts((prev) => {
            let changed = false;
            const next = {...prev};
            for (const [fsId, val] of Object.entries(cotUpdates)) {
                const id = Number(fsId);
                const node = findNodeByFsId((fsTree || []) as any[], id);
                // 可编侧（含 Last Year 模式）不覆盖用户输入
                if (!node || isEditableLeaf(node, cotInputModeLastYear)) continue;
                if (next[id] !== val) {
                    next[id] = val;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [
        mode,
        isFsMulti,
        fsTree,
        computeAllLastYearAmounts,
        setPlLastYearValues,
        isEditableLeaf,
        findNodeByFsId,
        cotInputModeLastYear,
        plCotFsIds,
    ]);

    useEffect(() => {
        if (mode !== 'fs' || isFsMulti) return;
        const AFTER_TAX = findNodeByCodes(fsTree as any[], FS_CODE.PL.AFTER_TAX);
        if (AFTER_TAX) {
            const valLastYear = computeAllLastYearAmounts[AFTER_TAX.fsId] ?? 0;
            try {
                if (lastYearOnly) {
                    // 只推上年 PAT，避免本年 afterTax 与 BS/灌表互相覆盖
                    setPLComputed({ afterTaxLastYear: valLastYear });
                } else {
                    const val = computeAllComputedAmounts[AFTER_TAX.fsId] ?? 0;
                    setPLComputed({ afterTax: val, afterTaxLastYear: valLastYear });
                }
                if (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_FS_BALANCE') === '1') {
                    // eslint-disable-next-line no-console
                    console.log('[FS_BALANCE][PL→BS afterTax]', {
                        afterTaxLastYear: valLastYear,
                        afterTaxFsId: AFTER_TAX.fsId,
                        lastYearOnly,
                    });
                }
            } catch {
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, isFsMulti, lastYearOnly, computeAllComputedAmounts, computeAllLastYearAmounts, fsTree]);

    // Compute period label via annualMeta for both fs and adjustment modes
    // Phase 4: when periodScope set (MULTI PL Tab), use sub-period for header (#20: omit = unchanged)
    useEffect(() => {
        let mounted = true;
        const run = async () => {
            try {
                let periodFrom = periodScope?.periodFrom;
                let periodTo = periodScope?.periodTo;
                if (!periodFrom || !periodTo) {
                    const meta = await fetchAnnualMeta(Number(annualId));
                    periodFrom = meta.periodFrom || '';
                    periodTo = meta.periodTo || '';
                }
                const days = daysBetween(periodFrom, periodTo);
                const isYear = days === 365 || days === 366;
                const periodToDate = parseDateFlexible(periodTo || '');
                const periodFromDate = parseDateFlexible(periodFrom || '');
                let label: string;
                if (isYear) {
                    label = 'For the year ended ' + formatDayMonYYYY(periodToDate);
                } else {
                    label = 'For the period from ' + formatDayMonYYYY(periodFromDate) + ' to ' + formatDayMonYYYY(periodToDate);
                }
                if (mounted) {
                    setPeriodLineText(label);
                    if (mode === 'fs') {
                        try {
                            if (isFsMulti) {
                                useFsCategoryStore.getState().applyPlFsMountSync({
                                    periodType: isYear ? 'year' : 'period',
                                });
                            } else {
                                setPLComputed({periodType: isYear ? 'year' : 'period'});
                            }
                        } catch {
                        }
                    }
                }
            } catch {
            }
        };
        run();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [annualId, periodScope?.periodFrom, periodScope?.periodTo]);

    // Load presentationCurrency and yearOfAssessment via companyAnnualStore
    useEffect(() => {
        let mounted = true;
        const run = async () => {
            try {
                const store = useCompanyAnnualStore.getState();
                const info = await store.getYearOfAssessment(Number(annualId));
                if (mounted) {
                    setYearOfAssessment(periodScope?.yearOfAssessment || info?.yearOfAssessment || null);
                    // MULTI non-first Tab: Last Year label = prior slice YOA (amounts already chain this way)
                    const priorSlice = resolvePriorPeriodSlice(periodsFromStore, periodScope?.sliceKey);
                    const lastYoa = priorSlice
                        ? (priorSlice.yearOfAssessment
                            || computeYearOfAssessment(priorSlice.periodTo)
                            || info?.lastYearOfAssessment
                            || null)
                        : (info?.lastYearOfAssessment || null);
                    setLastYearOfAssessment(lastYoa);
                }
                const currency = await store.getPresentationCurrency(Number(annualId));
                if (mounted) setPresentationCurrency(currency || null);
            } catch {
            }
        };
        run();
        return () => {
            mounted = false;
        };
    }, [annualId, periodScope?.yearOfAssessment, periodScope?.sliceKey, periodsFromStore]);

    /**
     * Display-only: MULTI + Total view Client / Adjust# / Audited caption YOA span.
     * Multiple tabs / SINGLE / amounts / periodScope YOA unchanged; Last Year caption original.
     */
    const columnHeaderYoa = useMemo(() => {
        if (mode !== 'adjustment' || plMultiViewMode !== 'total') return yearOfAssessment;
        if (String(adjustSpanType || '').toUpperCase() !== 'MULTI') return yearOfAssessment;
        const periods = Array.isArray(periodsFromStore) ? periodsFromStore : [];
        if (periods.length < 2) return yearOfAssessment;
        return resolveAdjustmentColumnHeaderYoa({
            spanType: 'MULTI',
            periods,
            engagementYoa: adjustEngagementYoa,
            fallbackYoa: yearOfAssessment,
        });
    }, [
        mode,
        plMultiViewMode,
        adjustSpanType,
        periodsFromStore,
        adjustEngagementYoa,
        yearOfAssessment,
    ]);

    const getDisplayName = useCallback((item: AuditBuildCompanyFSTreeItems): string => {
        const amount = computeAllComputedAmounts[item.fsId] ?? 0;
        const fallback = showClientAlias
            ? ((item.aliasEn || item.name || item.nameEn || item.aliasZh || item.nameZh || (item.code as any) || '') as string)
            : ((item.name || item.nameEn || item.aliasEn || item.nameZh || item.aliasZh || (item.code as any) || '') as string);
        const english = plNameFor(item, amount)
            ?? glExcelLineName(item.code, glExcelNameByFsCode)
            ?? fallback;
        if (glExcelNameByFsCode == null) return english;
        // Client 模式：英文用客户别名；中文优先 aliasZh，map 已回退标准 nameZh
        if (showClientAlias) {
            return appendFsChineseName(
                english,
                glExcelLineName(item.code, glExcelNameZhByFsCode)
                    ?? resolveFsChineseName(item, null),
            );
        }
        return appendFsChineseName(english, resolveFsChineseName(item, glExcelNameZhByFsCode));
    }, [computeAllComputedAmounts, glExcelNameByFsCode, glExcelNameZhByFsCode, showClientAlias]);

    // 共享的 flatten 函数（FS 和 adjustment 模式都使用）
    // 返回包含 parentFsId 的结构，用于 Collapse 动画分组
    const flatten = useCallback((nodes: any[], level = 0, parentFsId: number | null = null): Array<{
        item: any;
        level: number;
        isParent: boolean;
        isSubtotal?: boolean;
        parentItem?: any;
        parentFsId: number | null;
    }> => {
        const out: Array<{
            item: any;
            level: number;
            isParent: boolean;
            isSubtotal?: boolean;
            parentItem?: any;
            parentFsId: number | null
        }> = [];
        for (const n of nodes || []) {
            const isParent = Array.isArray(n.children) && n.children.length > 0;
            const isExpanded = !!expanded[Number(n.fsId)];
            const nodeLevel = (n as any).level;
            const childrenArr = (n.children || []) as any[];

            out.push({item: n, level, isParent, parentFsId});

            if (isParent) {
                // Always include children structure for Collapse animation
                const childRows = flatten(childrenArr, level + 1, Number(n.fsId));

                // Add subtotal if needed
                if (nodeLevel === 2 && isExpanded) {
                    childRows.push({
                        item: {
                            ...n,
                            fsId: `${n.fsId}_subtotal`,
                            name: '',
                        },
                        level: level + 1,
                        isParent: false,
                        isSubtotal: true,
                        parentItem: n,
                        parentFsId: Number(n.fsId)
                    });
                }

                // Only include children if expanded (Collapse handles animation)
                if (isExpanded) {
                    out.push(...childRows);
                }
            }
        }
        return out;
    }, [expanded]);

    // 共享的 toggleNode 函数
    const toggleNode = useCallback((id: number) => {
        setExpanded(prev => ({...prev, [id]: !prev[id]}));
    }, []);

    const handleFsChangeCurrentAmount = useCallback((fsId: number, num: number) => {
        fsComputeSeedRef.current = null;
        let nextNum = num;
        // PUR 可编：不得小于公式 PUR = COS_floor − OI − CI
        if (
            cotInputModeCurrent === 'PUR'
            && plCotFsIds.purFsId != null
            && Number(fsId) === Number(plCotFsIds.purFsId)
        ) {
            nextNum = clampPlCotPurToFormulaMin(
                num,
                bsInventoriesLastYearAmount,
                -bsInventoriesCurrentAmount,
                purCosFloorRef.current ?? 0,
            );
        }
        setAmountDisplays(prev => {
            const next = {...prev, [fsId]: String(nextNum)};
            resolvedOnExternalChange?.(
                zeroPlCotFormulaSideInDataMap(next, cotInputModeCurrent, plCotFsIds),
            );
            return next;
        });
    }, [
        resolvedOnExternalChange,
        cotInputModeCurrent,
        plCotFsIds,
        bsInventoriesCurrentAmount,
        bsInventoriesLastYearAmount,
    ]);

    const handleFsChangeLastYearAmount = useCallback((fsId: number, num: number) => {
        fsComputeSeedRef.current = null;
        // WHY: 与 Current 一致——展示层保留双边；store 数据层清公式侧为 0
        setLastYearAmounts(prev => {
            const next = {...prev, [fsId]: String(num)};
            const lyMode = resolvePlCotInputMode(cotInputModeLastYear, {
                plTree: fsTree,
                bsTree: bsTreeForCot,
                plValuesMap: next,
                bsValuesMap: sBsLastYearValues || {},
                bsLastYearMap: sBsLastYearValues || {},
            });
            setPlLastYearValues(zeroPlCotFormulaSideInDataMap(next, lyMode, plCotFsIds));
            return next;
        });
    }, [
        cotInputModeLastYear, plCotFsIds, fsTree,
        bsTreeForCot, sBsLastYearValues, setPlLastYearValues,
    ]);

    const handleFsChangeYoaAmount = useCallback((yoa: string, fsId: number, num: number) => {
        fsComputeSeedRef.current = null;
        let nextNum = num;
        if (
            cotInputModeCurrent === 'PUR'
            && plCotFsIds.purFsId != null
            && Number(fsId) === Number(plCotFsIds.purFsId)
        ) {
            nextNum = clampPlCotPurToFormulaMin(
                num,
                bsInventoriesLastYearAmount,
                -bsInventoriesCurrentAmount,
                purCosFloorRef.current ?? 0,
            );
        }
        setAmountDisplaysByYoa(prev => {
            const col = {...(prev[yoa] || {}), [fsId]: String(nextNum)};
            const next = {...prev, [yoa]: col};
            const summed = sumAmountDisplayMaps(next);
            setAmountDisplays(summed);
            resolvedOnExternalChange?.(
                zeroPlCotFormulaSideInDataMap(summed, cotInputModeCurrent, plCotFsIds),
            );
            const nextData: Record<string, Record<number, string>> = {};
            for (const [y, m] of Object.entries(next)) {
                nextData[y] = zeroPlCotFormulaSideInDataMap(m, cotInputModeCurrent, plCotFsIds);
            }
            setPlValuesByYoa(nextData);
            return next;
        });
    }, [
        resolvedOnExternalChange,
        setPlValuesByYoa,
        cotInputModeCurrent,
        plCotFsIds,
        bsInventoriesCurrentAmount,
        bsInventoriesLastYearAmount,
    ]);

    // PUR 可编且 OI/CI（BS）变化：抬高 PUR 至公式下限 COS_floor − OI − CI
    useEffect(() => {
        if (mode !== 'fs' || lastYearOnly) return;
        if (cotInputModeCurrent !== 'PUR') return;
        const purId = plCotFsIds.purFsId;
        if (purId == null || !Number.isFinite(purId)) return;
        const floor = resolvePlCotPurFormulaAmount(
            bsInventoriesLastYearAmount,
            -bsInventoriesCurrentAmount,
            purCosFloorRef.current ?? 0,
        );
        const cur = parseAmountDisplay(amountDisplaysRef.current[purId] ?? '0');
        if (cur + 0.009 >= floor) return;
        handleFsChangeCurrentAmount(purId, floor);
        // 用 ref 读当前 PUR，避免 amountDisplays 引用进 deps 与 setAmountDisplays 互踢
    }, [
        mode,
        lastYearOnly,
        cotInputModeCurrent,
        plCotFsIds.purFsId,
        bsInventoriesCurrentAmount,
        bsInventoriesLastYearAmount,
        handleFsChangeCurrentAmount,
    ]);

    // Apply inline-create amounts after custom node lands in fsTree (post reload)
    useEffect(() => {
        if (mode !== 'fs') return;
        const pending = pendingCustomDraftAmountsRef.current;
        if (!pending) return;
        const findNode = (nodes: any[]): boolean => {
            for (const n of nodes || []) {
                if (Number(n?.fsId) === pending.fsId) return true;
                if (Array.isArray(n?.children) && findNode(n.children)) return true;
            }
            return false;
        };
        if (!findNode(fsTree as any[])) return;
        pendingCustomDraftAmountsRef.current = null;
        if (isFsMulti) {
            for (const [yoa, num] of Object.entries(pending.amountsByYoa || {})) {
                if (num) handleFsChangeYoaAmount(yoa, pending.fsId, num);
            }
        } else if (pending.currentAmount) {
            handleFsChangeCurrentAmount(pending.fsId, pending.currentAmount);
        }
        if (pending.lastYearAmount) {
            handleFsChangeLastYearAmount(pending.fsId, pending.lastYearAmount);
        }
    }, [
        mode, fsTree, isFsMulti,
        handleFsChangeCurrentAmount, handleFsChangeLastYearAmount, handleFsChangeYoaAmount,
    ]);

    const prepareForPersistRef = useRef<(() => Promise<void>) | null>(null);

    useImperativeHandle(ref, () => ({
        getLines: () => {
            if (mode !== 'fs') return [];
            const lines: Array<{ fsId: number; amount: number }> = [];
            const collectLeaves = (nodes: any[]) => {
                for (const it of nodes || []) {
                    const leaf = !hasChildren(it);
                    if (leaf && !isProtectedParentHeader(it)) {
                        const fsId = Number(it.fsId);
                        const amount = Object.prototype.hasOwnProperty.call(computeAllComputedAmounts, fsId)
                            ? (computeAllComputedAmounts[fsId] ?? 0)
                            : parseAmountDisplay(amountDisplaysRef.current[fsId] || '');
                        lines.push({fsId, amount});
                    }
                    if (hasChildren(it)) collectLeaves(it.children);
                }
            };
            collectLeaves(fsTree as any[]);
            return lines;
        },
        prepareForPersist: async () => {
            await prepareForPersistRef.current?.();
        },
    }), [mode, fsTree, computeAllComputedAmounts, isProtectedParentHeader]);

    const reportHeader = (
        <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={4} mb={0.5}>
            <Box sx={{minWidth: 0}}>
                {!!resolvedCompanyName && (
                    <Typography variant="h6" fontWeight={700} mb={0.5}>{resolvedCompanyName}</Typography>)}
                {periodLineText && (
                    <Typography variant="subtitle2" mb={0.25}>{periodLineText}</Typography>)}
                <Typography variant="subtitle1" fontWeight={700} mb={0.25}>Detailed income statement</Typography>
                <Typography variant="subtitle2" color="text.secondary" mb={0.25}>(For management information purpose
                    only)</Typography>
            </Box>
            <FsMaterialityHeader
                companyId={resolvedCompanyId}
                annualId={annualId}
                statement="PL"
            />
        </Stack>
    );

    // Total view Client expand — hooks must stay unconditional (HMR / Rules of Hooks).
    const clientYoaColumns = useMemo(() => {
        if (mode !== 'adjustment' || plMultiViewMode !== 'total') return [];
        if (String(adjustSpanType || '').toUpperCase() !== 'MULTI') return [];
        return listPlClientYoaColumns(periodsFromStore);
    }, [mode, plMultiViewMode, adjustSpanType, periodsFromStore]);
    const canExpandClientByYoa = mode === 'adjustment' && clientYoaColumns.length >= 2;
    const showClientByYoa = canExpandClientByYoa && clientByYoaExpanded;
    const clientColumnCount = showClientByYoa ? clientYoaColumns.length : 1;
    const clientByYoaFsId = useMemo(() => {
        if (!showClientByYoa) return {} as Record<string, Record<number, number>>;
        const bsInv = resolveBsInventoriesFromBsTree(sBSTreeNested);
        const hideCos = shouldHidePlCostOfSalesRow(bsInv, fsTree);
        return buildPerYoaPlClientByFsId(
            periodsFromStore,
            convertAuditFsItemVOForYoa,
            plTreeFromStore,
            {
                formulaTree: fsTree,
                cotInputMode: plCotInputMode,
                hideCostOfSalesRow: hideCos,
                bsLastYearAudited: bsInv.lastYearAudited,
                bsClientCurrent: bsInv.clientCurrent,
            },
        );
    }, [showClientByYoa, periodsFromStore, plTreeFromStore, fsTree, sBSTreeNested, plCotInputMode]);
    const toggleClientByYoa = canExpandClientByYoa
        ? () => setClientByYoaExpanded((v) => !v)
        : undefined;
    const clientHeaderCellSx = {
        width: 160,
        fontWeight: 700,
        ...(canExpandClientByYoa && {
            cursor: 'pointer' as const,
            '&:hover': {bgcolor: 'action.hover'},
        }),
    };
    const resolveClientAmountForYoa = useCallback((fsId: number, yoa: string): number => (
        Number(clientByYoaFsId[yoa]?.[fsId] ?? 0) || 0
    ), [clientByYoaFsId]);

    if (mode === 'adjustment') {
        const bsInventoriesForPl = useMemo(() => {
            const invNode = findNodeByCodes((sBSTreeNested || []) as any[], ['BS-CA-INV']);
            const invFsId = Number((invNode as any)?.fsId);
            const lastYearFromTree = Number((invNode as any)?.lastYearAudited ?? 0) || 0;
            const lastYearFromFs = Number.isFinite(invFsId)
                ? resolvePlLastYearLeafAmount(invFsId, sBsLastYearValues, lastYearFromTree)
                : lastYearFromTree;
            return {
                clientCurrent: Number((invNode as any)?.openingAmount ?? 0) || 0,
                auditedCurrent: Number((invNode as any)?.finalAmount ?? 0) || 0,
                lastYearAudited: lastYearFromFs,
            };
        }, [sBSTreeNested, sBsLastYearValues]);

        const hideCostOfSalesRow = useMemo(
            () => shouldHidePlCostOfSalesRow(resolveBsInventoriesFromBsTree(sBSTreeNested), fsTree),
            [sBSTreeNested, fsTree],
        );

        const plCotLeafClientState = useMemo(() => {
            const out: Record<number, number> = {};
            const visit = (nodes: any[]) => {
                for (const n of nodes || []) {
                    if (Array.isArray(n?.children) && n.children.length > 0) {
                        visit(n.children);
                        continue;
                    }
                    if (plIsProtectedParentHeader(n)) continue;
                    out[Number(n.fsId)] = Number((n as any)?.openingAmount ?? (n as any)?.amount ?? 0) || 0;
                }
            };
            visit((fsTree || []) as any[]);

            const oiNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-OI']);
            const purNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-PUR']);
            const ciNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-CI']);
            const cosNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-COS']);

            const oiId = Number((oiNode as any)?.fsId);
            const purId = Number((purNode as any)?.fsId);
            const ciId = Number((ciNode as any)?.fsId);
            const cosId = Number((cosNode as any)?.fsId);

            const rawCosOpening = cosNode && Number.isFinite(cosId) ? (out[cosId] ?? 0) : 0;
            const rawPurValue = purNode && Number.isFinite(purId) ? (out[purId] ?? 0) : 0;

            const showCosRow = applyPlCotCosPurOverrides({
                out,
                cotInputMode: plCotInputMode,
                hideCostOfSalesRow,
                bsLastYearAudited: bsInventoriesForPl.lastYearAudited,
                bsClientCurrent: bsInventoriesForPl.clientCurrent,
                oiId: Number.isFinite(oiId) ? oiId : undefined,
                purId: Number.isFinite(purId) ? purId : undefined,
                ciId: Number.isFinite(ciId) ? ciId : undefined,
                cosId: Number.isFinite(cosId) ? cosId : undefined,
                rawCosOpening,
                rawPurValue,
            });

            const rawCosClient = rawCosOpening;
            const cosAdjForHide = cosNode && Array.isArray((cosNode as any)?.adjustment)
                ? (cosNode as any).adjustment.reduce(
                    (sum: number, adj: any) => sum + (Number(adj?.adjustment) || 0),
                    0,
                )
                : 0;
            const rawCosAudited = rawCosClient + cosAdjForHide;

            return {
                leafClientValues: out,
                showCosRowInAdjustment: showCosRow,
                rawCosClient,
                rawCosAudited,
            };
        }, [fsTree, bsInventoriesForPl, hideCostOfSalesRow, plCotInputMode]);

        const leafClientValues = plCotLeafClientState.leafClientValues;
        const showCosRowInAdjustment = plCotLeafClientState.showCosRowInAdjustment;
        const rawCosClientForDisplay = plCotLeafClientState.rawCosClient;
        const rawCosAuditedForDisplay = plCotLeafClientState.rawCosAudited;
        // raw COS 双侧为 0：不展示 COS 行，公式值仅进父节点汇总
        const hideCostOfSalesRowForDisplay =
            shouldHidePlCotCosRowWhenRawZero(rawCosClientForDisplay, rawCosAuditedForDisplay)
            || !showCosRowInAdjustment;

        const leafAuditedValues = useMemo(() => {
            const out: Record<number, number> = {};
            const sumLeafAdjustments = (node: any): number => {
                if (!Array.isArray(node?.adjustment)) return 0;
                return node.adjustment.reduce((sum: number, adj: any) => sum + (Number(adj?.adjustment) || 0), 0);
            };
            const visit = (nodes: any[]) => {
                for (const n of nodes || []) {
                    if (Array.isArray(n?.children) && n.children.length > 0) {
                        visit(n.children);
                        continue;
                    }
                    if (plIsProtectedParentHeader(n)) continue;
                    const base = Number((n as any)?.openingAmount ?? (n as any)?.amount ?? 0) || 0;
                    out[Number(n.fsId)] = base + sumLeafAdjustments(n);
                }
            };
            visit((fsTree || []) as any[]);

            const oiNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-OI']);
            const purNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-PUR']);
            const ciNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-CI']);
            const cosNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-COS']);

            const oiId = Number((oiNode as any)?.fsId);
            const purId = Number((purNode as any)?.fsId);
            const ciId = Number((ciNode as any)?.fsId);
            const cosId = Number((cosNode as any)?.fsId);

            const rawCosOpening = cosNode && Number.isFinite(cosId)
                ? (Number((cosNode as any)?.openingAmount ?? (cosNode as any)?.amount ?? 0) || 0)
                : 0;
            const rawPurOpening = purNode && Number.isFinite(purId)
                ? (Number((purNode as any)?.openingAmount ?? (purNode as any)?.amount ?? 0) || 0)
                : 0;
            const purAdj = purNode ? sumLeafAdjustments(purNode) : 0;
            const cosAdj = cosNode ? sumLeafAdjustments(cosNode) : 0;
            const ciAdj = ciNode ? sumLeafAdjustments(ciNode) : 0;
            const oiAdj = oiNode ? sumLeafAdjustments(oiNode) : 0;

            applyPlCotCosPurOverrides({
                out,
                cotInputMode: plCotInputMode,
                hideCostOfSalesRow,
                bsLastYearAudited: bsInventoriesForPl.lastYearAudited,
                bsClientCurrent: bsInventoriesForPl.clientCurrent,
                oiId: Number.isFinite(oiId) ? oiId : undefined,
                purId: Number.isFinite(purId) ? purId : undefined,
                ciId: Number.isFinite(ciId) ? ciId : undefined,
                cosId: Number.isFinite(cosId) ? cosId : undefined,
                rawCosOpening,
                rawPurValue: rawPurOpening,
                purAdjustments: purAdj,
                ciAdjustments: ciAdj,
                cosAdjustments: cosAdj,
            });

            // 仅 PL-COT-PUR Audited（不改 OI/CI/COS 等其他叶子）：
            // - COS 行可见：公式不含 PUR adjustment → 补正 Client + Adjust
            // - COS 行隐藏：公式含伴生 COS adj 再加 purAdj 会重复 → 同样补正 Client + Adjust
            if (
                plCotInputMode === 'COS'
                && purNode
                && Number.isFinite(purId)
                && Math.abs(purAdj) >= 0.01
            ) {
                out[purId] = (leafClientValues[purId] ?? rawPurOpening) + purAdj;
            }

            // PUR adjustment 全 0：Audited 仅取节点自身 opening，忽略 COS/BS 公式反推
            applyPlCotPurSelfAuditedIfAllAdjustmentsZero(out, purNode);

            // 仅 PL-COT-COS Audited：公式行汇总 OI + PUR + CI（含 PUR 补正后的 audited）
            if (
                cosNode
                && Number.isFinite(cosId)
                && shouldUsePlCotFormulaCosAudited({
                    cotInputMode: plCotInputMode,
                    showCosRowInAdjustment,
                    hideCostOfSalesRow,
                    purAdjustments: purAdj,
                    ciAdjustments: ciAdj,
                    oiAdjustments: oiAdj,
                    openingInv: bsInventoriesForPl.lastYearAudited,
                    bsClientCurrent: bsInventoriesForPl.clientCurrent,
                })
            ) {
                out[cosId] = sumPlCotComponentAuditedValues(out, oiId, purId, ciId);
            }

            return out;
        }, [fsTree, bsInventoriesForPl, hideCostOfSalesRow, plCotInputMode, leafClientValues, showCosRowInAdjustment]);

        const computedClientMap = useMemo(() => {
            const leafGetter = (node: AuditBuildCompanyFSTreeItems): number => {
                if (plIsProtectedParentHeader(node)) return 0;
                return Number(leafClientValues[Number(node.fsId)] ?? 0) || 0;
            };
            const computed = plComputeComputedAmounts((fsTree || []) as any, leafGetter);
            const purNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-PUR']);
            if (purNode) computed[Number((purNode as any).fsId)] = Number(leafClientValues[Number((purNode as any).fsId)] ?? 0) || 0;

            const sumNode = (node: any): number => {
                if (!node) return 0;
                if (plIsProtectedParentHeader(node)) return 0;
                if (!hasChildren(node)) return Number(leafClientValues[Number(node.fsId)] ?? 0) || 0;
                let sum = 0;
                for (const ch of (node.children || [])) sum += sumNode(ch);
                return sum;
            };

            const COST_TURNOVER = findNodeByCodes(fsTree || [], FS_CODE.PL.COST_OF_TURNOVER);
            const COST_SALES = findNodeByCodes(fsTree || [], ['PL-COT-COS']);
            const OPEN_INV = findNodeByCodes(fsTree || [], ['PL-COT-OI']);
            const CLOSE_INV = findNodeByCodes(fsTree || [], ['PL-COT-CI']);
            const ROYALITY = findNodeByCodes(fsTree || [], ['PL-COT-ROY']);
            const SUBCONTRACT = findNodeByCodes(fsTree || [], ['PL-COT-SUB']);
            const OTHER_COT = findNodeByCodes(fsTree || [], ['PL-COT-OCT']);

            if (COST_TURNOVER) {
                let costSum = 0;
                const customCotSum = ((COST_TURNOVER as any).children || [])
                    .filter((ch: any) => isPlCustomFsNode(ch))
                    .reduce((s: number, ch: any) => s + sumNode(ch), 0);
                if (COST_SALES || ROYALITY || SUBCONTRACT || OTHER_COT || purNode || customCotSum) {
                    const purAmt = purNode ? sumNode(purNode) : 0;
                    // Client：保持原口径 — raw COS!=0 用本值；==0 用 OI+PUR+CI（不走 Audited 伴生修复）
                    const primaryCost = resolvePlCotCosAmountForParentRollup({
                        rawCos: rawCosClientForDisplay,
                        openingInv: sumNode(OPEN_INV),
                        purchases: purAmt,
                        closingInv: sumNode(CLOSE_INV),
                    });
                    // Include custom Level-3 under PL-COT (same as plComputeComputedAmounts)
                    costSum = primaryCost
                        + sumNode(ROYALITY) + sumNode(SUBCONTRACT) + sumNode(OTHER_COT)
                        + customCotSum;
                } else {
                    costSum = sumNode(COST_TURNOVER);
                }
                computed[Number((COST_TURNOVER as any).fsId)] = costSum;
            }
            // WHY: plComputeComputedAmounts 按 COS+ROY…（排除 PUR）写 PL-GP；覆盖 PL-COT 后必须重算 GP→PAT
            recomputePlDerivedFormulaNodes((fsTree || []) as any, computed, leafGetter);
            return computed;
        }, [fsTree, leafClientValues, rawCosClientForDisplay]);

        const computedAuditedMap = useMemo(() => {
            const leafGetter = (node: AuditBuildCompanyFSTreeItems): number => {
                if (plIsProtectedParentHeader(node)) return 0;
                return resolvePlLeafAuditedValue(node, leafClientValues, leafAuditedValues);
            };
            const computed = plComputeComputedAmounts((fsTree || []) as any, leafGetter);
            const purNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-PUR']);
            const cosNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-COS']);
            if (purNode) computed[Number((purNode as any).fsId)] = Number(leafAuditedValues[Number((purNode as any).fsId)] ?? 0) || 0;
            if (cosNode) computed[Number((cosNode as any).fsId)] = Number(leafAuditedValues[Number((cosNode as any).fsId)] ?? 0) || 0;

            const sumNode = (node: any): number => {
                if (!node) return 0;
                if (plIsProtectedParentHeader(node)) return 0;
                if (!hasChildren(node)) return Number(leafAuditedValues[Number(node.fsId)] ?? 0) || 0;
                let sum = 0;
                for (const ch of (node.children || [])) sum += sumNode(ch);
                return sum;
            };

            const COST_TURNOVER = findNodeByCodes(fsTree || [], FS_CODE.PL.COST_OF_TURNOVER);
            const COST_SALES = findNodeByCodes(fsTree || [], ['PL-COT-COS']);
            const OPEN_INV = findNodeByCodes(fsTree || [], ['PL-COT-OI']);
            const CLOSE_INV = findNodeByCodes(fsTree || [], ['PL-COT-CI']);
            const ROYALITY = findNodeByCodes(fsTree || [], ['PL-COT-ROY']);
            const SUBCONTRACT = findNodeByCodes(fsTree || [], ['PL-COT-SUB']);
            const OTHER_COT = findNodeByCodes(fsTree || [], ['PL-COT-OCT']);

            if (COST_TURNOVER) {
                let costSum = 0;
                const customCotSum = ((COST_TURNOVER as any).children || [])
                    .filter((ch: any) => isPlCustomFsNode(ch))
                    .reduce((s: number, ch: any) => s + sumNode(ch), 0);
                if (COST_SALES || ROYALITY || SUBCONTRACT || OTHER_COT || purNode || customCotSum) {
                    const purAmt = purNode ? sumNode(purNode) : 0;
                    const oiAmt = sumNode(OPEN_INV);
                    const ciAmt = sumNode(CLOSE_INV);
                    const cosLeafAudited = COST_SALES ? sumNode(COST_SALES) : 0;
                    const purOpening = purNode
                        ? (Number((purNode as any)?.openingAmount ?? (purNode as any)?.amount ?? 0) || 0)
                        : 0;
                    // Audited only：PUR adj（伴生 COS）走 OI+PUR+CI；COS adj 用叶子 Audited
                    const cosAuditedRaw = Math.abs(cosLeafAudited) >= 0.01
                        ? cosLeafAudited
                        : rawCosAuditedForDisplay;
                    const primaryCost = resolvePlCotCosAmountForParentRollup({
                        rawCos: resolveRawCosForPlCotParentAuditedRollup({
                            cosOpening: rawCosClientForDisplay,
                            purOpening,
                            cosAuditedRaw,
                            purAudited: purAmt,
                        }),
                        openingInv: oiAmt,
                        purchases: purAmt,
                        closingInv: ciAmt,
                    });
                    costSum = primaryCost
                        + sumNode(ROYALITY) + sumNode(SUBCONTRACT) + sumNode(OTHER_COT)
                        + customCotSum;
                } else {
                    costSum = sumNode(COST_TURNOVER);
                }
                computed[Number((COST_TURNOVER as any).fsId)] = costSum;
            }
            // WHY: 同上 — Audited 覆盖 PL-COT 后重算 PL-GP / GPOI / PBT / PAT
            recomputePlDerivedFormulaNodes((fsTree || []) as any, computed, leafGetter);
            return computed;
        }, [
            fsTree,
            leafAuditedValues,
            leafClientValues,
            rawCosAuditedForDisplay,
            rawCosClientForDisplay,
        ]);

        const turnoverAuditedForThreshold = useMemo(() => {
            const turnoverNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TURNOVER);
            if (!turnoverNode) return 0;
            return computedAuditedMap[Number((turnoverNode as any).fsId)] ?? 0;
        }, [fsTree, computedAuditedMap]);

        // 仅用于 Ratio against turnover 列；不参与其它列/可见性/rounding 逻辑
        // hideLastYearColumns 时不展示该列，此处直接返回空集避免无谓遍历
        const ratioAgainstTurnoverFsIds = useMemo(() => {
            const ids = new Set<number>();
            if (hideLastYearColumns) return ids;
            const collectLeaves = (node: any) => {
                const kids: any[] = Array.isArray(node?.children) ? node.children : [];
                if (kids.length === 0) {
                    const fsId = Number(node?.fsId);
                    if (Number.isFinite(fsId)) ids.add(fsId);
                    return;
                }
                for (const ch of kids) collectLeaves(ch);
            };
            for (const codes of [FS_CODE.PL.OTHER_INCOME, FS_CODE.PL.ADMIN_EXPENSES, FS_CODE.PL.FINANCE_COSTS]) {
                const parent = findNodeByCodes((fsTree || []) as any[], codes);
                if (!parent) continue;
                for (const ch of ((parent as any).children || [])) collectLeaves(ch);
            }
            return ids;
        }, [fsTree, hideLastYearColumns]);

        // Compute Last Year Audited values using the same approach as Client/Audited columns
        // Create a custom leaf getter that reads lastYearAudited field
        // Phase 6-B MULTI ≥2 tabs: first = FS lastYear; later = prior Tab Audited
        const plMultiChainMeta = useMemo(() => {
            if (mode !== 'adjustment' || !periodScope?.sliceKey) return null;
            if (!Array.isArray(periodsFromStore) || periodsFromStore.length < 2) return null;
            const idx = periodsFromStore.findIndex(
                (p) =>
                    p.key === periodScope.sliceKey
                    || (periodScope.yearOfAssessment
                        && p.yearOfAssessment
                        && String(p.yearOfAssessment).trim() === String(periodScope.yearOfAssessment).trim()),
            );
            if (idx < 0) return null;
            return {idx, periods: periodsFromStore};
        }, [mode, periodScope?.sliceKey, periodScope?.yearOfAssessment, periodsFromStore]);

        const computedLastYearAuditedMap = useMemo(() => {
            const oiNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-OI']);
            const purNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-PUR']);
            const ciNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-CI']);
            const cosNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-COS']);

            const oiFsId = Number((oiNode as any)?.fsId);
            const purFsId = Number((purNode as any)?.fsId);
            const ciFsId = Number((ciNode as any)?.fsId);
            const cosFsId = Number((cosNode as any)?.fsId);

            // Non-first MULTI tab: Last Year := prior Tab Audited (same fsId)
            if (plMultiChainMeta && plMultiChainMeta.idx > 0) {
                const prior = plMultiChainMeta.periods[plMultiChainMeta.idx - 1];
                const priorKey = String(prior?.key || '');
                let priorAudited = priorKey ? plAuditedBySlice[priorKey] : undefined;
                if (!priorAudited || Object.keys(priorAudited).length === 0) {
                    const priorLine = prior?.plLine;
                    priorAudited = bootstrapAuditedMapFromPlVos(
                        Array.isArray(priorLine) && priorLine.length > 0 ? priorLine : null,
                    );
                    if (!priorAudited || Object.keys(priorAudited).length === 0) {
                        // Fallback: top-level tree + prior YOA opening overlay
                        const yoa = prior?.yearOfAssessment
                            ? String(prior.yearOfAssessment).trim()
                            : '';
                        const source = (plTreeFromStore || []).map((vo: any) =>
                            yoa ? convertAuditFsItemVOForYoa(vo, yoa) : convertAuditFsItemVOToFsSimpleData(vo),
                        );
                        priorAudited = bootstrapAuditedMapFromPlVos(source);
                    }
                }

                // Prior slice may have FS-cleared formula side (COS=0 / PUR=0); re-apply COT formula
                const lyOverrides: Record<number, number> = {...(priorAudited || {})};
                const rawCosLy = Number.isFinite(cosFsId) ? (Number(lyOverrides[cosFsId]) || 0) : 0;
                const rawPurLy = Number.isFinite(purFsId) ? (Number(lyOverrides[purFsId]) || 0) : 0;
                const showCosLy = applyPlCotCosPurOverrides({
                    out: lyOverrides,
                    cotInputMode: plCotInputMode,
                    hideCostOfSalesRow,
                    bsLastYearAudited: bsInventoriesForPl.lastYearAudited,
                    bsClientCurrent: bsInventoriesForPl.clientCurrent,
                    oiId: Number.isFinite(oiFsId) ? oiFsId : undefined,
                    purId: Number.isFinite(purFsId) ? purFsId : undefined,
                    ciId: Number.isFinite(ciFsId) ? ciFsId : undefined,
                    cosId: Number.isFinite(cosFsId) ? cosFsId : undefined,
                    rawCosOpening: rawCosLy,
                    rawPurValue: rawPurLy,
                });

                const lastYearLeafGetter = (node: AuditBuildCompanyFSTreeItems): number => {
                    if (plIsProtectedParentHeader(node)) return 0;
                    const fsId = Number((node as any)?.fsId);
                    if (Number.isFinite(fsId) && Object.prototype.hasOwnProperty.call(lyOverrides, fsId)) {
                        return Number(lyOverrides[fsId]) || 0;
                    }
                    return 0;
                };

                const computed = plComputeComputedAmounts((fsTree || []) as any, lastYearLeafGetter);
                for (const [fsId, val] of Object.entries(lyOverrides)) {
                    computed[Number(fsId)] = Number(val) || 0;
                }

                const sumLastYearNode = (node: any): number => {
                    if (!node) return 0;
                    if (plIsProtectedParentHeader(node)) return 0;
                    if (!hasChildren(node)) return lastYearLeafGetter(node);
                    let sum = 0;
                    for (const ch of (node.children || [])) sum += sumLastYearNode(ch);
                    return sum;
                };

                const COST_TURNOVER = findNodeByCodes(fsTree || [], FS_CODE.PL.COST_OF_TURNOVER);
                const COST_SALES_LY = findNodeByCodes((fsTree || []) as any[], ['PL-COT-COS']);
                const ROYALITY_LY = findNodeByCodes((fsTree || []) as any[], ['PL-COT-ROY']);
                const SUBCONTRACT_LY = findNodeByCodes((fsTree || []) as any[], ['PL-COT-SUB']);
                const OTHER_COT_LY = findNodeByCodes((fsTree || []) as any[], ['PL-COT-OCT']);

                if (COST_TURNOVER) {
                    let costSum = 0;
                    const customCotSum = ((COST_TURNOVER as any).children || [])
                        .filter((ch: any) => isPlCustomFsNode(ch))
                        .reduce((s: number, ch: any) => s + sumLastYearNode(ch), 0);
                    if (COST_SALES_LY || ROYALITY_LY || SUBCONTRACT_LY || OTHER_COT_LY || purNode || customCotSum) {
                        const cosAmt = sumLastYearNode(COST_SALES_LY);
                        const purAmt = purNode ? sumLastYearNode(purNode) : 0;
                        const primaryCost = resolvePlCotPrimaryCostAmount(showCosLy, cosAmt, purAmt);
                        costSum = primaryCost
                            + sumLastYearNode(ROYALITY_LY) + sumLastYearNode(SUBCONTRACT_LY)
                            + sumLastYearNode(OTHER_COT_LY) + customCotSum;
                    } else {
                        costSum = sumLastYearNode(COST_TURNOVER);
                    }
                    computed[Number((COST_TURNOVER as any).fsId)] = costSum;
                }

                recomputePlDerivedFormulaNodes((fsTree || []) as any, computed, lastYearLeafGetter);
                return computed;
            }

            const openingInvLastYear = bsInventoriesForPl.lastYearAudited;
            const closingInvLastYear = -bsInventoriesForPl.lastYearAudited;
            const costOfSalesLastYear = resolvePlLastYearLeafAmount(
                Number.isFinite(cosFsId) ? cosFsId : undefined,
                sPLLastYearValues,
                Number((cosNode as any)?.lastYearAudited ?? 0) || 0,
            );
            const purchasesLastYearRaw = resolvePlLastYearLeafAmount(
                Number.isFinite(purFsId) ? purFsId : undefined,
                sPLLastYearValues,
                Number((purNode as any)?.lastYearAudited ?? 0) || 0,
            );
            const cotInputModeLastYear = inferPlCotInputMode({
                plTree: fsTree,
                bsTree: bsTreeForCot,
                plValuesMap: sPLLastYearValues || {},
                bsValuesMap: sBsLastYearValues || {},
                bsLastYearMap: sBsLastYearValues || {},
            });

            const lyOverrides: Record<number, number> = {};

            if (Number.isFinite(oiFsId)) lyOverrides[oiFsId] = openingInvLastYear;
            if (Number.isFinite(ciFsId)) lyOverrides[ciFsId] = closingInvLastYear;

            const showCosLy = applyPlCotCosPurOverrides({
                out: lyOverrides,
                cotInputMode: cotInputModeLastYear,
                hideCostOfSalesRow,
                bsLastYearAudited: bsInventoriesForPl.lastYearAudited,
                bsClientCurrent: bsInventoriesForPl.lastYearAudited,
                oiId: Number.isFinite(oiFsId) ? oiFsId : undefined,
                purId: Number.isFinite(purFsId) ? purFsId : undefined,
                ciId: Number.isFinite(ciFsId) ? ciFsId : undefined,
                cosId: Number.isFinite(cosFsId) ? cosFsId : undefined,
                rawCosOpening: costOfSalesLastYear,
                rawPurValue: purchasesLastYearRaw,
            });

            // First MULTI tab (or SINGLE): prefer FS lastYear input when present
            const preferFsLastYear = !!plMultiChainMeta && plMultiChainMeta.idx === 0;

            const lastYearLeafGetter = (node: AuditBuildCompanyFSTreeItems): number => {
                if (plIsProtectedParentHeader(node)) return 0;
                const fsId = Number((node as any)?.fsId);
                if (Object.prototype.hasOwnProperty.call(lyOverrides, fsId)) {
                    return lyOverrides[fsId];
                }
                const fromTree = Number((node as any)?.lastYearAudited ?? 0) || 0;
                if (preferFsLastYear) {
                    return resolvePlLastYearLeafAmount(
                        Number.isFinite(fsId) ? fsId : undefined,
                        sPLLastYearValues,
                        fromTree,
                    );
                }
                return fromTree;
            };

            const computed = plComputeComputedAmounts((fsTree || []) as any, lastYearLeafGetter);

            for (const [fsId, val] of Object.entries(lyOverrides)) {
                computed[Number(fsId)] = val;
            }

            const sumLastYearNode = (node: any): number => {
                if (!node) return 0;
                if (plIsProtectedParentHeader(node)) return 0;
                if (!hasChildren(node)) return lastYearLeafGetter(node);
                let sum = 0;
                for (const ch of (node.children || [])) sum += sumLastYearNode(ch);
                return sum;
            };

            const COST_TURNOVER = findNodeByCodes(fsTree || [], FS_CODE.PL.COST_OF_TURNOVER);
            const COST_SALES_LY = findNodeByCodes((fsTree || []) as any[], ['PL-COT-COS']);
            const ROYALITY_LY = findNodeByCodes((fsTree || []) as any[], ['PL-COT-ROY']);
            const SUBCONTRACT_LY = findNodeByCodes((fsTree || []) as any[], ['PL-COT-SUB']);
            const OTHER_COT_LY = findNodeByCodes((fsTree || []) as any[], ['PL-COT-OCT']);

            if (COST_TURNOVER) {
                let costSum = 0;
                const customCotSum = ((COST_TURNOVER as any).children || [])
                    .filter((ch: any) => isPlCustomFsNode(ch))
                    .reduce((s: number, ch: any) => s + sumLastYearNode(ch), 0);
                if (COST_SALES_LY || ROYALITY_LY || SUBCONTRACT_LY || OTHER_COT_LY || purNode || customCotSum) {
                    const cosAmt = sumLastYearNode(COST_SALES_LY);
                    const purAmt = purNode ? sumLastYearNode(purNode) : 0;
                    const primaryCost = resolvePlCotPrimaryCostAmount(showCosLy, cosAmt, purAmt);
                    costSum = primaryCost
                        + sumLastYearNode(ROYALITY_LY) + sumLastYearNode(SUBCONTRACT_LY)
                        + sumLastYearNode(OTHER_COT_LY) + customCotSum;
                } else {
                    costSum = sumLastYearNode(COST_TURNOVER);
                }
                computed[Number((COST_TURNOVER as any).fsId)] = costSum;
            }

            // PL-GP 等衍生节点：在 PL-COT Last Year 汇总完成后按公式重算（Turnover - CostOfTurnover ...）
            recomputePlDerivedFormulaNodes((fsTree || []) as any, computed, lastYearLeafGetter);

            return computed;
        }, [
            fsTree,
            bsInventoriesForPl.lastYearAudited,
            bsInventoriesForPl.clientCurrent,
            hideCostOfSalesRow,
            plCotInputMode,
            sPLLastYearValues,
            sBsLastYearValues,
            plMultiChainMeta,
            plAuditedBySlice,
            plTreeFromStore,
            mode,
        ]);

        // Phase 6-B: publish this Tab's Audited (parents + leaves) for later Tabs' Last Year chain
        useEffect(() => {
            if (!plMultiChainMeta || !periodScope?.sliceKey) return;
            // plComputeComputedAmounts 只写父/派生；叶子在 leafAuditedValues。合并后下游才能渲染明细行。
            const merged: Record<number, number> = {
                ...leafAuditedValues,
                ...computedAuditedMap,
            };
            setPlSliceAudited(periodScope.sliceKey, merged);
        }, [
            plMultiChainMeta,
            periodScope?.sliceKey,
            computedAuditedMap,
            leafAuditedValues,
            setPlSliceAudited,
        ]);

        const isAuditedZero = useCallback((n: number): boolean => {
            const v = Number(n) || 0;
            return Math.abs(v) < 0.01;
        }, []);

        const getAuditedValue = useCallback((node: any): number => {
            const isDerived = isCode(
                node as any,
                ...FS_CODE.PL.COST_OF_TURNOVER,
                ...FS_CODE.PL.GROSS,
                ...FS_CODE.PL.GROSS_AND_OTHER,
                ...FS_CODE.PL.BEFORE_TAX,
                ...FS_CODE.PL.AFTER_TAX,
            );
            const isLeaf = !hasChildren(node) && !plIsProtectedParentHeader(node) && !isDerived;
            const fsIdNum = Number(node?.fsId);

            let clientVal = isLeaf
                ? (leafClientValues[fsIdNum] ?? 0)
                : plResolveAdjustmentValue(node, 'client', computedClientMap);

            // adjustment 模式：叶子 Audited = Client + Adjust Total（PL-COT 除外，见 resolvePlLeafAuditedValue）
            let auditedVal = isLeaf
                ? resolvePlLeafAuditedValue(node, leafClientValues, leafAuditedValues)
                : plResolveAdjustmentValue(node, 'audited', computedAuditedMap);

            if (fsOpeningEditMode) {
                const inputAdj = fsOpeningAmounts[fsIdNum] || 0;
                auditedVal = clientVal + inputAdj + (auditedVal - clientVal);
            }

            return auditedVal;
        }, [computedAuditedMap, computedClientMap, fsOpeningEditMode, fsOpeningAmounts, leafAuditedValues, leafClientValues]);

        const isAutoAdjEntry = useCallback((adj: any): boolean => {
            return adj?.isAutoAdj === true || adj?.adjustmentMode === 4;
        }, []);

        // AFTER_TAX for bridge to BS
        const afterTaxClient = useMemo(() => {
            const AFTER_TAX = findNodeByCodes(fsTree || [], FS_CODE.PL.AFTER_TAX);
            return AFTER_TAX ? (computedClientMap[Number(AFTER_TAX.fsId)] ?? 0) : 0;
        }, [fsTree, computedClientMap]);
        const afterTaxAudited = useMemo(() => {
            const AFTER_TAX = findNodeByCodes(fsTree || [], FS_CODE.PL.AFTER_TAX);
            return AFTER_TAX ? (computedAuditedMap[Number(AFTER_TAX.fsId)] ?? 0) : 0;
        }, [fsTree, computedAuditedMap]);
        const afterTaxLastYear = useMemo(() => {
            const AFTER_TAX = findNodeByCodes(fsTree || [], FS_CODE.PL.AFTER_TAX);
            return AFTER_TAX ? (computedLastYearAuditedMap[Number(AFTER_TAX.fsId)] ?? 0) : 0;
        }, [fsTree, computedLastYearAuditedMap]);

        // Persist into store so BalanceSheet (adjustment) can reference all values for the linking BS node.
        // SINGLE: unchanged — publish this instance's AFTER_TAX only.
        useEffect(() => {
            if (plMultiChainMeta && periodScope?.sliceKey) return;
            try {
                setPLComputed({
                    clientAfterTax: afterTaxClient,
                    afterTax: afterTaxAudited,
                    afterTaxLastYear: afterTaxLastYear
                });
            } catch {
            }
        }, [afterTaxClient, afterTaxAudited, afterTaxLastYear, plMultiChainMeta, periodScope?.sliceKey]);

        // MULTI Period Tabs only: each tab publishes AFTER_TAX; engagement Σ → setPLComputed
        // (avoid last-writer-wins when all tabs stay mounted). No-op in SINGLE.
        useEffect(() => {
            if (!plMultiChainMeta || !periodScope?.sliceKey) return;
            try {
                const sliceKey = periodScope.sliceKey;
                const prev = usePlMultiPeriodChainStore.getState().afterTaxBySlice[sliceKey];
                setPlSliceAfterTax(sliceKey, {
                    client: afterTaxClient,
                    audited: afterTaxAudited,
                    lastYear: afterTaxLastYear,
                    roundedAudited: prev?.roundedAudited,
                });
                const orderedSlices = plMultiChainMeta.periods
                    .map((p) => ({
                        key: String(p?.key || '').trim(),
                        yearOfAssessment: p?.yearOfAssessment,
                    }))
                    .filter((p) => p.key);
                const agg = sumPlAfterTaxBridgeAcrossSlices(
                    usePlMultiPeriodChainStore.getState().afterTaxBySlice,
                    orderedSlices,
                );
                setPLComputed({
                    clientAfterTax: agg.client,
                    afterTax: agg.audited,
                    afterTaxLastYear: agg.lastYear,
                });
            } catch {
            }
        }, [
            afterTaxClient,
            afterTaxAudited,
            afterTaxLastYear,
            plMultiChainMeta,
            periodScope?.sliceKey,
            setPlSliceAfterTax,
            setPLComputed,
        ]);

        // Persist computed Purchases (PL-COT-PUR) values into fsAdjustStore
        // so PurchasesWp can use them as fallback when backend data is empty.
        // SINGLE: write CODE only (original). MULTI (≥2 Period Tabs): write CODE@YOA.
        useEffect(() => {
            const purNode = findNodeByCodes((fsTree || []) as any[], ['PL-COT-PUR']);
            if (!purNode) return;
            const purId = Number((purNode as any)?.fsId);
            if (!Number.isFinite(purId)) return;
            const data = {
                auditedValue: leafAuditedValues[purId] ?? 0,
                clientValue: leafClientValues[purId] ?? 0,
                lastYearAudited: computedLastYearAuditedMap[purId] ?? 0,
                statement: 'PL' as const,
            };
            try {
                if (plMultiChainMeta && periodScope?.yearOfAssessment) {
                    useFsAdjustStore.getState().setComputedPerData(
                        'PL-COT-PUR',
                        data,
                        String(periodScope.yearOfAssessment).trim(),
                    );
                } else {
                    // Original SINGLE / non-multi signature — no YOA arg
                    useFsAdjustStore.getState().setComputedPerData('PL-COT-PUR', data);
                }
            } catch {
            }
        }, [
            fsTree,
            leafClientValues,
            leafAuditedValues,
            computedLastYearAuditedMap,
            plMultiChainMeta,
            periodScope?.yearOfAssessment,
        ]);

        // MULTI only (#20): publish PL-TAX@YOA for TaxWp expenses mirror.
        // SINGLE keeps engagement perData from syncFromQueryData — do not overwrite.
        useEffect(() => {
            if (!plMultiChainMeta || !periodScope?.yearOfAssessment) return;
            const taxNode = findNodeByCodes((fsTree || []) as any[], ['PL-TAX']);
            if (!taxNode) return;
            const taxId = Number((taxNode as any)?.fsId);
            if (!Number.isFinite(taxId)) return;
            try {
                useFsAdjustStore.getState().setComputedPerData(
                    'PL-TAX',
                    {
                        auditedValue: leafAuditedValues[taxId] ?? 0,
                        clientValue: leafClientValues[taxId] ?? 0,
                        lastYearAudited: computedLastYearAuditedMap[taxId] ?? 0,
                        statement: 'PL' as const,
                    },
                    String(periodScope.yearOfAssessment).trim(),
                );
            } catch {
            }
        }, [
            fsTree,
            leafClientValues,
            leafAuditedValues,
            computedLastYearAuditedMap,
            plMultiChainMeta,
            periodScope?.yearOfAssessment,
        ]);

        const subtreeIdsMap = useMemo(() => {
            const map = new Map<number, Set<number>>();
            const collect = (node: any): Set<number> => {
                const ownId = Number(node?.fsId);
                const childSets: Set<number>[] = (node?.children || []).map((c: any) => collect(c));
                const agg = new Set<number>([ownId]);
                childSets.forEach((s) => s.forEach((v) => agg.add(v)));
                map.set(ownId, agg);
                return agg;
            };
            for (const root of (fsTree || []) as any[]) collect(root);
            return map;
        }, [fsTree]);

        // computedClientMap and computedAuditedMap already defined above
        // Grouped extra columns removed (draft, review, history)

        // Collect Adjust# columns (adjustment mode only).
        // MULTI: header catalog drives single vs merge column sets (gap doc §4.2).
        const multiAdjCatalog = useMemo(() => {
            if (mode !== 'adjustment') return null;
            if (adjustSpanType !== 'MULTI') return null;
            if (!Array.isArray(periodsFromStore) || periodsFromStore.length < 2) return null;
            return collectMultiAdjColumnCatalog({
                bsLine: bsTreeFromStore,
                plLine: plTreeFromStore,
                periods: periodsFromStore,
            });
        }, [mode, adjustSpanType, periodsFromStore, bsTreeFromStore, plTreeFromStore]);

        const adjustmentRefNos = useMemo(() => {
            if (mode !== 'adjustment') return [];

            if (multiAdjCatalog) {
                if (plMultiViewMode === 'total') {
                    return manualRefNosFromCatalog(multiAdjCatalog, plMergeColumnFilter);
                }
                const tabYoa = periodScope?.yearOfAssessment
                    ? String(periodScope.yearOfAssessment).trim()
                    : '';
                if (tabYoa) {
                    return manualRefNosFromCatalog(
                        multiAdjCatalog,
                        (col) => plSingleColumnFilter(col, tabYoa),
                    );
                }
            }

            const refNos = new Set<string>();
            const collectRefNos = (nodes: any[]) => {
                for (const node of nodes || []) {
                    if (Array.isArray(node.adjustment)) {
                        for (const adj of node.adjustment) {
                            // Exclude auto adjustment refNo (shown as separate columns)
                            if (adj?.refNo && !isAutoAdjEntry(adj)) {
                                refNos.add(String(adj.refNo));
                            }
                        }
                    }
                    if (Array.isArray(node.children)) {
                        collectRefNos(node.children);
                    }
                }
            };
            collectRefNos(fsTree as any[]);
            return Array.from(refNos).sort((a, b) => {
                const numA = parseFloat(a) || 0;
                const numB = parseFloat(b) || 0;
                return numA - numB;
            });
        }, [
            mode,
            fsTree,
            isAutoAdjEntry,
            multiAdjCatalog,
            plMultiViewMode,
            periodScope?.yearOfAssessment,
        ]);

        // Auto adj columns: document order; MULTI uses shared catalog filters
        const AUTO_ADJ_TYPE_ORDER = ['DIR_DTD', 'LOAN_BANK', 'LOAN_VEHICLE', 'GL_DISPOSAL', 'GL_EXCHANGE', 'TAX', 'TAX_EXCHANGE'];
        const autoAdjRefNos = useMemo(() => {
            if (mode !== 'adjustment') return [];

            if (multiAdjCatalog) {
                if (plMultiViewMode === 'total') {
                    return autoAdjItemsFromCatalog(multiAdjCatalog, plMergeColumnFilter);
                }
                const tabYoa = periodScope?.yearOfAssessment
                    ? String(periodScope.yearOfAssessment).trim()
                    : '';
                if (tabYoa) {
                    return autoAdjItemsFromCatalog(
                        multiAdjCatalog,
                        (col) => plSingleColumnFilter(col, tabYoa),
                    );
                }
            }

            const typeRefMap = new Map<string, string>(); // autoAdjType → refNo (first seen)
            const refNoOnly = new Map<string, string>(); // refNo → refNo (no autoAdjType)
            const collect = (nodes: any[]) => {
                for (const node of nodes || []) {
                    if (Array.isArray(node.adjustment)) {
                        for (const adj of node.adjustment) {
                            if (!isAutoAdjEntry(adj) || !adj?.refNo) continue;
                            const refNo = String(adj.refNo);
                            if (adj.autoAdjType && !typeRefMap.has(adj.autoAdjType)) {
                                typeRefMap.set(adj.autoAdjType, refNo);
                            } else if (!adj.autoAdjType && !refNoOnly.has(refNo)) {
                                refNoOnly.set(refNo, refNo);
                            }
                        }
                    }
                    if (Array.isArray(node.children)) collect(node.children);
                }
            };
            collect(fsTree as any[]);
            const ordered = AUTO_ADJ_TYPE_ORDER
                .filter(t => typeRefMap.has(t))
                .map(t => ({type: t, refNo: typeRefMap.get(t)!}));
            for (const refNo of refNoOnly.values()) {
                if (!ordered.some(item => item.refNo === refNo)) {
                    ordered.push({type: refNo, refNo});
                }
            }
            return ordered;
        }, [
            mode,
            fsTree,
            isAutoAdjEntry,
            multiAdjCatalog,
            plMultiViewMode,
            periodScope?.yearOfAssessment,
        ]);
        const hasAutoAdjustment = autoAdjRefNos.length > 0;
        // Adjust Total 折叠门槛：
        // - 原逻辑：手动 Adjust#N > 1 可合并
        // - 扩展：至少 1 条手动，且手动 + Adjust#N(Auto) 合计 ≥ 2 也可合并（不影响纯 Auto / 单手动场景）
        // - 再扩展：纯 Auto 且列数 ≥ 5 也可合并（与 FsAdjustment 初始化规则对齐）
        const adjustColumnCount = adjustmentRefNos.length + autoAdjRefNos.length;
        const canCollapseAdjustColumns =
            adjustColumnCount >= 5
            || adjustmentRefNos.length > 1
            || (adjustmentRefNos.length >= 1 && autoAdjRefNos.length >= 1);

        // Notify parent of column count (manual + Auto) before paint — init Adjust Total vs detail
        useLayoutEffect(() => {
            if (mode !== 'adjustment') return;
            onAdjustColumnCountChange?.(adjustColumnCount);
        }, [mode, adjustColumnCount, onAdjustColumnCountChange]);

        // PL-COT 节点汇总时需要忽略的子节点 code
        const PL_COT_EXCLUDED = useMemo(() => [
            ...FS_CODE.PL.OPENING_INVENTORIES, ...FS_CODE.PL.PURCHASES, ...FS_CODE.PL.CLOSING_INVENTORIES
        ], []);

        const resolvePlCotCosAdjustSum = useCallback((
            refNo?: string | null,
            options?: {
                excludeAuto?: boolean;
                autoOnly?: boolean;
                autoAdjType?: string;
                autoRefNo?: string;
            },
        ): number => resolvePlCotCostOfSalesAdjustmentSum({
            fsTree,
            cotInputMode: plCotInputMode,
            showCosRowInAdjustment,
            refNo,
            excludeAuto: options?.excludeAuto,
            isAutoAdjEntry,
            autoOnly: options?.autoOnly,
            autoAdjType: options?.autoAdjType,
            autoRefNo: options?.autoRefNo,
        }), [fsTree, plCotInputMode, showCosRowInAdjustment, isAutoAdjEntry]);

        // 计算父节点下所有子节点的 adjustment 总和
        const sumAdjustmentForParent = useCallback((node: any, refNo: string): number => {
            const hasKids = Array.isArray(node.children) && node.children.length > 0;
            if (!hasKids) {
                if (isCode(node, ...FS_CODE.PL.COST_OF_SALES)) {
                    return resolvePlCotCosAdjustSum(refNo, {excludeAuto: true});
                }
                if (!Array.isArray(node.adjustment)) return 0;
                // Manual Adjust#N parent totals must ignore auto-gen entries (same rule as leaf cells)
                const adj = node.adjustment.find((a: any) => String(a?.refNo) === refNo && !isAutoAdjEntry(a));
                return adj?.adjustment ?? 0;
            }
            // ✅ PL-COT 节点汇总时忽略 OI/PUR/CI
            const children = isCode(node, ...FS_CODE.PL.COST_OF_TURNOVER)
                ? (node.children || []).filter((ch: any) => !isCode(ch, ...PL_COT_EXCLUDED))
                : (node.children || []);
            let sum = 0;
            for (const child of children) {
                sum += sumAdjustmentForParent(child, refNo);
            }
            return sum;
        }, [PL_COT_EXCLUDED, resolvePlCotCosAdjustSum, isAutoAdjEntry]);

        // ✅ 计算节点的 Adjust Total（所有 refNo 的 adjustment 之和）
        const sumAllAdjustments = useCallback((node: any): number => {
            const hasKids = Array.isArray(node.children) && node.children.length > 0;
            if (!hasKids) {
                if (isCode(node, ...FS_CODE.PL.COST_OF_SALES)) {
                    return resolvePlCotCosAdjustSum(null);
                }
                // 叶子节点：直接计算所有 adjustment 之和
                if (!Array.isArray(node.adjustment)) return 0;
                return node.adjustment.reduce((sum: number, adj: any) => sum + (adj?.adjustment ?? 0), 0);
            }
            // ✅ PL-COT 节点汇总时忽略 OI/PUR/CI
            const children = isCode(node, ...FS_CODE.PL.COST_OF_TURNOVER)
                ? (node.children || []).filter((ch: any) => !isCode(ch, ...PL_COT_EXCLUDED))
                : (node.children || []);
            // 父节点：递归计算所有子节点的 adjustment 之和
            let sum = 0;
            for (const child of children) {
                sum += sumAllAdjustments(child);
            }
            return sum;
        }, [PL_COT_EXCLUDED, resolvePlCotCosAdjustSum]);

        // 递归计算父节点下所有子节点的 auto adjustment 总和
        const sumAutoAdjForParent = useCallback((node: any): number => {
            const hasKids = Array.isArray(node.children) && node.children.length > 0;
            if (!hasKids) {
                if (!Array.isArray(node.adjustment)) return 0;
                return node.adjustment
                    .filter((a: any) => isAutoAdjEntry(a))
                    .reduce((sum: number, a: any) => sum + (a?.adjustment ?? 0), 0);
            }
            const children = isCode(node, ...FS_CODE.PL.COST_OF_TURNOVER)
                ? (node.children || []).filter((ch: any) => !isCode(ch, ...PL_COT_EXCLUDED))
                : (node.children || []);
            let sum = 0;
            for (const child of children) {
                sum += sumAutoAdjForParent(child);
            }
            return sum;
        }, [PL_COT_EXCLUDED, isAutoAdjEntry]);

        // Compute derived PL adjustment per refNo using the same formula as Client column:
        // GROSS = Turnover - CostOfTurnover
        // GROSS_AND_OTHER = GROSS + OtherIncome
        // BEFORE_TAX = GROSS_AND_OTHER - Admin - Finance
        // AFTER_TAX = BEFORE_TAX - Tax
        const computeDerivedAdjForRefNo = useCallback((item: any, refNo: string): number | null => {
            const TURNOVER = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TURNOVER);
            const COST_TURNOVER = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.COST_OF_TURNOVER);
            const OTHER_INCOME = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.OTHER_INCOME);
            const ADMIN = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.ADMIN_EXPENSES);
            const FINANCE = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.FINANCE_COSTS);
            const TAX = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TAXATION);

            const getAdj = (node: any) => node ? sumAdjustmentForParent(node, refNo) : 0;

            const turnoverAdj = getAdj(TURNOVER);
            const costAdj = getAdj(COST_TURNOVER);
            const gross = turnoverAdj - costAdj;
            if (isCode(item, ...FS_CODE.PL.GROSS)) return gross;

            const otherAdj = getAdj(OTHER_INCOME);
            const grossAndOther = gross + otherAdj;
            if (isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER)) return grossAndOther;

            const adminAdj = getAdj(ADMIN);
            const financeAdj = getAdj(FINANCE);
            const beforeTax = grossAndOther - adminAdj - financeAdj;
            if (isCode(item, ...FS_CODE.PL.BEFORE_TAX)) return beforeTax;

            const taxAdj = getAdj(TAX);
            const afterTax = beforeTax - taxAdj;
            if (isCode(item, ...FS_CODE.PL.AFTER_TAX)) return afterTax;

            return null;
        }, [fsTree, sumAdjustmentForParent]);

        // Same as above but for all refNos combined (Adjust Total mode)
        const computeDerivedAdjTotal = useCallback((item: any): number | null => {
            const TURNOVER = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TURNOVER);
            const COST_TURNOVER = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.COST_OF_TURNOVER);
            const OTHER_INCOME = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.OTHER_INCOME);
            const ADMIN = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.ADMIN_EXPENSES);
            const FINANCE = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.FINANCE_COSTS);
            const TAX = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TAXATION);

            const getAdj = (node: any) => node ? sumAllAdjustments(node) : 0;

            const turnoverAdj = getAdj(TURNOVER);
            const costAdj = getAdj(COST_TURNOVER);
            const gross = turnoverAdj - costAdj;
            if (isCode(item, ...FS_CODE.PL.GROSS)) return gross;

            const otherAdj = getAdj(OTHER_INCOME);
            const grossAndOther = gross + otherAdj;
            if (isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER)) return grossAndOther;

            const adminAdj = getAdj(ADMIN);
            const financeAdj = getAdj(FINANCE);
            const beforeTax = grossAndOther - adminAdj - financeAdj;
            if (isCode(item, ...FS_CODE.PL.BEFORE_TAX)) return beforeTax;

            const taxAdj = getAdj(TAX);
            const afterTax = beforeTax - taxAdj;
            if (isCode(item, ...FS_CODE.PL.AFTER_TAX)) return afterTax;

            return null;
        }, [fsTree, sumAllAdjustments]);

        // Auto 列值为 0 时：若该行 Client/Audited/Last Year/opening 仍有数据，仍渲染 '-'
        const rowHasDisplayData = useCallback((node: any, isParent: boolean, isSubtotal: boolean): boolean => {
            if (Boolean(node?.adjustByPaper)) return true;
            // Custom Level-3 leaves must stay visible after reload even when amounts are 0
            if (isPlCustomFsItem(node) || isPlCustomFsNode(node)) return true;
            const fsIdNum = Number(node?.fsId);
            const isDerived = isCode(
                node as any,
                ...FS_CODE.PL.COST_OF_TURNOVER,
                ...FS_CODE.PL.GROSS,
                ...FS_CODE.PL.GROSS_AND_OTHER,
                ...FS_CODE.PL.BEFORE_TAX,
                ...FS_CODE.PL.AFTER_TAX,
            );
            const isLeaf = !hasChildren(node) && !plIsProtectedParentHeader(node) && !isDerived;
            const clientVal = isLeaf
                ? (leafClientValues[fsIdNum] ?? 0)
                : plResolveAdjustmentValue(node, 'client', computedClientMap);
            const auditedVal = getAuditedValue(node);
            const lastYearVal = Number(computedLastYearAuditedMap?.[fsIdNum] ?? node?.lastYearAudited ?? 0) || 0;
            if (Math.abs(clientVal) >= 0.01 || Math.abs(auditedVal) >= 0.01 || Math.abs(lastYearVal) >= 0.01) {
                return true;
            }
            if (!isParent && !isSubtotal) {
                const opening = Number(node?.openingAmount ?? node?.amount ?? 0) || 0;
                if (Math.abs(opening) >= 0.01) return true;
            }
            return false;
        }, [computedClientMap, computedLastYearAuditedMap, getAuditedValue, leafClientValues]);

        // Persist AFTER_TAX adjustment-by-refNo into store for BalanceSheet BS-EQ-LFP
        // (BalanceSheet also computes locally from PL tree; store kept for other consumers)
        const setPlAdjustmentsByRefNo = useFsCategoryStore(s => s.setPlAdjustmentsByRefNo);
        useEffect(() => {
            if (mode !== 'adjustment' || !fsTree || fsTree.length === 0) {
                setPlAdjustmentsByRefNo({});
                return;
            }
            const AFTER_TAX = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.AFTER_TAX);
            if (!AFTER_TAX) {
                setPlAdjustmentsByRefNo({});
                return;
            }
            const map = computePlAfterTaxAdjustmentsByRefNo({
                plTree: fsTree as any[],
                refNos: adjustmentRefNos.map((r) => String(r || '').trim()).filter(Boolean),
                isAutoAdjEntry,
                cotInputMode: plCotInputMode,
                showCosRowInAdjustment,
            });
            setPlAdjustmentsByRefNo(map);
        }, [
            mode,
            fsTree,
            adjustmentRefNos,
            isAutoAdjEntry,
            plCotInputMode,
            showCosRowInAdjustment,
            setPlAdjustmentsByRefNo,
        ]);

        // Build adjustment columns based on refNo (adjustment mode only)
        // When adjustTotalMode is true and there are multiple columns, show only "Adjust Total"
        const adjustmentColumns = useMemo(() => {
            if (mode !== 'adjustment') return [];

            // 原逻辑：多手动列；扩展：手动+Auto 合计达门槛时也可合并为 Adjust Total
            if (adjustTotalMode && canCollapseAdjustColumns) {
                return [{
                    key: 'adjust_total',
                    title: 'Adjust Total',
                    refNo: 'total',
                    width: 120,
                    render: (item: any, isParent: boolean, isExpanded: boolean, isSubtotal: boolean) => {
                        const isDerived = isCode(item, ...FS_CODE.PL.GROSS) ||
                            isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER) ||
                            isCode(item, ...FS_CODE.PL.BEFORE_TAX) ||
                            isCode(item, ...FS_CODE.PL.AFTER_TAX);
                        if (isDerived) {
                            const derivedVal = computeDerivedAdjTotal(item);
                            if (derivedVal == null) return null;
                            return (
                                <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                    {formatNumber(derivedVal, true)}
                                </Typography>
                            );
                        }

                        if (isParent && isExpanded && !isSubtotal) {
                            return <Box sx={{flex: 1}}/>;
                        }

                        if (isParent || isSubtotal) {
                            const parentTotal = sumAllAdjustments(item);
                            return (
                                <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                    {formatNumber(parentTotal, true)}
                                </Typography>
                            );
                        }

                        if (isCode(item, ...FS_CODE.PL.COST_OF_SALES)) {
                            const totalValue = resolvePlCotCosAdjustSum(null);
                            return (
                                <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                    {formatNumber(totalValue, true)}
                                </Typography>
                            );
                        }

                        if (!Array.isArray(item.adjustment)) {
                            return (
                                <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                    {formatNumber(0, true)}
                                </Typography>
                            );
                        }

                        // Sum all adjustments for this item
                        const totalValue = item.adjustment.reduce((sum: number, adj: any) => sum + (adj?.adjustment ?? 0), 0);

                        // In total mode, clicking is disabled
                        return (
                            <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                {formatNumber(totalValue, true)}
                            </Typography>
                        );
                    }
                }];
            }

            const manualColumns = adjustmentRefNos.map(refNo => ({
                key: `adjust_${refNo}`,
                title: `Adjust#${refNo}`,
                refNo: refNo,
                width: 120,
                render: (item: any, isParent: boolean, isExpanded: boolean, isSubtotal: boolean) => {
                    // 独立节点（派生节点）使用公式计算 adjustment
                    const isDerived = isCode(item, ...FS_CODE.PL.GROSS) ||
                        isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER) ||
                        isCode(item, ...FS_CODE.PL.BEFORE_TAX) ||
                        isCode(item, ...FS_CODE.PL.AFTER_TAX);
                    if (isDerived) {
                        const derivedVal = computeDerivedAdjForRefNo(item, refNo);
                        if (derivedVal == null) return null;
                        return (
                            <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                {formatNumber(derivedVal, true)}
                            </Typography>
                        );
                    }

                    // 父节点展开时隐藏值（小计行除外），收起时显示子节点 adjustment 总和
                    if (isParent && isExpanded && !isSubtotal) {
                        return <Box sx={{flex: 1}}/>;
                    }

                    if (isParent || isSubtotal) {
                        const parentTotal = sumAdjustmentForParent(item, refNo);
                        return (
                            <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                {formatNumber(parentTotal, true)}
                            </Typography>
                        );
                    }

                    if (isCode(item, ...FS_CODE.PL.COST_OF_SALES)) {
                        const value = resolvePlCotCosAdjustSum(refNo, {excludeAuto: true});
                        const cosNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.COST_OF_SALES);
                        const adj = Array.isArray(cosNode?.adjustment)
                            ? cosNode.adjustment.find((a: any) => String(a?.refNo) === refNo && !isAutoAdjEntry(a))
                            : undefined;
                        const headerId = adj?.headerId;
                        const isClickable = !!headerId && value !== 0 && typeof onAdjustmentCellClick === 'function';
                        return (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: 14,
                                    textAlign: 'center',
                                    ...(isClickable && {
                                        cursor: 'pointer',
                                        '&:hover': {
                                            color: 'primary.main',
                                            textDecoration: 'underline',
                                        },
                                    }),
                                }}
                                onClick={isClickable ? () => onAdjustmentCellClick(headerId, refNo, item) : undefined}
                            >
                                {formatNumber(value, true)}
                            </Typography>
                        );
                    }

                    // 叶子节点显示具体值
                    if (!Array.isArray(item.adjustment)) {
                        return (
                            <Typography variant="body2" sx={{fontSize: 14, textAlign: 'center'}}>
                                {formatNumber(0, true)}
                            </Typography>
                        );
                    }
                    const adj = item.adjustment.find((a: any) => String(a?.refNo) === refNo && !isAutoAdjEntry(a));
                    const value = adj?.adjustment ?? 0;
                    const headerId = adj?.headerId;

                    // Make cell clickable only if headerId exists AND value is non-zero
                    const isClickable = !!headerId && value !== 0 && typeof onAdjustmentCellClick === 'function';

                    return (
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: 14,
                                textAlign: 'center',
                                ...(isClickable && {
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: 'primary.main',
                                        textDecoration: 'underline'
                                    }
                                })
                            }}
                            onClick={isClickable ? () => onAdjustmentCellClick(headerId, refNo, item) : undefined}
                        >
                            {formatNumber(value, true)}
                        </Typography>
                    );
                }
            }));

            // 追加 Auto Adjust 列（按文档顺序，每个 type 一列，标题 Adjust #N(Auto)）
            if (hasAutoAdjustment) {
                for (const autoItem of autoAdjRefNos) {
                    const {type: autoType, refNo: autoRefNo} = autoItem;

                    // 递归计算指定 autoAdjType 的 adjustment 总和
                    const sumAutoTypeForParent = (node: any): number => {
                        const hasKids = Array.isArray(node.children) && node.children.length > 0;
                        if (!hasKids) {
                            if (isCode(node, ...FS_CODE.PL.COST_OF_SALES)) {
                                return resolvePlCotCosAdjustSum(autoRefNo, {
                                    autoOnly: true,
                                    autoAdjType: autoType,
                                    autoRefNo,
                                });
                            }
                            if (!Array.isArray(node.adjustment)) return 0;
                            return node.adjustment
                                .filter((a: any) => isAutoAdjEntry(a) && matchesAutoAdjColumn(a, autoType, autoRefNo))
                                .reduce((s: number, a: any) => s + (a?.adjustment ?? 0), 0);
                        }
                        const children = isCode(node, ...FS_CODE.PL.COST_OF_TURNOVER)
                            ? (node.children || []).filter((ch: any) => !isCode(ch, ...PL_COT_EXCLUDED))
                            : (node.children || []);
                        let sum = 0;
                        for (const child of children) {
                            sum += sumAutoTypeForParent(child);
                        }
                        return sum;
                    };

                    const computeDerivedAutoAdj = (item: any): number | null => {
                        const TURNOVER = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TURNOVER);
                        const COST_TURNOVER = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.COST_OF_TURNOVER);
                        const OTHER_INCOME = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.OTHER_INCOME);
                        const ADMIN = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.ADMIN_EXPENSES);
                        const FINANCE = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.FINANCE_COSTS);
                        const TAX = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TAXATION);
                        const getAdj = (node: any) => node ? sumAutoTypeForParent(node) : 0;
                        const gross = getAdj(TURNOVER) - getAdj(COST_TURNOVER);
                        if (isCode(item, ...FS_CODE.PL.GROSS)) return gross;
                        const grossAndOther = gross + getAdj(OTHER_INCOME);
                        if (isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER)) return grossAndOther;
                        const beforeTax = grossAndOther - getAdj(ADMIN) - getAdj(FINANCE);
                        if (isCode(item, ...FS_CODE.PL.BEFORE_TAX)) return beforeTax;
                        const afterTax = beforeTax - getAdj(TAX);
                        if (isCode(item, ...FS_CODE.PL.AFTER_TAX)) return afterTax;
                        return null;
                    };

                    manualColumns.push({
                        key: `auto_adjust_${autoType}`,
                        title: `Adjust#${autoRefNo}(Auto)`,
                        refNo: autoRefNo,
                        width: 120,
                        render: (item: any, isParent: boolean, isExpanded: boolean, isSubtotal: boolean) => {
                            const renderAutoCell = (value: number, sourceAdj?: any) => {
                                const headerId = sourceAdj?.headerId;
                                const isClickable = !!headerId
                                    && Math.abs(value) >= 0.01
                                    && typeof onAdjustmentCellClick === 'function';
                                return (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: 14,
                                            textAlign: 'center',
                                            ...(isClickable && {
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    color: 'primary.main',
                                                    textDecoration: 'underline',
                                                },
                                            }),
                                        }}
                                        onClick={isClickable
                                            ? () => onAdjustmentCellClick!(headerId, autoRefNo, item)
                                            : undefined}
                                    >
                                        {formatNumber(value, true)}
                                    </Typography>
                                );
                            };

                            const isDerived = isCode(item, ...FS_CODE.PL.GROSS) ||
                                isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER) ||
                                isCode(item, ...FS_CODE.PL.BEFORE_TAX) ||
                                isCode(item, ...FS_CODE.PL.AFTER_TAX);
                            if (isDerived) {
                                const derivedVal = computeDerivedAutoAdj(item);
                                if (derivedVal == null) {
                                    return renderAutoCell(0);
                                }
                                return renderAutoCell(derivedVal);
                            }
                            if (isParent && isExpanded && !isSubtotal) return <Box sx={{flex: 1}}/>;
                            if (isParent || isSubtotal) {
                                return renderAutoCell(sumAutoTypeForParent(item));
                            }
                            if (isCode(item, ...FS_CODE.PL.COST_OF_SALES)) {
                                const value = resolvePlCotCosAdjustSum(autoRefNo, {
                                    autoOnly: true,
                                    autoAdjType: autoType,
                                    autoRefNo,
                                });
                                const cosNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.COST_OF_SALES);
                                const adj = Array.isArray(cosNode?.adjustment)
                                    ? cosNode.adjustment.find((a: any) =>
                                        isAutoAdjEntry(a) && matchesAutoAdjColumn(a, autoType, autoRefNo),
                                    )
                                    : undefined;
                                return renderAutoCell(value, adj);
                            }
                            if (!Array.isArray(item.adjustment)) {
                                return renderAutoCell(0);
                            }
                            // Sum all papers of this auto type (e.g. MULTI TAX YOAs); never mix TAX_EXCHANGE via refNo
                            const matched = item.adjustment.filter((a: any) =>
                                isAutoAdjEntry(a) && matchesAutoAdjColumn(a, autoType, autoRefNo),
                            );
                            const value = matched.reduce((s: number, a: any) => s + (Number(a?.adjustment) || 0), 0);
                            const adj = matched.find((a: any) => Math.abs(Number(a?.adjustment) || 0) >= 0.01)
                                ?? matched[0];
                            return renderAutoCell(value, adj);
                        }
                    });
                }
            }

            return manualColumns;
        }, [mode, adjustmentRefNos, adjustTotalMode, canCollapseAdjustColumns, hasAutoAdjustment, autoAdjRefNos, sumAdjustmentForParent, sumAllAdjustments, sumAutoAdjForParent, computeDerivedAdjForRefNo, computeDerivedAdjTotal, onAdjustmentCellClick, fsTree, PL_COT_EXCLUDED, rowHasDisplayData, isAutoAdjEntry, resolvePlCotCosAdjustSum]);

        // Inline rendering (copy of TreeFsTable behavior)
        const [internalAuditedRounded, setInternalAuditedRounded] = useState<AuditedMode>('normal');
        const auditedRounded = controlledAuditedRounded !== undefined ? controlledAuditedRounded : internalAuditedRounded;
        const setAuditedRounded = onAuditedRoundedChange || setInternalAuditedRounded;
        const isRounding = auditedRounded !== 'normal';
        const isRoundedFinal = auditedRounded === 'rounded';
        const hasRoundingRecords = hasRoundingTargets(roundingGroup);
        const cycleAuditedMode = useCallback(() => {
            if (auditedRounded === 'normal') {
                // Total 汇总：仅跳过 RoundingAdjustment 弹窗 gate；Audited 三态切换与修前一致
                if (!onAuditedRoundedChange && plMultiViewMode !== 'total') {
                    requestAuditedRoundingGate();
                }
                setAuditedRounded('round');
            } else if (auditedRounded === 'round') {
                if (hasRoundingRecords) {
                    setAuditedRounded('rounded');
                } else {
                    setAuditedRounded('normal');
                }
            } else {
                setAuditedRounded('normal');
            }
        }, [auditedRounded, hasRoundingRecords, setAuditedRounded, onAuditedRoundedChange, plMultiViewMode]);
        useEffect(() => {
            const next: Record<number, boolean> = {};
            const walk = (nodes: any[]) => {
                for (const n of nodes || []) {
                    const hasKids = Array.isArray(n?.children) && n.children.length > 0;
                    if (hasKids) next[Number(n.fsId)] = true;
                    if (hasKids) walk(n.children as any[]);
                }
            };
            walk((fsTree || []) as any[]);
            setExpanded(next);
        }, [fsTree]);

        const fallbackName = (it: any): string => (it.name || it.aliasEn || it.nameEn || it.aliasZh || it.nameZh || it.code || '') as string;

        // 格式化数字：添加千分符和两位小数，0 显示为 '-'，负数使用括号
        const formatNumber = (n: number, showCents: boolean = true) => {
            if (n === 0 || Math.abs(n) < 0.01) {
                return '-';
            }

            const absValue = Math.abs(n);
            let formatted: string;

            if (showCents) {
                formatted = absValue.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            } else {
                formatted = Math.round(absValue).toLocaleString();
            }

            // 负数使用括号
            return n < 0 ? `(${formatted})` : formatted;
        };

        const toFixed2 = (n: number) => n.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        const renderAudited = (n: number) => isRounding ? (Math.round(n)).toLocaleString() : toFixed2(n);

        // ===== Rounding Adjustment: 递归加总子节点 PL rounding 值（用于父节点加总） =====
        const sumRoundingForPlNode = useCallback((node: any): number => {
            if (!isRounding || Object.keys(plRoundingAdjustments).length === 0) return 0;
            const code = String(node?.code || '');
            const kids = Array.isArray(node?.children) ? node.children : [];
            if (kids.length === 0) {
                return plRoundingAdjustments[code] ?? 0;
            }
            let sum = 0;
            for (const ch of kids) sum += sumRoundingForPlNode(ch);
            if (plRoundingAdjustments[code] != null) sum += plRoundingAdjustments[code];
            return sum;
        }, [isRounding, plRoundingAdjustments]);

        // Audited(Round) 模式：叶子节点四舍五入，父节点汇总子节点四舍五入后的值（round-then-sum）
        // 独立节点（派生公式节点）：先对组成部分四舍五入，再做公式计算
        // 派生节点公式中包含 sumRoundingForPlNode 以确保用户选择补充差额后，公式节点值被正确重算
        // Local = single / 各 Period Tab 算法；Total 另包一层 Σ(local per tab)
        const getRoundedAuditedValueLocal = useCallback((node: any): number => {
            // PL-COT: COS+ROY+SUB+OCT + custom Level-3（与 plComputeComputedAmounts 一致；忽略 OI/PUR/CI）
            if (isCode(node, ...FS_CODE.PL.COST_OF_TURNOVER)) {
                const COS = findNodeByCodes((fsTree || []) as any[], ['PL-COT-COS']);
                const PUR = findNodeByCodes((fsTree || []) as any[], ['PL-COT-PUR']);
                const OI = findNodeByCodes((fsTree || []) as any[], ['PL-COT-OI']);
                const CI = findNodeByCodes((fsTree || []) as any[], ['PL-COT-CI']);
                const ROY = findNodeByCodes((fsTree || []) as any[], ['PL-COT-ROY']);
                const SUB = findNodeByCodes((fsTree || []) as any[], ['PL-COT-SUB']);
                const OCT = findNodeByCodes((fsTree || []) as any[], ['PL-COT-OCT']);
                const customKids = (node.children || []).filter((ch: any) => isPlCustomFsNode(ch));
                if (COS || PUR || ROY || SUB || OCT || customKids.length) {
                    // WHY: Audited(Round) 先对叶子四舍五入，再用整元结果做 PL-COT 汇总；
                    // 禁止喂未 round 的 rawCosAuditedForDisplay，否则分位渗入 PAT/BS-EQ-LFP。
                    // PUR 录入 + 伴生 COS adj：rawCos 强制 0，按 round(OI)+round(PUR)+round(CI)。
                    const cosRounded = COS ? getRoundedAuditedValueLocal(COS) : 0;
                    const oiRounded = OI ? getRoundedAuditedValueLocal(OI) : 0;
                    const ciRounded = CI ? getRoundedAuditedValueLocal(CI) : 0;
                    const purAmt = PUR ? getRoundedAuditedValueLocal(PUR) : 0;
                    const purOpening = PUR
                        ? (Number((PUR as any)?.openingAmount ?? (PUR as any)?.amount ?? 0) || 0)
                        : 0;
                    // Audited(Round) only：与 computedAuditedMap 同口径（Client 不走此分支）
                    const primaryCost = resolvePlCotCosAmountForParentRollup({
                        rawCos: resolveRawCosForPlCotParentAuditedRollup({
                            cosOpening: rawCosClientForDisplay,
                            purOpening,
                            cosAuditedRaw: cosRounded,
                            purAudited: purAmt,
                        }),
                        openingInv: oiRounded,
                        purchases: purAmt,
                        closingInv: ciRounded,
                    });
                    let customSum = 0;
                    for (const ch of customKids) customSum += getRoundedAuditedValueLocal(ch);
                    return primaryCost +
                        (ROY ? getRoundedAuditedValueLocal(ROY) : 0) +
                        (SUB ? getRoundedAuditedValueLocal(SUB) : 0) +
                        (OCT ? getRoundedAuditedValueLocal(OCT) : 0) +
                        customSum;
                }
            }
            // PL-GP: TURNOVER - COST_OF_TURNOVER（含 rounding adjustment）
            if (isCode(node, ...FS_CODE.PL.GROSS)) {
                const TURNOVER = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TURNOVER);
                const COT = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.COST_OF_TURNOVER);
                return (TURNOVER ? getRoundedAuditedValueLocal(TURNOVER) + sumRoundingForPlNode(TURNOVER) : 0)
                    - (COT ? getRoundedAuditedValueLocal(COT) + sumRoundingForPlNode(COT) : 0);
            }
            // PL-GPOI: GROSS + OTHER_INCOME（含 rounding adjustment）
            if (isCode(node, ...FS_CODE.PL.GROSS_AND_OTHER)) {
                const GROSS = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.GROSS);
                const OI = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.OTHER_INCOME);
                return (GROSS ? getRoundedAuditedValueLocal(GROSS) + sumRoundingForPlNode(GROSS) : 0)
                    + (OI ? getRoundedAuditedValueLocal(OI) + sumRoundingForPlNode(OI) : 0);
            }
            // PL-PBT: GROSS_AND_OTHER - ADMIN - FINANCE（含 rounding adjustment）
            if (isCode(node, ...FS_CODE.PL.BEFORE_TAX)) {
                const GPOI = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.GROSS_AND_OTHER);
                const ADMIN = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.ADMIN_EXPENSES);
                const FIN = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.FINANCE_COSTS);
                return (GPOI ? getRoundedAuditedValueLocal(GPOI) + sumRoundingForPlNode(GPOI) : 0)
                    - (ADMIN ? getRoundedAuditedValueLocal(ADMIN) + sumRoundingForPlNode(ADMIN) : 0)
                    - (FIN ? getRoundedAuditedValueLocal(FIN) + sumRoundingForPlNode(FIN) : 0);
            }
            // PL-PAT: BEFORE_TAX - TAXATION（含 rounding adjustment）
            if (isCode(node, ...FS_CODE.PL.AFTER_TAX)) {
                const PBT = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.BEFORE_TAX);
                const TAX = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TAXATION);
                return (PBT ? getRoundedAuditedValueLocal(PBT) + sumRoundingForPlNode(PBT) : 0)
                    - (TAX ? getRoundedAuditedValueLocal(TAX) + sumRoundingForPlNode(TAX) : 0);
            }

            const kids = Array.isArray(node?.children) ? node.children : [];
            if (kids.length === 0) {
                return Math.round(getAuditedValue(node));
            }
            let sum = 0;
            for (const child of kids) {
                sum += getRoundedAuditedValueLocal(child);
            }
            return sum;
        }, [getAuditedValue, fsTree, sumRoundingForPlNode, rawCosClientForDisplay]);

        // Total/combine：禁止 round(Σ)；改为 Σ(各 tab local rounding)
        const getRoundedAuditedValue = useCallback((node: any): number => {
            if (
                plMultiViewMode === 'total'
                && Array.isArray(periodsFromStore)
                && periodsFromStore.length >= 2
            ) {
                const fsId = Number(node?.fsId);
                const orderedSlices = periodsFromStore
                    .map((p) => ({
                        key: String(p?.key || '').trim(),
                        yearOfAssessment: p?.yearOfAssessment,
                    }))
                    .filter((p) => p.key);
                const summed = sumRoundedAuditedAcrossSlices(
                    roundedAuditedBySlice,
                    orderedSlices,
                    fsId,
                );
                if (summed != null) return summed;
            }
            return getRoundedAuditedValueLocal(node);
        }, [
            plMultiViewMode,
            periodsFromStore,
            roundedAuditedBySlice,
            getRoundedAuditedValueLocal,
        ]);

        // Persist rounded AFTER_TAX value into store so BalanceSheet can use it for BS-EQ-LFP in rounding mode.
        // SINGLE: original path unchanged.
        // Total/combine: must NOT write round(Σ) — period tabs own Σ(round-each-tab).
        const setPlRoundedAfterTax = useFsCategoryStore(s => s.setPlRoundedAfterTax);
        useEffect(() => {
            if (plMultiViewMode === 'total') return;
            if (plMultiChainMeta && periodScope?.sliceKey) return;
            if (mode !== 'adjustment' || !fsTree || fsTree.length === 0) {
                setPlRoundedAfterTax(null);
                return;
            }
            const AFTER_TAX = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.AFTER_TAX);
            if (!AFTER_TAX) {
                setPlRoundedAfterTax(null);
                return;
            }
            const roundedVal = getRoundedAuditedValueLocal(AFTER_TAX);
            setPlRoundedAfterTax(roundedVal);
        }, [
            mode,
            fsTree,
            getRoundedAuditedValueLocal,
            setPlRoundedAfterTax,
            plMultiChainMeta,
            periodScope?.sliceKey,
            plMultiViewMode,
        ]);

        // MULTI Period Tabs only: each tab rounds like SINGLE, then Σ → BS-EQ-LFP.
        // Also publish per-fsId rounded map for Total/combine Audited (Round) display.
        useEffect(() => {
            if (!plMultiChainMeta || !periodScope?.sliceKey) return;
            if (mode !== 'adjustment' || !fsTree || fsTree.length === 0) {
                setPlRoundedAfterTax(null);
                return;
            }
            const AFTER_TAX = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.AFTER_TAX);
            if (!AFTER_TAX) {
                setPlRoundedAfterTax(null);
                return;
            }
            const roundedVal = getRoundedAuditedValueLocal(AFTER_TAX);
            const sliceKey = periodScope.sliceKey;
            const prev = usePlMultiPeriodChainStore.getState().afterTaxBySlice[sliceKey];
            setPlSliceAfterTax(sliceKey, {
                client: prev?.client ?? afterTaxClient,
                audited: prev?.audited ?? afterTaxAudited,
                lastYear: prev?.lastYear ?? afterTaxLastYear,
                roundedAudited: roundedVal,
            });

            const roundedByFsId: Record<number, number> = {};
            const visitRounded = (n: any) => {
                const fsId = Number(n?.fsId);
                if (Number.isFinite(fsId) && fsId > 0) {
                    roundedByFsId[fsId] = getRoundedAuditedValueLocal(n);
                }
                const kids = Array.isArray(n?.children) ? n.children : [];
                for (const ch of kids) visitRounded(ch);
            };
            for (const root of (fsTree || []) as any[]) visitRounded(root);
            setPlSliceRoundedAudited(sliceKey, roundedByFsId);

            const orderedSlices = plMultiChainMeta.periods
                .map((p) => ({
                    key: String(p?.key || '').trim(),
                    yearOfAssessment: p?.yearOfAssessment,
                }))
                .filter((p) => p.key);
            const agg = sumPlAfterTaxBridgeAcrossSlices(
                usePlMultiPeriodChainStore.getState().afterTaxBySlice,
                orderedSlices,
            );
            setPlRoundedAfterTax(agg.roundedAudited);
        }, [
            mode,
            fsTree,
            getRoundedAuditedValueLocal,
            setPlRoundedAfterTax,
            plMultiChainMeta,
            periodScope?.sliceKey,
            setPlSliceAfterTax,
            setPlSliceRoundedAudited,
            afterTaxClient,
            afterTaxAudited,
            afterTaxLastYear,
        ]);

        // Total/combine: mirror Σ(round-each-tab) from chain store → BS bridge (tabs stay mounted).
        useEffect(() => {
            if (plMultiViewMode !== 'total') return;
            if (mode !== 'adjustment') return;
            if (!Array.isArray(periodsFromStore) || periodsFromStore.length < 2) return;
            const orderedSlices = periodsFromStore
                .map((p) => ({
                    key: String(p?.key || '').trim(),
                    yearOfAssessment: p?.yearOfAssessment,
                }))
                .filter((p) => p.key);
            const agg = sumPlAfterTaxBridgeAcrossSlices(afterTaxBySlice, orderedSlices);
            if (agg.roundedAudited != null) {
                setPlRoundedAfterTax(agg.roundedAudited);
            }
        }, [
            plMultiViewMode,
            mode,
            periodsFromStore,
            afterTaxBySlice,
            setPlRoundedAfterTax,
        ]);

        // Visibility rule (adjustment mode only):
        // - Leaf: hide if audited == 0
        // - Parent: hide only if all descendants audited == 0
        const visibleByFsId = useMemo(() => {
            const out: Record<number, boolean> = {};
            const visit = (node: any): boolean => {
                const kids: any[] = Array.isArray(node?.children) ? node.children : [];
                const isParentNode = kids.length > 0;
                let hasVisibleChild = false;
                for (const ch of kids) {
                    if (visit(ch)) hasVisibleChild = true;
                }
                const hasNonZeroAdj = Array.isArray(node.adjustment) && node.adjustment.some((a: any) => Math.abs(a?.adjustment ?? 0) >= 0.01);
                const hasAdjustByPaper = Boolean(node?.adjustByPaper);
                const hasRoundingAdj = isRounding && (plRoundingAdjustments[String(node?.code || '')] ?? 0) !== 0;
                // 仅当 client / audited / last year / 非零 adjust 有值时显示；Auto 占位（值为 0）不触发显示
                let selfVisible = rowHasDisplayData(node, isParentNode, false)
                    || hasNonZeroAdj || hasAdjustByPaper || hasRoundingAdj;
                // BS 有存货时必须展示 Cost of Sales（由 OI + PUR + CI 计算，底稿 COS 常为 0）
                if (isCode(node, 'PL-COT-COS') && !hideCostOfSalesRowForDisplay) {
                    selfVisible = true;
                }
                // FS 模式录入 Purchases Last Year 时，adjustment 模式仍展示 PUR 行
                if (isCode(node, 'PL-COT-PUR')) {
                    const purFsId = Number(node?.fsId);
                    const fsPurLastYear = resolvePlLastYearLeafAmount(
                        Number.isFinite(purFsId) ? purFsId : undefined,
                        sPLLastYearValues,
                    );
                    if (Math.abs(fsPurLastYear) >= 0.01) {
                        selfVisible = true;
                    }
                }
                // PL-TURN-SI：与 PL-TURN-SE 联动显示
                const isTurnSi = isCode(node, 'PL-TURN-SI');
                let turnSiVisible = selfVisible;
                if (isTurnSi) {
                    const turnSeNode = findNodeByCodes((fsTree || []) as any[], ['PL-TURN-SE']);
                    const turnSeFsId = Number((turnSeNode as any)?.fsId);
                    const turnSeClientVal = turnSeFsId ? (leafClientValues[turnSeFsId] ?? computedClientMap[turnSeFsId] ?? 0) : 0;
                    const turnSeAuditedVal = turnSeFsId ? (leafAuditedValues[turnSeFsId] ?? computedAuditedMap[turnSeFsId] ?? 0) : 0;
                    const turnSeHasData = !isAuditedZero(turnSeClientVal) || !isAuditedZero(turnSeAuditedVal);
                    if (!turnSeHasData) {
                        turnSiVisible = true;
                    }
                }
                const visible = (isTurnSi ? turnSiVisible : selfVisible) || hasVisibleChild;
                out[Number(node?.fsId)] = visible;

                return visible;
            };

            for (const root of (fsTree || []) as any[]) {
                visit(root);
            }
            return out;
        }, [fsTree, rowHasDisplayData, isAuditedZero, leafClientValues, leafAuditedValues, computedClientMap, computedAuditedMap, isRounding, plRoundingAdjustments, hideCostOfSalesRowForDisplay, sPLLastYearValues]);

        // PL-GP / TURNOVER：非法除法（除数为 0 / 非有限）→ "N/A"；合法结果按百分比两位小数展示
        const formatGpOverTurnoverRatio = (numerator: number, denominator: number): string => {
            if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
                return 'N/A';
            }
            const ratioPercent = (numerator / denominator) * 100;
            if (!Number.isFinite(ratioPercent)) return 'N/A';
            return `${ratioPercent.toFixed(2)}%`;
        };

        const rows = useMemo(() => {
            const flat = flatten((fsTree || []) as any[], 0);
            const base = hideCostOfSalesRowForDisplay ? filterHiddenCostOfSalesRows(flat) : flat;
            // 仅在 PL-GP 节点下方插入比率行（Client / Audited / Last Year = GP ÷ TURNOVER）
            const out: Array<{
                item: any;
                level: number;
                isParent: boolean;
                isSubtotal?: boolean;
                parentItem?: any;
                parentFsId: number | null;
                isGpTurnoverRatio?: boolean;
            }> = [];
            for (const row of base) {
                out.push(row);
                if (!row.isSubtotal && isCode(row.item, ...FS_CODE.PL.GROSS)) {
                    out.push({
                        item: {
                            ...row.item,
                            fsId: `${row.item.fsId}_gp_turn_ratio`,
                            name: '',
                            code: 'PL-GP-TURN-RATIO',
                        },
                        level: row.level,
                        isParent: false,
                        isSubtotal: false,
                        parentItem: row.item,
                        parentFsId: Number(row.item.fsId),
                        isGpTurnoverRatio: true,
                    });
                }
            }
            return out;
        }, [fsTree, expanded, hideCostOfSalesRowForDisplay, flatten, filterHiddenCostOfSalesRows]);
        const isZeroish = (node: React.ReactNode): boolean => {
            if (node === null || node === undefined) return false;
            if (typeof node === 'number') return node === 0;
            if (typeof node === 'string') {
                const cleaned = node.replace(/,/g, '').trim();
                if (cleaned === '') return false;
                const n = Number(cleaned);
                return !Number.isNaN(n) && n === 0;
            }
            if (React.isValidElement(node)) {
                const anyProps: any = (node as any).props || {};
                const v = anyProps.value;
                if (typeof v === 'number' && v === 0) return true;
                if (typeof v === 'string') {
                    const cleaned = v.replace(/,/g, '').trim();
                    if (cleaned !== '' && !Number.isNaN(Number(cleaned)) && Number(cleaned) === 0) return true;
                }
                const ch = anyProps.children;
                if (typeof ch === 'string') {
                    const cleaned = ch.replace(/,/g, '').trim();
                    if (cleaned !== '' && !Number.isNaN(Number(cleaned)) && Number(cleaned) === 0) return true;
                }
            }
            return false;
        };
        // filterNonEmpty and visible columns removed (draft, review, history)

        return (
            <Box
                ref={fillRootRef}
                sx={{
                width: '100%',
                height: '100%',
                maxHeight: fillHeightPx != null ? fillHeightPx : undefined,
                minHeight: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {reportHeader}
                <Divider sx={{mb: 1}}/>
                <Box sx={{
                    flex: 1,
                    height: '100%',
                    minHeight: 0,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <TableContainer ref={tableContainerRef} component={Paper} sx={{flex: 1, minHeight: 0, overflow: 'auto'}}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={mode === 'adjustment'
                                        ? {
                                            position: 'sticky', left: 0, top: 0, zIndex: 4, backgroundColor: 'background.paper',
                                            width: 400,
                                            minWidth: 400,
                                            maxWidth: 400, px: 0
                                        }
                                        : {width: '18%'}}/>
                                    <TableCell align="center" sx={mode === 'adjustment'
                                        ? {
                                            position: 'sticky',
                                            left: 400,
                                            top: 0,
                                            zIndex: 4,
                                            backgroundColor: 'background.paper',
                                            width: 50,
                                            minWidth: 50,
                                            maxWidth: 50,
                                            px: 0
                                        }
                                        : {width: 65, px: 0}}/>
                                    {/*<TableCell align="center" sx={{width: 60, minWidth: 60, maxWidth: 60}}/>*/}
                                    {showClientByYoa ? (
                                        clientYoaColumns.map((col) => (
                                            <TableCell
                                                align="center"
                                                key={`client_hdr_${col.yoa}`}
                                                sx={clientHeaderCellSx}
                                                onClick={toggleClientByYoa}
                                            >
                                                Client
                                                <Typography variant="caption" sx={{
                                                    fontSize: 12,
                                                    color: 'text.secondary',
                                                    display: 'block',
                                                    lineHeight: 1
                                                }}>{col.yoa}</Typography>
                                                {presentationCurrency && <Typography variant="caption" sx={{
                                                    fontSize: 12,
                                                    color: 'text.secondary',
                                                    display: 'block',
                                                    lineHeight: 1
                                                }}>{presentationCurrency}</Typography>}
                                            </TableCell>
                                        ))
                                    ) : canExpandClientByYoa ? (
                                        <TableCell
                                            align="center"
                                            sx={clientHeaderCellSx}
                                            onClick={toggleClientByYoa}
                                        >
                                            Client
                                            {columnHeaderYoa && <Typography variant="caption" sx={{
                                                fontSize: 12,
                                                color: 'text.secondary',
                                                display: 'block',
                                                lineHeight: 1
                                            }}>{columnHeaderYoa}</Typography>}
                                            {presentationCurrency && <Typography variant="caption" sx={{
                                                fontSize: 12,
                                                color: 'text.secondary',
                                                display: 'block',
                                                lineHeight: 1
                                            }}>{presentationCurrency}</Typography>}
                                        </TableCell>
                                    ) : (
                                        <TableCell align="center" sx={{width: 160, fontWeight: 700}}>
                                            Client
                                            {columnHeaderYoa && <Typography variant="caption" sx={{
                                                fontSize: 12,
                                                color: 'text.secondary',
                                                display: 'block',
                                                lineHeight: 1
                                            }}>{columnHeaderYoa}</Typography>}
                                            {presentationCurrency && <Typography variant="caption" sx={{
                                                fontSize: 12,
                                                color: 'text.secondary',
                                                display: 'block',
                                                lineHeight: 1
                                            }}>{presentationCurrency}</Typography>}
                                        </TableCell>
                                    )}
                                    {fsOpeningEditMode && (
                                        <TableCell align="center" sx={{
                                            width: 160,
                                            fontWeight: 700,
                                            bgcolor: 'primary.light',
                                            color: 'white'
                                        }}>
                                            Adjustment
                                            {columnHeaderYoa && <Typography variant="caption" sx={{
                                                fontSize: 12,
                                                display: 'block',
                                                lineHeight: 1,
                                                opacity: 0.85
                                            }}>{columnHeaderYoa}</Typography>}
                                            {presentationCurrency && <Typography variant="caption" sx={{
                                                fontSize: 12,
                                                display: 'block',
                                                lineHeight: 1,
                                                opacity: 0.85
                                            }}>{presentationCurrency}</Typography>}
                                        </TableCell>
                                    )}
                                    {adjustmentColumns.map((col: any) => (
                                        <TableCell
                                            align="center"
                                            key={col.key}
                                            sx={[
                                                {fontWeight: 700, width: col.width},
                                                canCollapseAdjustColumns ? adjustColumnToggleHeaderSx : {},
                                            ]}
                                            onClick={canCollapseAdjustColumns ? () => setAdjustTotalMode(!adjustTotalMode) : undefined}
                                        >
                                            {col.title}
                                            {columnHeaderYoa && <Typography variant="caption" sx={{
                                                fontSize: 12,
                                                color: 'text.secondary',
                                                display: 'block',
                                                lineHeight: 1
                                            }}>{columnHeaderYoa}</Typography>}
                                            {presentationCurrency && <Typography variant="caption" sx={{
                                                fontSize: 12,
                                                color: 'text.secondary',
                                                display: 'block',
                                                lineHeight: 1
                                            }}>{presentationCurrency}</Typography>}
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{width: 160, fontWeight: 700, cursor: 'pointer'}}
                                               onClick={cycleAuditedMode}>
                                        {auditedRounded === 'normal' ? 'Audited' : auditedRounded === 'round' ? 'Audited (Round)' : 'Audited (Rounded)'}
                                        {auditedRounded === 'round' && roundingDifference !== 0 && !hasRoundingRecords
                                            && plMultiViewMode !== 'total' && (
                                            <Typography component="span" sx={{
                                                ml: 0.5,
                                                color: 'warning.main',
                                                fontSize: 14,
                                                cursor: 'pointer'
                                            }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            requestAuditedRoundingGate();
                                                        }}>
                                                ⚠️
                                            </Typography>
                                        )}
                                        {columnHeaderYoa && <Typography variant="caption" sx={{
                                            fontSize: 12,
                                            color: 'text.secondary',
                                            display: 'block',
                                            lineHeight: 1
                                        }}>{columnHeaderYoa}</Typography>}
                                        {presentationCurrency && <Typography variant="caption" sx={{
                                            fontSize: 12,
                                            color: 'text.secondary',
                                            display: 'block',
                                            lineHeight: 1
                                        }}>{presentationCurrency}</Typography>}
                                    </TableCell>
                                    {!hideLastYearColumns && (
                                        <>
                                            <TableCell align="center" sx={{width: 160, fontWeight: 700, ...lastYearColumnSeparatorSx}}>
                                                Last Year
                                                {lastYearOfAssessment && <Typography variant="caption" sx={{
                                                    fontSize: 12,
                                                    color: 'text.secondary',
                                                    display: 'block',
                                                    lineHeight: 1
                                                }}>{lastYearOfAssessment}</Typography>}
                                                {presentationCurrency && <Typography variant="caption" sx={{
                                                    fontSize: 12,
                                                    color: 'text.secondary',
                                                    display: 'block',
                                                    lineHeight: 1
                                                }}>{presentationCurrency}</Typography>}
                                            </TableCell>
                                            <TableCell align="center"
                                                       sx={{width: 120, fontWeight: 700}}>Change</TableCell>
                                            <TableCell align="center" sx={{width: 88, fontWeight: 700, lineHeight: 1.15}}>
                                                <Typography component="span" sx={{display: 'block', fontWeight: 700, fontSize: 14, lineHeight: 1.15}}>Ratio</Typography>
                                                <Typography component="span" sx={{display: 'block', fontWeight: 700, fontSize: 14, lineHeight: 1.15}}>against</Typography>
                                                <Typography component="span" sx={{display: 'block', fontWeight: 700, fontSize: 14, lineHeight: 1.15}}>turnover</Typography>
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && (
                                    <FsStatementTableSkeletonRows
                                        columns={buildAdjustmentSkeletonColumns({
                                            statement: 'pl',
                                            adjustColumnCount: adjustmentColumns.length,
                                            hideLastYearColumns,
                                            fsOpeningEditMode,
                                            clientColumnCount,
                                        })}
                                    />
                                )}
                                {!loading && rows.length === 0 && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={showClientByYoa
                                                ? (3 + clientColumnCount + adjustmentColumns.length)
                                                : (4 + adjustmentColumns.length)}
                                            align="center">No data</TableCell>
                                    </TableRow>
                                )}
                                {!loading && rows.length > 0 && rows.map(({
                                                                              item,
                                                                              level,
                                                                              isParent,
                                                                              isSubtotal,
                                                                              parentItem,
                                                                              isGpTurnoverRatio,
                                                                          }) => {
                                    // PL-GP 下方比率行：仅 Client / Audited / Last Year = GP ÷ TURNOVER
                                    if (isGpTurnoverRatio && parentItem) {
                                        const gpFsId = Number(parentItem.fsId);
                                        if (visibleByFsId[gpFsId] === false) return null;

                                        const turnoverNode = findNodeByCodes((fsTree || []) as any[], FS_CODE.PL.TURNOVER);
                                        if (!turnoverNode) return null;

                                        const gpClient = plResolveAdjustmentValue(parentItem, 'client', computedClientMap);
                                        const turnClient = plResolveAdjustmentValue(turnoverNode, 'client', computedClientMap);

                                        let gpAudited = getAuditedValue(parentItem);
                                        let turnAudited = getAuditedValue(turnoverNode);
                                        if (isRounding) {
                                            gpAudited = getRoundedAuditedValue(parentItem) + sumRoundingForPlNode(parentItem);
                                            turnAudited = getRoundedAuditedValue(turnoverNode) + sumRoundingForPlNode(turnoverNode);
                                        }

                                        const gpLastYear = computedLastYearAuditedMap[gpFsId] || 0;
                                        const turnLastYear = computedLastYearAuditedMap[Number((turnoverNode as any).fsId)] || 0;

                                        const renderRatioCell = (numerator: number, denominator: number) => {
                                            const text = formatGpOverTurnoverRatio(numerator, denominator);
                                            return (
                                                <Typography variant="body2" sx={{
                                                    fontSize: 14,
                                                    textAlign: 'center',
                                                    ...(text === 'N/A' ? {color: 'text.secondary'} : null),
                                                }}>
                                                    {text}
                                                </Typography>
                                            );
                                        };

                                        return (
                                            <TableRow key={item.fsId} hover={false} sx={{
                                                '& .MuiTableCell-root': {
                                                    paddingTop: '0.5px',
                                                    paddingBottom: '0.5px',
                                                },
                                            }}>
                                                <TableCell sx={mode === 'adjustment'
                                                    ? {
                                                        position: 'sticky',
                                                        left: 0,
                                                        zIndex: 1,
                                                        backgroundColor: 'background.paper',
                                                        width: 250,
                                                        minWidth: 250,
                                                        maxWidth: 250
                                                    }
                                                    : undefined}>
                                                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                        <Box sx={{pl: level * 2}}/>
                                                        <Box sx={{width: 15}}/>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={mode === 'adjustment'
                                                    ? {
                                                        position: 'sticky',
                                                        left: 400,
                                                        zIndex: 1,
                                                        backgroundColor: 'background.paper',
                                                        width: 70,
                                                        minWidth: 70,
                                                        maxWidth: 70,
                                                        px: 0
                                                    }
                                                    : {px: 0}}/>
                                                {showClientByYoa ? (
                                                    clientYoaColumns.map((col) => {
                                                        const gpYoa = resolveClientAmountForYoa(gpFsId, col.yoa);
                                                        const turnYoa = resolveClientAmountForYoa(
                                                            Number((turnoverNode as any).fsId),
                                                            col.yoa,
                                                        );
                                                        return (
                                                            <TableCell
                                                                align="center"
                                                                id="client"
                                                                key={`client_ratio_${col.yoa}_${item.fsId}`}
                                                            >
                                                                {renderRatioCell(gpYoa, turnYoa)}
                                                            </TableCell>
                                                        );
                                                    })
                                                ) : (
                                                    <TableCell align="center" id="client">
                                                        {renderRatioCell(gpClient, turnClient)}
                                                    </TableCell>
                                                )}
                                                {fsOpeningEditMode && <TableCell align="center" id="fsEdit"/>}
                                                {adjustmentColumns.map((col: any) => (
                                                    <TableCell align="center" id="adjust" key={`${col.key}_${item.fsId}`}/>
                                                ))}
                                                <TableCell align="center" id="audit">
                                                    {renderRatioCell(gpAudited, turnAudited)}
                                                </TableCell>
                                                {!hideLastYearColumns && (
                                                    <>
                                                        <TableCell align="center" id="lastYear" sx={lastYearColumnSeparatorSx}>
                                                            {renderRatioCell(gpLastYear, turnLastYear)}
                                                        </TableCell>
                                                        <TableCell align="center" id="change"/>
                                                        <TableCell align="center" id="ratioAgainstTurnover"/>
                                                    </>
                                                )}
                                            </TableRow>
                                        );
                                    }

                                    const isExpanded = expanded[Number(item.fsId)];

                                    // Hide 0-audited nodes in adjustment mode
                                    if (!isSubtotal && mode === 'adjustment') {
                                        const visible = visibleByFsId[Number(item.fsId)];
                                        if (visible === false) return null;
                                    }

                                    // Check if item has adjustByPaper = true (adjustment mode only)
                                    const hasAdjustByPaper = mode === 'adjustment' && (item as any)?.adjustByPaper === true;

                                    // 在 WP_TO_WP 模式下需要排除的 schedule_code
                                    // BANK_LOAN, VEHICLE_LOAN, PROPERTY_PLANT_EQUIPMENT 在所有模式下都允许操作
                                    // const excludedScheduleCodes = [
                                    //     // 'NON_BANK_LOAN',
                                    //     // 'NON_VEHICLE_LOAN'
                                    // ];

                                    // 检查是否应该排除该 schedule_code（使用顶层定义的 currentMode）
                                    const shouldExclude = (currentMode === AdjustmentMode.ADJUST_WP_TO_WP);
                                    // &&excludedScheduleCodes.includes(String(item?.scheduleCode || '').trim());

                                    let showSchedule = (!isParent && !!item.scheduleName) && !shouldExclude && (
                                        hasAdjustByPaper || (!resolvedAllowedScheduleCodes ? true : (
                                            resolvedAllowedScheduleCodes.has(normalizeLineCode((item as any)?.code || (item as any)?.scheduleCode)) ||
                                            resolvedAllowedScheduleCodes.has(String((item as any)?.scheduleCode || '').trim())
                                        ))
                                    );

                                    const isDerived = isCode(
                                        item as any,
                                        ...FS_CODE.PL.COST_OF_TURNOVER,
                                        ...FS_CODE.PL.GROSS,
                                        ...FS_CODE.PL.GROSS_AND_OTHER,
                                        ...FS_CODE.PL.BEFORE_TAX,
                                        ...FS_CODE.PL.AFTER_TAX,
                                    );
                                    const isLeaf = !hasChildren(item) && !plIsProtectedParentHeader(item) && !isDerived;
                                    const fsIdNum = Number(item.fsId);

                                    let clientVal = isLeaf
                                        ? (leafClientValues[fsIdNum] ?? 0)
                                        : plResolveAdjustmentValue(item, 'client', computedClientMap);

                                    let auditedVal = getAuditedValue(item);

                                    // ===== Rounding: 父节点使用 round-then-sum, 派生节点使用公式重算 =====
                                    // Total/combine：叶子也走 Σ(各 tab Math.round)，禁止 Math.round(Σ)
                                    if (isRounding && (hasChildren(item) || isDerived)) {
                                        auditedVal = getRoundedAuditedValue(item) + sumRoundingForPlNode(item);
                                    } else if (isRounding && isLeaf && plMultiViewMode === 'total') {
                                        auditedVal = getRoundedAuditedValue(item);
                                    }

                                    // 如果是小计行，使用父节点的计算值
                                    if (isSubtotal && parentItem) {
                                        clientVal = !hasChildren(parentItem) && !plIsProtectedParentHeader(parentItem)
                                            && !isCode(
                                                parentItem as any,
                                                ...FS_CODE.PL.COST_OF_TURNOVER,
                                                ...FS_CODE.PL.GROSS,
                                                ...FS_CODE.PL.GROSS_AND_OTHER,
                                                ...FS_CODE.PL.BEFORE_TAX,
                                                ...FS_CODE.PL.AFTER_TAX,
                                            )
                                            ? (leafClientValues[Number(parentItem.fsId)] ?? 0)
                                            : plResolveAdjustmentValue(parentItem, 'client', computedClientMap);

                                        auditedVal = getAuditedValue(parentItem);

                                        // ===== Rounding: 小计行使用父节点 round-then-sum / 派生节点公式重算 =====
                                        if (isRounding && (hasChildren(parentItem) || isCode(
                                            parentItem as any,
                                            ...FS_CODE.PL.COST_OF_TURNOVER,
                                            ...FS_CODE.PL.GROSS,
                                            ...FS_CODE.PL.GROSS_AND_OTHER,
                                            ...FS_CODE.PL.BEFORE_TAX,
                                            ...FS_CODE.PL.AFTER_TAX,
                                        ))) {
                                            auditedVal = getRoundedAuditedValue(parentItem) + sumRoundingForPlNode(parentItem);
                                        }
                                    }

                                    // Subtotal row: hide when client / adjust / audited / last year are all zero
                                    if (isSubtotal && parentItem) {
                                        const lastYearSubtotalVal = computedLastYearAuditedMap?.[Number(parentItem.fsId)] ?? 0;
                                        const parentHasKids = hasChildren(parentItem);
                                        const parentHasNonZeroAdj = Array.isArray((parentItem as any)?.adjustment)
                                            && (parentItem as any).adjustment.some((a: any) => Math.abs(a?.adjustment ?? 0) >= 0.01);
                                        if (isAuditedZero(auditedVal) && isAuditedZero(lastYearSubtotalVal)
                                            && !rowHasDisplayData(parentItem, parentHasKids, true)
                                            && !parentHasNonZeroAdj) {
                                            return null;
                                        }
                                    }

                                    // TODO PL-AE 受控节点：重要性门槛判断，不满足则隐藏 scheduleName (need to confirm the full logic)
                                    // if (showSchedule && PL_AE_SCHEDULE_CONTROLLED_CODES.has(normalizeLineCode(item?.code || ''))) {
                                    //     const absAudited = Math.abs(auditedVal);
                                    //     const absRounded = isRounding ? Math.abs(getRoundedAuditedValue(item)) : absAudited;
                                    //     const ratio = turnoverAuditedForThreshold !== 0
                                    //         ? absAudited / Math.abs(turnoverAuditedForThreshold)
                                    //         : 0;
                                    //     if (!(ratio > 0.04999 || absRounded > 149999)) {
                                    //         showSchedule = false;
                                    //     }
                                    // }

                                    // 名称生成：使用计算出的 auditedVal
                                    const name = ((): string => {
                                        if (isSubtotal) return ''; // 小计行显示空白名称
                                        const nameAmount = auditedVal;
                                        return plNameFor(item, nameAmount) || fallbackName(item);
                                    })();
                                    return (
                                        <TableRow key={item.fsId} hover={!isSubtotal} sx={{
                                            '& .MuiTableCell-root': {
                                                paddingTop: '0.5px',
                                                paddingBottom: '0.5px',
                                                ...((item as any).level === 2 && !isParent && !isSubtotal && {
                                                    paddingTop: '5px',
                                                    paddingBottom: '5px',
                                                }),
                                                ...((isSubtotal || isCode(item, 'PL-COT-CI', 'PL-COT-OCT')) && {
                                                    borderBottom: '2px solid',
                                                    borderBottomColor: 'divider',
                                                }),
                                                ...(isSubtotal && {
                                                    fontWeight: 600
                                                })
                                            },
                                            ...(mode === 'adjustment' && !isSubtotal && {
                                                '&:hover > .MuiTableCell-root:nth-of-type(-n+2)': {
                                                    backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0.04))',
                                                }
                                            })
                                        }}>
                                            <TableCell sx={mode === 'adjustment'
                                                ? {
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 1,
                                                    backgroundColor: 'background.paper',
                                                    width: 250,
                                                    minWidth: 250,
                                                    maxWidth: 250
                                                }
                                                : undefined}>
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <Box sx={{pl: level * 2}}/>
                                                    {isParent && !isSubtotal ? (
                                                        <IconButton size="small"
                                                                    onClick={() => toggleNode(Number(item.fsId))}>
                                                            {isExpanded ? <ExpandLessIcon fontSize="small"/> :
                                                                <ExpandMoreIcon fontSize="small"/>}
                                                        </IconButton>
                                                    ) : (
                                                        <Box sx={{width: 15}}/>
                                                    )}
                                                    <InlineEditableName
                                                        value={name}
                                                        placeholder={String((item as any).nameEn || (item as any).nameZh || item.code || '')}
                                                        editable={
                                                            mode === 'adjustment'
                                                            && itemAliasEditEnabled
                                                            && !readOnly
                                                            && !isSubtotal
                                                            && canEditItemAlias(item)
                                                        }
                                                        locked={
                                                            mode === 'adjustment'
                                                            && itemAliasEditEnabled
                                                            && !readOnly
                                                            && !isSubtotal
                                                            && isItemAliasLocked(item)
                                                        }
                                                        lockedItemLabel={name || String(item.code || 'item')}
                                                        typographySx={{
                                                            mr: 1,
                                                            ...((isSubtotal || (item as any).level === 2) && {fontWeight: 600}),
                                                            ...((item as any).level === 2 && {fontFamily: '"Times New Roman", serif'}),
                                                        }}
                                                        onCommit={async (next) => {
                                                            if (!onItemAliasChange || !item.fsId || !item.code) return;
                                                            await onItemAliasChange({
                                                                fsId: Number(item.fsId),
                                                                code: String(item.code),
                                                                statement: 'PL',
                                                                displayName: next,
                                                            });
                                                        }}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" sx={mode === 'adjustment'
                                                ? {
                                                    position: 'sticky',
                                                    left: 400,
                                                    zIndex: 1,
                                                    backgroundColor: 'background.paper',
                                                    width: 70,
                                                    minWidth: 70,
                                                    maxWidth: 70,
                                                    px: 0,
                                                    overflow: 'visible',
                                                }
                                                : {px: 0, overflow: 'visible'}}>
                                                {showSchedule && !isSubtotal ? (
                                                    <ScheduleChip
                                                        mode={mode}
                                                        item={{
                                                            wpStatus: (item as any).wpStatus ?? null,
                                                            wpIssueRemark: (item as any).wpIssueRemark ?? null,
                                                            openingAmount: (item as any).openingAmount ?? null,
                                                            outstanding: (item as any).outstanding ?? null,
                                                            outstandingRemark: (item as any).outstandingRemark ?? null,
                                                        }}
                                                        label={getScheduleName(scheduleNameMap, item)}
                                                        mustFill={isMustFillScheduleCode(
                                                            normalizeLineCode((item as any)?.code || (item as any)?.scheduleCode),
                                                            getScheduleName(scheduleNameMap, item),
                                                        )}
                                                        onClick={() => {
                                                            try {
                                                                if (typeof onScheduleClick === 'function') {
                                                                    // Same Last Year value as the Last Year column (MULTI chain aware)
                                                                    const fsIdForLy = Number(item.fsId);
                                                                    let computedLastYearAudited = 0;
                                                                    if (isSubtotal && parentItem) {
                                                                        computedLastYearAudited = computedLastYearAuditedMap[Number(parentItem.fsId)] || 0;
                                                                    } else if (
                                                                        Object.prototype.hasOwnProperty.call(computedLastYearAuditedMap || {}, fsIdForLy)
                                                                    ) {
                                                                        computedLastYearAudited = Number(computedLastYearAuditedMap[fsIdForLy]) || 0;
                                                                    } else {
                                                                        computedLastYearAudited = Number((item as any)?.lastYearAudited ?? 0) || 0;
                                                                    }
                                                                    onScheduleClick({
                                                                        ...item,
                                                                        computedAudited: auditedVal,
                                                                        computedLastYearAudited,
                                                                        lastYearAudited: computedLastYearAudited,
                                                                    });
                                                                }
                                                            } catch {
                                                            }
                                                        }}
                                                    />
                                                ) : null}
                                            </TableCell>
                                            {/* Client */}
                                            {showClientByYoa ? (
                                                clientYoaColumns.map((col) => {
                                                    const yoaFsId = isSubtotal && parentItem
                                                        ? Number(parentItem.fsId)
                                                        : fsIdNum;
                                                    const yoaClientVal = resolveClientAmountForYoa(yoaFsId, col.yoa);
                                                    return (
                                                        <TableCell
                                                            align="center"
                                                            id="client"
                                                            key={`client_${col.yoa}_${item.fsId}`}
                                                        >
                                                            {!isSubtotal && isParent && isExpanded ? (
                                                                <Box sx={{flex: 1}}/>
                                                            ) : (
                                                                <Typography variant="body2"
                                                                            sx={{fontSize: 14, textAlign: 'center'}}>
                                                                    {formatNumber(yoaClientVal || 0, true)}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })
                                            ) : (
                                                <TableCell align="center" id='client'>
                                                    {!isSubtotal && isParent && isExpanded ? (
                                                        <Box sx={{flex: 1}}/>
                                                    ) : (
                                                        <Typography variant="body2"
                                                                    sx={{fontSize: 14, textAlign: 'center'}}>
                                                            {formatNumber(clientVal || 0, true)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            )}
                                            {fsOpeningEditMode && (
                                                <TableCell align="center" id='fsEdit'>
                                                    {!isParent && !isSubtotal && !isDerived && !(currentMode === AdjustmentMode.ADJUST_FS_TO_FS && isCode(item, ...FS_CODE.PL.OPENING_INVENTORIES, ...FS_CODE.PL.PURCHASES, ...FS_CODE.PL.CLOSING_INVENTORIES, ...FS_CODE.PL.COST_OF_SALES)) && isExclusiveCode((item as any)?.code) ? (
                                                        <MoneyField
                                                            value={fsOpeningAmounts[Number(item.fsId)] || 0}
                                                            showCents
                                                            align="center"
                                                            tiny
                                                            editable
                                                            compact
                                                            sx={{
                                                                flex: 1,
                                                                fontSize: 14,
                                                                '& .MuiInputBase-input': {
                                                                    paddingLeft: '0.8px',
                                                                    paddingRight: '0.8px',
                                                                    paddingTop: '0.4px',
                                                                    paddingBottom: '0.4px',
                                                                }
                                                            }}
                                                            onFocus={() => {
                                                                // Mode 2 辅助输入：当用户点击第二个 InputField 时自动填充
                                                                if (currentMode === AdjustmentMode.ADJUST_FS_TO_FS &&
                                                                    mode2CapturedValue !== null &&
                                                                    mode2CapturedFsId !== null &&
                                                                    mode2CapturedFsId !== Number(item.fsId) &&
                                                                    !mode2AssistUsed) {

                                                                    // 获取当前 item 的 entry
                                                                    const currentEntry = (item as any)?.entry; // 'Dr' 或 'Cr'

                                                                    // 根据 WP_TO_WP 的方式计算值：entry 相同取负数，不同保持正数
                                                                    let calculatedValue = mode2CapturedValue;
                                                                    if (mode2CapturedEntry && currentEntry) {
                                                                        if (mode2CapturedEntry === currentEntry) {
                                                                            calculatedValue = -mode2CapturedValue;
                                                                        } else {
                                                                            calculatedValue = mode2CapturedValue;
                                                                        }
                                                                    } else {
                                                                        // 如果没有 entry 信息，默认取负数
                                                                        calculatedValue = -mode2CapturedValue;
                                                                    }

                                                                    // 自动填充值
                                                                    const newAmounts = {...fsOpeningAmounts};
                                                                    newAmounts[Number(item.fsId)] = calculatedValue;
                                                                    setFsOpeningAmounts(newAmounts);

                                                                    // 标记辅助已使用，清空捕获的值
                                                                    setMode2AssistUsed(true);
                                                                    setMode2CapturedValue(null, null, null);

                                                                    // 触发数据回调
                                                                    const findNodeById = (nodes: any[], targetId: number): any => {
                                                                        for (const node of nodes || []) {
                                                                            if (Number(node.fsId) === targetId) return node;
                                                                            if (Array.isArray(node.children)) {
                                                                                const found = findNodeById(node.children, targetId);
                                                                                if (found) return found;
                                                                            }
                                                                        }
                                                                        return null;
                                                                    };

                                                                    const rowData = Object.entries(newAmounts)
                                                                        .filter(([_, amount]) => Math.abs(amount) > 0.01)
                                                                        .map(([fsIdStr, amount]) => {
                                                                            const fsId = Number(fsIdStr);
                                                                            const node = findNodeById(fsTree as any[], fsId);
                                                                            if (!node) return null;

                                                                            const client = plResolveAdjustmentValue(node, 'client', computedClientMap);
                                                                            const originalAudited = plResolveAdjustmentValue(node, 'audited', computedAuditedMap);
                                                                            const adjustHis = originalAudited - client;
                                                                            const closing = client + amount + adjustHis;

                                                                            return {
                                                                                fsId: node.fsId,
                                                                                code: normalizeLineCode((node as any)?.code || (node as any)?.scheduleCode),
                                                                                scheduleName: node.scheduleName || node.name || '',
                                                                                statement: 'PL' as const,
                                                                                entry: (node as any)?.entry,
                                                                                adjustmentAmount: amount,
                                                                                openingAmount: client,
                                                                                closingAmount: closing,
                                                                                adjustHisAmount: adjustHis,
                                                                            };
                                                                        })
                                                                        .filter(d => d !== null) as any[];

                                                                    if (onFsOpeningDataChange) {
                                                                        onFsOpeningDataChange(rowData);
                                                                    }
                                                                }
                                                            }}
                                                            onChangeNumber={(val: number) => {
                                                                const newAmounts = {...fsOpeningAmounts};
                                                                newAmounts[Number(item.fsId)] = val;
                                                                setFsOpeningAmounts(newAmounts);

                                                                // Mode 2 辅助输入：捕获第一个输入的值
                                                                if (currentMode === AdjustmentMode.ADJUST_FS_TO_FS &&
                                                                    mode2CapturedValue === null &&
                                                                    !mode2AssistUsed &&
                                                                    Math.abs(val) > 0.01) {
                                                                    // 获取当前 item 的 entry
                                                                    const currentEntry = (item as any)?.entry || null;
                                                                    setMode2CapturedValue(val, Number(item.fsId), currentEntry);
                                                                }

                                                                // 收集所有非零数据并回调
                                                                // 使用扁平化的方式从 fsTree 查找节点
                                                                const findNodeById = (nodes: any[], targetId: number): any => {
                                                                    for (const node of nodes || []) {
                                                                        if (Number(node.fsId) === targetId) return node;
                                                                        if (Array.isArray(node.children)) {
                                                                            const found = findNodeById(node.children, targetId);
                                                                            if (found) return found;
                                                                        }
                                                                    }
                                                                    return null;
                                                                };

                                                                const rowData = Object.entries(newAmounts)
                                                                    .filter(([_, amount]) => Math.abs(amount) > 0.01)
                                                                    .map(([fsIdStr, amount]) => {
                                                                        const fsId = Number(fsIdStr);
                                                                        const node = findNodeById(fsTree as any[], fsId);
                                                                        if (!node) return null;

                                                                        // 计算 Client 值
                                                                        const client = plResolveAdjustmentValue(node, 'client', computedClientMap);

                                                                        // 计算原始 Audited 值（不含用户输入）
                                                                        const originalAudited = plResolveAdjustmentValue(node, 'audited', computedAuditedMap);

                                                                        // 历史调整值 = 原始 Audited - Client
                                                                        const adjustHis = originalAudited - client;

                                                                        // 新的 Audited = Client + 用户输入 + 历史调整
                                                                        const closing = client + amount + adjustHis;

                                                                        return {
                                                                            fsId: node.fsId,
                                                                            code: normalizeLineCode((node as any)?.code || (node as any)?.scheduleCode),
                                                                            scheduleName: node.scheduleName || node.name || '',
                                                                            statement: 'PL' as const,
                                                                            entry: (node as any)?.entry,
                                                                            adjustmentAmount: amount,
                                                                            openingAmount: client,
                                                                            closingAmount: closing,
                                                                            adjustHisAmount: adjustHis,
                                                                        };
                                                                    })
                                                                    .filter(d => d !== null) as any[];

                                                                if (onFsOpeningDataChange) {
                                                                    onFsOpeningDataChange(rowData);
                                                                }
                                                            }}
                                                        />
                                                    ) : null}
                                                </TableCell>
                                            )}
                                            {/* Adjust #No */}
                                            {adjustmentColumns.map((col: any) => (
                                                <TableCell align="center" id='adjust' key={`${col.key}_${item.fsId}`}>
                                                    {col.render(item, isParent, isExpanded, isSubtotal)}
                                                </TableCell>
                                            ))}
                                            {/* Audit */}
                                            <TableCell align="center" id='audit'>
                                                {!isSubtotal && isParent && isExpanded ? (
                                                    <Box sx={{flex: 1}}/>
                                                ) : (() => {
                                                    // 在 Round 模式下叠加四舍五入调整值（仅 PL 源节点）
                                                    let displayVal = auditedVal || 0;
                                                    const nodeCode = String((isSubtotal && parentItem ? parentItem : item)?.code || '');
                                                    const roundingAdj = isRounding ? (plRoundingAdjustments[nodeCode] ?? 0) : 0;
                                                    if (roundingAdj !== 0) displayVal += roundingAdj;
                                                    // 'round' 模式下显示拆分标记，'rounded' 模式下不显示
                                                    const hasRoundingMark = auditedRounded === 'round' && roundingAdj !== 0;

                                                    if (!hasRoundingMark) {
                                                        return (
                                                            <Typography variant="body2"
                                                                        sx={{fontSize: 14, textAlign: 'center'}}>
                                                                {formatNumber(displayVal, !isRounding)}
                                                            </Typography>
                                                        );
                                                    }

                                                    // 有 Round 调整 → 单行 xxx+xxx，"+xxx" 标黄 + 删除图标
                                                    return (
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: 0.5
                                                        }}>
                                                            <Typography variant="body2" component="span"
                                                                        sx={{fontSize: 14}}>
                                                                {auditedVal === 0 ? '0' : formatNumber(auditedVal, false)}
                                                            </Typography>
                                                            <Typography variant="body2" component="span"
                                                                        sx={{
                                                                            fontSize: 14,
                                                                            fontWeight: 600,
                                                                            color: 'warning.main'
                                                                        }}>
                                                                {roundingAdj > 0 ? '+' : ''}{roundingAdj}
                                                            </Typography>
                                                            {/* Total 只读汇总：展示 Σ rounding，不提供按节点删除（删除仍在 multiple 分片编辑） */}
                                                            {plMultiViewMode !== 'total' && !readOnly ? (
                                                            <IconButton size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setDeleteAnchor({
                                                                                el: e.currentTarget,
                                                                                nodeCode
                                                                            });
                                                                        }}
                                                                        sx={{
                                                                            p: 0,
                                                                            ml: 0.25,
                                                                            color: 'text.secondary',
                                                                            '&:hover': {color: 'error.main'}
                                                                        }}>
                                                                <CancelOutlinedIcon sx={{fontSize: 14}}/>
                                                            </IconButton>
                                                            ) : null}
                                                        </Box>
                                                    );
                                                })()}
                                            </TableCell>
                                            {/* Last Year */}
                                            {!hideLastYearColumns && (
                                                <TableCell align="center" id='lastYear' sx={lastYearColumnSeparatorSx}>
                                                    {!isSubtotal && isParent && isExpanded ? (
                                                        <Box sx={{flex: 1}}/>
                                                    ) : (() => {
                                                        // For Last Year, directly use computed values from computedLastYearAuditedMap
                                                        // which already handles parent sums and derived nodes correctly
                                                        let lastYearVal = 0;
                                                        if (isSubtotal && parentItem) {
                                                            // For subtotal rows, use parent's computed value
                                                            lastYearVal = computedLastYearAuditedMap[Number(parentItem.fsId)] || 0;
                                                        } else if (isCode(item, ...FS_CODE.PL.GROSS) ||
                                                            isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER) ||
                                                            isCode(item, ...FS_CODE.PL.BEFORE_TAX) ||
                                                            isCode(item, ...FS_CODE.PL.AFTER_TAX)) {
                                                            // For derived nodes, use computed value directly
                                                            lastYearVal = computedLastYearAuditedMap[Number(item.fsId)] || 0;
                                                        } else if (hasChildren(item)) {
                                                            // For parent nodes, use computed sum
                                                            lastYearVal = computedLastYearAuditedMap[Number(item.fsId)] || 0;
                                                        } else {
                                                            // Leaves: prefer computed map (MULTI chain / COT overrides);
                                                            // fallback tree lastYearAudited (SINGLE #20 — map usually has no leaf keys)
                                                            const fsId = Number(item.fsId);
                                                            lastYearVal = (Number.isFinite(fsId)
                                                                && computedLastYearAuditedMap
                                                                && Object.prototype.hasOwnProperty.call(computedLastYearAuditedMap, fsId))
                                                                ? (Number(computedLastYearAuditedMap[fsId]) || 0)
                                                                : (Number((item as any)?.lastYearAudited ?? 0) || 0);
                                                        }
                                                        return (
                                                            <Typography variant="body2"
                                                                        sx={{fontSize: 14, textAlign: 'center'}}>
                                                                {formatNumber(lastYearVal, !isRounding)}
                                                            </Typography>
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}
                                            {/* Change */}
                                            {!hideLastYearColumns && (
                                                <TableCell align="center" id='change'>
                                                    {!isSubtotal && isParent && isExpanded ? (
                                                        <Box sx={{flex: 1}}/>
                                                    ) : (() => {
                                                        // Change 列：父/小计/派生节点 auditedVal 已含 rounding，叶子节点需额外加
                                                        const changeNodeCode = String((isSubtotal && parentItem ? parentItem : item)?.code || '');
                                                        const isLeafForChange = !isSubtotal && !hasChildren(item) && !isDerived;
                                                        const changeRoundingAdj = (isRounding && isLeafForChange) ? (plRoundingAdjustments[changeNodeCode] ?? 0) : 0;
                                                        const currentValue = (auditedVal || 0) + changeRoundingAdj;
                                                        // For Last Year, directly use computed values from computedLastYearAuditedMap
                                                        let lastYearValue = 0;
                                                        if (isSubtotal && parentItem) {
                                                            lastYearValue = computedLastYearAuditedMap[Number(parentItem.fsId)] || 0;
                                                        } else if (isCode(item, ...FS_CODE.PL.GROSS) ||
                                                            isCode(item, ...FS_CODE.PL.GROSS_AND_OTHER) ||
                                                            isCode(item, ...FS_CODE.PL.BEFORE_TAX) ||
                                                            isCode(item, ...FS_CODE.PL.AFTER_TAX)) {
                                                            lastYearValue = computedLastYearAuditedMap[Number(item.fsId)] || 0;
                                                        } else if (hasChildren(item)) {
                                                            lastYearValue = computedLastYearAuditedMap[Number(item.fsId)] || 0;
                                                        } else {
                                                            // Leaves: same source as Last Year column (MULTI chain + COT)
                                                            const fsId = Number(item.fsId);
                                                            lastYearValue = (Number.isFinite(fsId)
                                                                && computedLastYearAuditedMap
                                                                && Object.prototype.hasOwnProperty.call(computedLastYearAuditedMap, fsId))
                                                                ? (Number(computedLastYearAuditedMap[fsId]) || 0)
                                                                : (Number((item as any)?.lastYearAudited ?? 0) || 0);
                                                        }

                                                        if (lastYearValue === 0 || !Number.isFinite(lastYearValue)) {
                                                            return (
                                                                <Typography variant="body2" sx={{
                                                                    fontSize: 14,
                                                                    color: 'text.secondary'
                                                                }}>
                                                                    N/A
                                                                </Typography>
                                                            );
                                                        }

                                                        const changePercent = ((currentValue - lastYearValue) / lastYearValue) * 100;

                                                        if (!Number.isFinite(changePercent) || changePercent === 0) {
                                                            return (
                                                                <Typography variant="body2" sx={{
                                                                    fontSize: 14,
                                                                    color: 'text.secondary'
                                                                }}>
                                                                    N/A
                                                                </Typography>
                                                            );
                                                        }

                                                        return (
                                                            <Box
                                                                sx={isSubtotal ? {'& .MuiTypography-root': {fontWeight: 600}} : undefined}>
                                                                <UpDownIconDisplay changePercent={changePercent}/>
                                                            </Box>
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}
                                            {/* Ratio against turnover：仅追加展示，不改动其它列取值 */}
                                            {!hideLastYearColumns && (
                                                <TableCell align="center" id="ratioAgainstTurnover">
                                                    {(() => {
                                                        if (isSubtotal || isParent) return null;
                                                        if (!ratioAgainstTurnoverFsIds.has(fsIdNum)) return null;
                                                        const turnoverAudited = turnoverAuditedForThreshold;
                                                        if (!Number.isFinite(turnoverAudited) || turnoverAudited === 0) {
                                                            return (
                                                                <Typography variant="body2" sx={{
                                                                    fontSize: 14,
                                                                    color: 'text.secondary'
                                                                }}>
                                                                    N/A
                                                                </Typography>
                                                            );
                                                        }
                                                        const ratioPercent = ((auditedVal || 0) / turnoverAudited) * 100;
                                                        if (!Number.isFinite(ratioPercent)) {
                                                            return (
                                                                <Typography variant="body2" sx={{
                                                                    fontSize: 14,
                                                                    color: 'text.secondary'
                                                                }}>
                                                                    N/A
                                                                </Typography>
                                                            );
                                                        }
                                                        return (
                                                            <Typography variant="body2" sx={{
                                                                fontSize: 14,
                                                                textAlign: 'center',
                                                                // 与 Change 列红色一致：error.main
                                                                color: ratioPercent >= 5 ? 'error.main' : 'text.primary',
                                                            }}>
                                                                {`${ratioPercent.toFixed(2)}%`}
                                                            </Typography>
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                {/* 共享 Popover：Rounding 删除二次确认（表格层级唯一实例） */}
                <Popover
                    open={!!deleteAnchor}
                    anchorEl={deleteAnchor?.el}
                    onClose={() => setDeleteAnchor(null)}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                >
                    <Box p={2}>
                        <Typography variant="body2" sx={{mb: 1}}>
                            Confirm to remove rounding adjustment?
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => setDeleteAnchor(null)}>Cancel</Button>
                            <Button size="small" color="error" variant="contained"
                                    onClick={() => {
                                        handleRoundingRemove();
                                        setDeleteAnchor(null);
                                    }}>
                                Confirm
                            </Button>
                        </Stack>
                    </Box>
                </Popover>
            </Box>
        );
    }

    const reloadFsAfterCustomNodeChange = useCallback(async () => {
        const cid = Number(resolvedCompanyId);
        const aid = Number(annualId);
        if (!cid || !aid) return;
        lastFsInitSigRef.current = null;
        fsComputeSeedSigRef.current = null;
        fsComputeSeedRef.current = null;
        await useFsCategoryStore.getState().initIfNeeded(cid, aid, `custom-node-${Date.now()}`);
    }, [resolvedCompanyId, annualId]);

    /** FS mode: + on parent → expand + inline name/amount row above subtotal (no create dialog) */
    const handleOpenCustomNodeDialog = useCallback((parentItem: any) => {
        const parentFsId = Number(parentItem?.fsId);
        if (!Number.isFinite(parentFsId) || parentFsId <= 0) return;
        setCustomInlineEdit(null);
        setExpanded((prev) => ({...prev, [parentFsId]: true}));
        const amountsByYoa: Record<string, number> = {};
        for (const yoa of fsYoaList) amountsByYoa[yoa] = 0;
        setCustomInlineDraft({
            parentFsId,
            parentCode: String(parentItem?.code || '').trim() || undefined,
            parentEntry: String(parentItem?.entry || '').trim() || undefined,
            nameEn: '',
            currentAmount: 0,
            lastYearAmount: 0,
            amountsByYoa,
            saving: false,
        });
    }, [fsYoaList]);

    const handleCustomDraftNameChange = useCallback((nameEn: string) => {
        setCustomInlineDraft((prev) => (prev ? {...prev, nameEn} : prev));
    }, []);

    const handleCustomDraftCurrentChange = useCallback((num: number) => {
        setCustomInlineDraft((prev) => (prev ? {...prev, currentAmount: num} : prev));
    }, []);

    const handleCustomDraftLastYearChange = useCallback((num: number) => {
        setCustomInlineDraft((prev) => (prev ? {...prev, lastYearAmount: num} : prev));
    }, []);

    const handleCustomDraftYoaChange = useCallback((yoa: string, num: number) => {
        setCustomInlineDraft((prev) => {
            if (!prev) return prev;
            return {...prev, amountsByYoa: {...prev.amountsByYoa, [yoa]: num}};
        });
    }, []);

    const customDraftCommitLockRef = useRef(false);

    const handleCustomDraftCancel = useCallback(() => {
        customDraftCommitLockRef.current = false;
        pendingCustomDraftAmountsRef.current = null;
        setCustomInlineDraft(null);
    }, []);

    /** Write custom-node amounts into local maps + store immediately (collectLines / changeFs). */
    const applyCustomNodeAmountsToStore = useCallback((args: {
        fsId: number;
        currentAmount: number;
        lastYearAmount: number;
        amountsByYoa: Record<string, number>;
    }) => {
        const {fsId, currentAmount, lastYearAmount, amountsByYoa} = args;
        if (!Number.isFinite(fsId) || fsId <= 0) return;
        const st = useFsCategoryStore.getState();
        const nextDisplays = {...amountDisplaysRef.current, [fsId]: String(currentAmount || 0)};
        const nextLy = {...lastYearAmountsRef.current, [fsId]: String(lastYearAmount || 0)};
        amountDisplaysRef.current = nextDisplays;
        lastYearAmountsRef.current = nextLy;
        setAmountDisplays(nextDisplays);
        setLastYearAmounts(nextLy);
        st.setPlValues(zeroPlCotFormulaSideInDataMap(nextDisplays, cotInputModeCurrent, plCotFsIds));
        const lyMode = resolvePlCotInputMode(cotInputModeLastYear, {
            plTree: fsTree,
            bsTree: bsTreeForCot,
            plValuesMap: nextLy,
            bsValuesMap: sBsLastYearValues || {},
            bsLastYearMap: sBsLastYearValues || {},
        });
        st.setPlLastYearValues(zeroPlCotFormulaSideInDataMap(nextLy, lyMode, plCotFsIds));
        if (isFsMulti) {
            const nextByYoa: Record<string, Record<number, string>> = {...amountDisplaysByYoaRef.current};
            for (const [yoa, num] of Object.entries(amountsByYoa || {})) {
                nextByYoa[yoa] = {...(nextByYoa[yoa] || {}), [fsId]: String(num || 0)};
            }
            amountDisplaysByYoaRef.current = nextByYoa;
            setAmountDisplaysByYoa(nextByYoa);
            const storeByYoa: Record<string, Record<number, string>> = {};
            for (const [yoa, m] of Object.entries(nextByYoa)) {
                storeByYoa[yoa] = zeroPlCotFormulaSideInDataMap(m, cotInputModeCurrent, plCotFsIds);
            }
            st.setPlValuesByYoa(storeByYoa);
        }
    }, [
        cotInputModeCurrent, cotInputModeLastYear, plCotFsIds, isFsMulti, fsTree,
        sBSTreeNested, sBsTree, sBsLastYearValues,
    ]);

    const handleCustomDraftCommit = useCallback(async () => {
        const draft = customInlineDraftRef.current;
        const cid = Number(resolvedCompanyId);
        if (!draft || draft.saving || customDraftCommitLockRef.current) return;
        if (!cid) {
            // WHY: silent return previously skipped POST /create with empty table
            throw new Error('Company id is required to create a custom FS node');
        }
        const nameEn = draft.nameEn.trim();
        if (!nameEn) {
            setCustomInlineDraft(null);
            return;
        }
        customDraftCommitLockRef.current = true;
        setCustomInlineDraft({...draft, saving: true});
        try {
            const vo = await createCustomNode({
                companyId: cid,
                parentFsId: draft.parentFsId,
                parentCode: draft.parentCode,
                nameEn,
                entry: draft.parentEntry,
            });
            const newFsId = Number(vo?.fsId);
            if (!Number.isFinite(newFsId) || newFsId <= 0) {
                throw new Error('Custom node create returned no fsId');
            }
            const pending = {
                fsId: newFsId,
                currentAmount: draft.currentAmount,
                lastYearAmount: draft.lastYearAmount,
                amountsByYoa: {...draft.amountsByYoa},
            };
            // WHY: store must hold amounts before changeFs; pending also survives tree reload wipe
            pendingCustomDraftAmountsRef.current = pending;
            applyCustomNodeAmountsToStore(pending);
            setCustomInlineDraft(null);
            await reloadFsAfterCustomNodeChange();
        } catch (e: unknown) {
            pendingCustomDraftAmountsRef.current = null;
            setCustomInlineDraft((prev) => (prev ? {...prev, saving: false} : prev));
            const msg = e instanceof Error ? e.message : 'Failed to create custom node';
            // eslint-disable-next-line no-console
            console.error('[custom-node] create failed', e);
            // Re-throw so FsDetail Update/Submit can surface msg and abort changeFs
            throw (e instanceof Error ? e : new Error(msg));
        } finally {
            customDraftCommitLockRef.current = false;
        }
    }, [resolvedCompanyId, reloadFsAfterCustomNodeChange, applyCustomNodeAmountsToStore]);

    // Bound each render for FsDetail Update/Submit → changeFs
    prepareForPersistRef.current = async () => {
        if (mode !== 'fs') return;
        const draft = customInlineDraftRef.current;
        if (draft?.nameEn?.trim()) {
            await handleCustomDraftCommit();
        } else if (draft) {
            setCustomInlineDraft(null);
        }
        const st = useFsCategoryStore.getState();
        const displays = amountDisplaysRef.current;
        const ly = lastYearAmountsRef.current;
        const byYoa = amountDisplaysByYoaRef.current;
        st.setPlValues(zeroPlCotFormulaSideInDataMap(displays, cotInputModeCurrent, plCotFsIds));
        const lyMode = resolvePlCotInputMode(cotInputModeLastYear, {
            plTree: fsTree,
            bsTree: bsTreeForCot,
            plValuesMap: ly,
            bsValuesMap: sBsLastYearValues || {},
            bsLastYearMap: sBsLastYearValues || {},
        });
        st.setPlLastYearValues(zeroPlCotFormulaSideInDataMap(ly, lyMode, plCotFsIds));
        if (isFsMulti) {
            const storeByYoa: Record<string, Record<number, string>> = {};
            for (const [yoa, m] of Object.entries(byYoa || {})) {
                storeByYoa[yoa] = zeroPlCotFormulaSideInDataMap(m, cotInputModeCurrent, plCotFsIds);
            }
            st.setPlValuesByYoa(storeByYoa);
        }
        // Re-apply pending after reload wipe if init has not consumed it yet
        const pending = pendingCustomDraftAmountsRef.current;
        if (pending && Number.isFinite(pending.fsId) && pending.fsId > 0) {
            applyCustomNodeAmountsToStore(pending);
        }
    };

    const resolveCustomNodeId = useCallback((item: any): number | null => {
        if (!item) return null;
        const fromField = Number(item.customNodeId);
        if (Number.isFinite(fromField) && fromField > 0) return fromField;
        const fsId = Number(item.fsId);
        if (Number.isFinite(fsId) && fsId >= 2_000_000_000) return fsId - 2_000_000_000;
        return null;
    }, []);

    const handleEditCustomNode = useCallback((item: any) => {
        if (!isPlCustomFsItem(item)) return;
        const editId = resolveCustomNodeId(item);
        const fsId = Number(item.fsId);
        if (!editId || !Number.isFinite(fsId)) return;
        setCustomInlineDraft(null);
        setCustomInlineEdit({
            editId,
            fsId,
            nameEn: String(item.nameEn || item.name || ''),
            saving: false,
        });
    }, [resolveCustomNodeId]);

    const handleCustomEditNameChange = useCallback((nameEn: string) => {
        setCustomInlineEdit((prev) => (prev ? {...prev, nameEn} : prev));
    }, []);

    const handleCustomEditCancel = useCallback(() => {
        setCustomInlineEdit(null);
    }, []);

    const customEditCommitLockRef = useRef(false);

    const handleCustomEditCommit = useCallback(async () => {
        const draft = customInlineEditRef.current;
        const cid = Number(resolvedCompanyId);
        if (!draft || !cid || customEditCommitLockRef.current || draft.saving) return;
        const trimmed = draft.nameEn.trim();
        if (!trimmed) {
            throw new Error('name_en is required');
        }
        customEditCommitLockRef.current = true;
        setCustomInlineEdit({...draft, saving: true});
        try {
            await updateCustomNode({
                id: draft.editId,
                companyId: cid,
                nameEn: trimmed,
            });
            setCustomInlineEdit(null);
            await reloadFsAfterCustomNodeChange();
        } catch (e: unknown) {
            setCustomInlineEdit((prev) => (prev ? {...prev, saving: false} : prev));
            throw e;
        } finally {
            customEditCommitLockRef.current = false;
        }
    }, [resolvedCompanyId, reloadFsAfterCustomNodeChange]);

    const handleDeleteCustomNodeClick = useCallback((item: any, anchorEl: HTMLElement) => {
        const cid = Number(resolvedCompanyId);
        const id = resolveCustomNodeId(item);
        if (!cid || !id) return;
        setCustomDeleteAnchor({el: anchorEl, item});
    }, [resolvedCompanyId, resolveCustomNodeId]);

    const handleConfirmDeleteCustomNode = useCallback(async () => {
        const cid = Number(resolvedCompanyId);
        const id = resolveCustomNodeId(customDeleteAnchor?.item);
        setCustomDeleteAnchor(null);
        if (!cid || !id) return;
        await deleteCustomNode(cid, id, true);
        await reloadFsAfterCustomNodeChange();
    }, [resolvedCompanyId, customDeleteAnchor, resolveCustomNodeId, reloadFsAfterCustomNodeChange]);

    // FS mode: full table like BalanceSheet FS (no windowing — virtual spacer remount caused scroll jank)
    if (mode === 'fs') {
        const rows = useMemo(() => {
            return flatten((fsTree || []) as any[], 0);
        }, [fsTree, expanded, flatten]);

        // GL FS Summary（readOnly）：隐藏 Current / Last Year 均为 0 的行
        const fsSummaryVisibleByFsId = useMemo(() => {
            if (!readOnly) return null;
            const resolveCurrent = (node: any): number => {
                const fsId = Number(node?.fsId);
                const isParent = hasChildren(node);
                let amount = computeAllComputedAmounts[fsId] ?? 0;
                if (!isParent && !isProtectedParentHeader(node) && !(fsId in computeAllComputedAmounts)) {
                    amount = parseAmountDisplay(amountDisplays[fsId] ?? '0');
                }
                return amount;
            };
            const resolveLastYear = (node: any): number => {
                const fsId = Number(node?.fsId);
                const isParent = hasChildren(node);
                let amount = computeAllLastYearAmounts[fsId] ?? 0;
                if (!isParent && !isProtectedParentHeader(node) && !(fsId in computeAllLastYearAmounts)) {
                    amount = parseAmountDisplay(lastYearAmounts[fsId] ?? '0');
                }
                return amount;
            };
            return buildFsSummaryVisibleByFsId(
                (fsTree || []) as any[],
                resolveCurrent,
                resolveLastYear,
                { hideLastYear: !!hideLastYearColumns },
            );
        }, [
            readOnly, fsTree, computeAllComputedAmounts, computeAllLastYearAmounts,
            amountDisplays, lastYearAmounts, isProtectedParentHeader, hideLastYearColumns,
        ]);

        const displayRows = useMemo(() => {
            let next = rows as PlFsFlatRow[];
            if (fsSummaryVisibleByFsId) {
                next = next.filter((row) => {
                    const visibilityId = row.isSubtotal && row.parentItem
                        ? Number(row.parentItem.fsId)
                        : Number(row.item.fsId);
                    return fsSummaryVisibleByFsId[visibilityId] !== false;
                });
            }
            // Inject inline create row after Cost of Sales (PL-COT) or above parent Level-2 subtotal
            if (customInlineDraft) {
                const parentFsId = customInlineDraft.parentFsId;
                const draftRow: PlFsFlatRow = {
                    item: {
                        fsId: `custom-draft-${parentFsId}`,
                        name: '',
                        level: 3,
                        parentId: parentFsId,
                    },
                    level: 1,
                    isParent: false,
                    isCustomDraft: true,
                    parentItem: {fsId: parentFsId},
                };
                next = insertCustomRowAfterCostOfSales(
                    next,
                    parentFsId,
                    customInlineDraft.parentCode,
                    draftRow,
                );
            }
            return next;
        }, [rows, fsSummaryVisibleByFsId, customInlineDraft]);

        return (
            <>
            <PlFsMultiVirtualTable
                reportHeader={reportHeader}
                loading={loading}
                rows={displayRows}
                expanded={expanded}
                fsYoaList={fsYoaList}
                isFsMulti={isFsMulti}
                hideLastYearColumns={!!hideLastYearColumns}
                lastYearOnly={!!lastYearOnly}
                lastYearRemarkByFsCode={lastYearRemarkByFsCode}
                readOnly={!!readOnly}
                cotInputModeCurrent={cotInputModeCurrent}
                cotInputModeLastYear={cotInputModeLastYear}
                amountDisplays={amountDisplays}
                lastYearAmounts={lastYearAmounts}
                amountDisplaysByYoa={amountDisplaysByYoa}
                computedAmounts={computeAllComputedAmounts}
                computedByYoa={computeAllComputedAmountsByYoa}
                lastYearComputed={computeAllLastYearAmounts}
                isProtectedParentHeader={isProtectedParentHeader}
                isEditableLeaf={isEditableLeaf}
                getDisplayName={getDisplayName}
                onToggleNode={toggleNode}
                onToggleCotCurrent={toggleCotInputModeCurrent}
                onToggleCotLastYear={toggleCotInputModeLastYear}
                onChangeCurrentAmount={handleFsChangeCurrentAmount}
                onChangeLastYearAmount={handleFsChangeLastYearAmount}
                onChangeYoaAmount={handleFsChangeYoaAmount}
                onFsLineClick={onFsLineClick}
                navigableFsCodes={navigableFsCodes}
                onAddCustomNode={!readOnly ? handleOpenCustomNodeDialog : undefined}
                onEditCustomNode={!readOnly ? handleEditCustomNode : undefined}
                onDeleteCustomNode={!readOnly ? handleDeleteCustomNodeClick : undefined}
                customDraftNameEn={customInlineDraft?.nameEn}
                customDraftCurrentAmount={customInlineDraft?.currentAmount}
                customDraftLastYearAmount={customInlineDraft?.lastYearAmount}
                customDraftAmountsByYoa={customInlineDraft?.amountsByYoa}
                customDraftSaving={customInlineDraft?.saving}
                onCustomDraftNameChange={handleCustomDraftNameChange}
                onCustomDraftCurrentChange={handleCustomDraftCurrentChange}
                onCustomDraftLastYearChange={handleCustomDraftLastYearChange}
                onCustomDraftYoaChange={handleCustomDraftYoaChange}
                onCustomDraftCommit={handleCustomDraftCommit}
                onCustomDraftCancel={handleCustomDraftCancel}
                customEditFsId={customInlineEdit?.fsId ?? null}
                customEditNameEn={customInlineEdit?.nameEn}
                customEditSaving={customInlineEdit?.saving}
                onCustomEditNameChange={handleCustomEditNameChange}
                onCustomEditCommit={handleCustomEditCommit}
                onCustomEditCancel={handleCustomEditCancel}
            />
            <Popover
                open={!!customDeleteAnchor}
                anchorEl={customDeleteAnchor?.el}
                onClose={() => setCustomDeleteAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                <Box p={2} sx={{maxWidth: 320}}>
                    <Typography variant="body2" sx={{mb: 1}}>
                        Remove this custom node column? This permanently deletes the node and any linked
                        working paper / schedule data.
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => setCustomDeleteAnchor(null)}>Cancel</Button>
                        <Button size="small" color="error" variant="contained"
                                onClick={handleConfirmDeleteCustomNode}>
                            Confirm
                        </Button>
                    </Stack>
                </Box>
            </Popover>
            </>
        );
    }
});

ProfitLoss.displayName = 'ProfitLoss';

export type {CompanyAnnualSelectItem};
